import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  FiSearch, FiChevronRight, FiChevronLeft, FiFile, FiFolder,
  FiCheck, FiX, FiAlertCircle, FiFileText,
  FiChevronDown, FiFolderPlus, FiDownload,
  FiChevronsLeft, FiChevronsRight, FiExternalLink,
  FiStar, FiTrash2, FiPlus,
  FiZoomIn, FiZoomOut, FiRotateCw, FiRotateCcw, FiScissors, FiLayers, FiCornerUpLeft,
} from 'react-icons/fi';
import JSZip from 'jszip';
import { useDragOffset } from '../../../lib/useDragOffset';
import { useRefreshState, readRefreshValue, consumeRefreshValue, writeRefreshValue } from '../../../lib/refreshState';
import { loadTable, saveRow, addRow, getTableData, isApiReady } from '../../db-viewer/data/dataAccess';
import { fetchTableRow } from '../../db-viewer/data/dbViewerService';
import { applicableSalesTaxRates, ownerTaxLocation, salesTaxRateLabel } from '../utils/salesTaxApplicable';
import { decompressBase64ToBytes, compressBytesToBase64 } from '../utils/pdfBytes';
import { rotatePdfPages, isPdfBytes, RotateBy } from '../utils/pdfRotate';
import { cutPdfGroups, mergePdfs, nameWithSuffix, pagesSuffix, createDocumentLike, retireDocuments } from '../utils/pageEdit';
import SplitReviewModal from './SplitReviewModal';
import CombineDocsModal from './CombineDocsModal';
import { putServerBlob } from '../../../lib/serverDocStore';
import { resolveDocBase64 } from '../utils/docStorage';
import { addNotification } from '../../../lib/notifications';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { resolveDocuTypeId, localExtractFilingFields } from '../utils/documentExtract';
import { loadDocExtract, hasCachedExtract, amendDocExtract } from '../utils/extractCache';
import { decideDocDate, isDateSettled, readHandwrittenDate, handwritingCheckAllowed } from '../utils/handwrittenDate';
import { matchOwner, matchFolder, OwnerMatchCandidate, FolderMatchCandidate } from '../utils/filingAutoMatch';
import { interpretDate, findFormatId, formatFromConfirmedDate, recheckFutureDate, formatFitsRawDate } from '../utils/dateDisambiguate';
import { entityBaseCurrency, currencyOptions } from '../utils/baseCurrency';
import { pickBestCandidate, DirectoryCandidate } from '../utils/directoryMatch';
import { matchPaymentMethod, matchTax } from '../utils/paymentTaxMatch';
import { isPaymentAccount, paymentAccountLabel, accountBrand, matchPaymentAccount, PAYMENT_MATCH_WHY_LABEL, PaymentMatchWhy } from '../utils/paymentAccount';
import {
  buildContentTokens, computeMinHash, parseMinHash, computeDuplicateGroups,
  DupCandidate, DupGroupInfo, DUP_THRESHOLD,
} from '../utils/documentFingerprint';
import { fetchDedupGroups, markNotDuplicate } from '../utils/dedupApi';
import apiClient from '../../../lib/api/apiClient';
import { docMatchesSearch, searchTerms } from '../utils/docSearch';
import PdfCanvasViewer, { VIEW_MODES, ViewMode } from '../../../components/ui/PdfCanvasViewer';
import ManageFolders, { ManageFoldersInitialAction } from './ManageFoldersTab';
import PartiesMasterTab, { PartyKind } from './PartiesMasterTab';
import { matchPartyDocType, pickPartyDocType, planDocTypeLearning, PartyDocType } from '../utils/partyDocType';
import { naturesForAccount, natureLookup, natureIds, suggestNature, findNatureByName, resolveAccountHead, buildMemo, toDocNature, ACCOUNT_HEAD_SOURCE_LABEL, DocNature, AccountHead } from '../utils/docNature';
import NatureFormModal, { NatureFormValues } from './NatureFormModal';
import KindFormModal, { KindFormValues } from './KindFormModal';
import { effectiveDocuTypes, ownKindCode, EXPENSE_KIND_CODES } from '../utils/docKind';
import { PartySide, partySideOf } from '../utils/partySide';
import { partiesNamed } from '../utils/partyNameMatch';
import JournalEntriesTab from './JournalEntriesTab';
import { postJournalEntry, bankTxnToLines } from '../utils/glPosting';
import { getProfitCenters } from '../utils/profitCenters';
import { setFormDirty, isFormDirty, getEditSeq, registerSaveHandler, registerDiscardHandler, registerAutosaveHandler, submitSaver, confirmDiscardChanges } from '../../../lib/unsavedChanges';
import { listDocDrafts, saveDocDraft, clearDocDraft } from '../../../lib/docDraftStore';
import TagInput from '../../../components/ui/TagInput';
import { parseTags, joinTags, recordTagUse } from '../../../lib/tagStore';
import { subscribeDocsUploaded } from '../utils/uploadQueue';
import './ProcessReceiptsTab.css';

// =============================================
// Types
// =============================================
interface DocRow {
  document_id: number;
  account_owner_id: number;
  user_id: number;
  dms_folders_id: number | null;
  filename: string;
  original_filename: string;
  file_base64: string | null;
  file_size_original: number;
  mime_type: string;
  page_count: number | null;
  upload_source: string;
  upload_date: string;
  status: string;
  docu_type_id: number | null;
  owner_type: string | null;
  owner_id: number | null;
  party_name: string | null;
  invoice_ref: string | null;
  date_of_document: string | null;
  date_verified_at?: string | null;   // migration 134 — date checked, not filed yet
  date_verified_by?: number | null;
  date_undated?: number | null;       // migration 135 — 1 = the document has no date
  due_date: string | null;
  payment_method: string | null;
  payment_coa_id?: number | null;     // migration 143 — the GL account it was paid from
  total_amount: number | null;
  currency: string | null;
  tax_rate: number | null;
  purpose: string | null;
  notes: string | null;
  is_active: number;
  is_deleted: number;
  created_by: number | null;
  [key: string]: any;
}

interface FolderRow {
  folder_id: number;
  account_owner_id: number;
  parent_folder_id: number | null;
  folder_name: string;
  folder_path: string;
  folder_depth: number;
  folder_icon: string | null;
  folder_color: string | null;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface FolderOwnerRow {
  folder_owner_id: number;
  folder_id: number;
  owner_type: string;
  owner_id: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

// Multi-owner link for a document (dms_document_owners). One row per owner; each
// owner files the SAME stored document into their OWN folder (folder_id). Lets a
// single doc (e.g. a transfer) live in a business folder AND a personal folder.
interface DocOwnerRow {
  doc_owner_id: number;
  document_id: number;
  owner_type: string;
  owner_id: number;
  is_primary: number;
  folder_id: number | null;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

// dms_document_folders — a document filed in MANY folders, independent of owners.
interface DocFolderRow {
  doc_folder_id: number;
  document_id: number;
  folder_id: number;
  is_primary: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface OwnerEntity {
  owner_type: string;
  owner_id: number;
  display_name: string;
  badge: string;
}

interface DocuTypeRow {
  docu_type_id: number;
  docu_type_name: string;
  docu_type_code: string;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface BankTxnRow {
  txn_id: number;
  document_id: number | null;
  account_owner_id: number;
  owner_type: string | null;
  owner_id: number | null;
  txn_date: string | null;
  description: string | null;
  amount: number | null;
  direction: 'debit' | 'credit';
  balance: number | null;
  currency: string | null;
  category: string | null;
  coa_id: number | null;
  status: string;
  source: string | null;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

interface CoaRow {
  coa_id: number;
  account_owner_id: number;
  owner_type: string;
  owner_id: number;
  account_number: string | null;
  account_name: string;
  is_header: number;
  is_active: number;
  is_deleted: number;
  [key: string]: any;
}

// Draft edits for a bank txn line-item (committed to acctg_bank_txn on Save).
interface TxnDraft {
  txn_id: number;
  coa_id: number | null;
  category: string;
  status: string;
  pc: string;        // profit-center picker value ("loc:x" | "unit:y" | "")
}

type FilterTab = 'all' | 'to-file' | 'date-verified' | 'filed' | 'rejected' | 'bk-done' | 'duplicates';
type RightTab = 'filing' | 'bookkeeping' | 'banktxn' | 'makejournal';

// Unsaved Filing-form values, autosaved per user to dms_document_draft (migration 109)
// until the document is filed. The rename field is not here — it saves in real time.
const FILING_DRAFT_FORM = 'filing';
const FILING_DRAFT_DEBOUNCE_MS = 800;
// How many documents after the open one are read ahead in the background.
const PRE_READ_AHEAD = 3;

// One row of Masters → Vendors / Customers / Other Parties, reduced to what Filing needs.
interface PartyMaster {
  kind: 'Vendor' | 'Customer' | 'Other party';
  id: number;
  account_owner_id: number;
  owner_type: string;
  owner_id: number;
  name: string;         // party name — what is displayed and stored on the document
  legal_name: string;
  nickname: string;     // short name for file names
  aliases: string[];    // other names the same party goes by
  tags: string;         // labels from Masters (comma list) — the pickers find a party by them
  folder_id: number | null;
  default_coa_id: number | null;   // the party's default account (none on a shared entry)
}
// party_kind in acctg_party_doc_type for each master list
const PARTY_KIND_KEY: Record<string, string> = { Vendor: 'vendor', Customer: 'customer', 'Other party': 'other' };
const toPartyDocType = (r: any): PartyDocType => ({
  party_doc_type_id: Number(r.party_doc_type_id), party_kind: String(r.party_kind || ''), party_id: Number(r.party_id),
  doc_type_name: String(r.doc_type_name || ''), match_words: r.match_words || '',
  date_format_id: r.date_format_id != null ? Number(r.date_format_id) : null,
  nature_id: r.nature_id != null ? Number(r.nature_id) : null,
  coa_id: r.coa_id != null ? Number(r.coa_id) : null,
});
const PARTY_MASTER_TABLES: { table: string; kind: PartyMaster['kind']; pk: string; nameCol: string }[] = [
  { table: 'acctg_vendor',      kind: 'Vendor',      pk: 'vendor_id',      nameCol: 'vendor_name' },
  { table: 'acctg_customer',    kind: 'Customer',    pk: 'customer_id',    nameCol: 'customer_name' },
  { table: 'acctg_other_party', kind: 'Other party', pk: 'other_party_id', nameCol: 'party_name' },
];
// Which side of the books a document type is on decides whose names the Bookkeeping
// Party Name offers: money going out → vendors (+ other parties); money coming in →
// customers; a report / other document (or no type yet) → everyone. By docu_type_code.
const PARTY_SIDES: Record<PartySide, { addLabel: string; listed: PartyMaster['kind'][]; addKinds: PartyKind[] }> = {
  vendor:   { addLabel: 'Add new Vendor / Party',   listed: ['Vendor', 'Other party'],             addKinds: ['vendor', 'other'] },
  customer: { addLabel: 'Add new Customer / Party', listed: ['Customer'],                          addKinds: ['customer', 'other'] },
  any:      { addLabel: 'Add New Party',            listed: ['Vendor', 'Customer', 'Other party'], addKinds: ['vendor', 'customer', 'other'] },
};
// Characters a file name cannot carry on Windows / macOS / the web.
const FILENAME_UNSAFE = /[\\/:*?"<>|\u0000-\u001f]+/g;
interface FilingDraft {
  ownerKeys?: string[];
  folderIds?: number[];
  docuTypeId?: string;
  natureId?: string;      // acctg_doc_nature (migration 142)
  expenseTypeId?: string;
  receiptKind?: string;
  partyName?: string;
  invoiceRef?: string;
  date?: string;
  paymentMethod?: string;
  paymentMethodId?: string;
  paymentCoaId?: string;  // acctg_coa account it was paid from (migration 143)
  taxTypeId?: string;
  taxRateId?: string;
  taxAmount?: string;
  dateFormatId?: number | null;
  rawDate?: string;
  globalBusiId?: number | null;
  amount?: string;
  currency?: string;
  purpose?: string;
  tags?: string;          // comma list (migration 138)
  autoFilled?: boolean;   // taken after auto-fill finished → its blank fields are deliberate
  // Fields this draft only holds because a duplicate copy of the document already had
  // them saved (see the duplicates effect). They are not this user's edits to THIS copy,
  // so they are not carried on to further copies as if they were.
  fromCopy?: string[];
}

// Combine the Filing drafts a user has on copies of the SAME document into one. The
// drafts are given newest first: for each field the newest draft that actually holds a
// value wins, so nothing typed on either copy is lost and a later change beats an
// earlier one. A blank never overrides a value.
function combineFilingDrafts(newestFirst: FilingDraft[]): FilingDraft {
  const filled = (v: unknown) => !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
  const out: Record<string, unknown> = {};
  for (const draft of newestFirst) {
    for (const [key, value] of Object.entries(draft)) {
      if (key === 'autoFilled') continue;
      if (!filled(out[key]) && (filled(value) || !(key in out))) out[key] = value;
    }
  }
  if (newestFirst.some(d => d.autoFilled)) out.autoFilled = true;
  return out as FilingDraft;
}

const draftValueFilled = (v: unknown) => !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
const draftValueSame = (a: unknown, b: unknown) =>
  JSON.stringify(Array.isArray(a) ? a.slice().sort() : a ?? '') === JSON.stringify(Array.isArray(b) ? b.slice().sort() : b ?? '');
// The Filing fields a document can pick up from a duplicate copy of itself.
// Amount, tax amount, date and invoice / ref # are each document's OWN facts: they are
// never copied between duplicates (2026-10-15 — two look-alike Co-op receipts of $90.25 and
// $80.30 kept overwriting each other's amount). They also decide that two documents are
// NOT duplicates when they differ (utils/documentFingerprint factsDiffer).
const DRAFT_SHARED_KEYS: (keyof FilingDraft)[] = [
  'ownerKeys', 'folderIds', 'docuTypeId', 'natureId', 'expenseTypeId', 'receiptKind', 'partyName',
  'paymentMethod', 'paymentMethodId', 'paymentCoaId', 'taxTypeId', 'taxRateId', 'currency', 'purpose', 'tags',
];

// Manual statuses a user can pick in the Bank Txns grid. 'posted' is intentionally
// EXCLUDED — the only path to posted is the "Post to GL" action (which creates the
// journal entry); a row can't be marked posted without a real journal behind it.
const TXN_STATUSES = ['imported', 'reviewed', 'rejected'];

// =============================================
// Constants
// =============================================
const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'ALL' },
  // New uploads whose date nobody has checked yet (owner renamed "To File", 2026-10-17).
  { key: 'to-file', label: 'Verify date & File' },
  // Not filed yet, but the user has checked the date (dms_documents.date_verified_at,
  // migration 134). Splits the queue: new uploads vs documents already checked.
  { key: 'date-verified', label: 'Date Verified' },
  { key: 'filed', label: 'Filed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'bk-done', label: 'BK Done' },
  // Not a status: every document that has a copy (or is one), grouped — original first.
  { key: 'duplicates', label: 'Duplicates' },
];

const STATUS_MAP: Record<FilterTab, string[]> = {
  'all': [],
  'to-file': ['Review'],
  'date-verified': ['Review'],
  'filed': ['Filed'],
  'rejected': ['Rejected'],
  'bk-done': ['BK Done'],
  'duplicates': [],
};

// A To File document whose date the user has checked (migration 134).
const isDateVerified = (d: { date_verified_at?: string | null }) => !!d.date_verified_at;
// A date that looks wrong for this document, checked before the Date verified tick saves it
// (owner, 2026-10-17 — 3301-11-30 / 2007-08-31 / 2029-11-29 were saved by mistake): later
// than today, before 1990, or a year more than 1 away from MOST of the dates the document
// gives (printed, handwritten, file name, the date it had) — one misread among them (1382:
// printed "25/05/08" read as 2008) must not vouch for the wrong year. '' = looks fine.
const suspectDocDate = (iso: string, refs: string[], todayIso: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  if (iso > todayIso) return 'is later than today';
  const year = Number(iso.slice(0, 4));
  if (year < 1990) return 'is before 1990';
  const refYears = refs.map(r => Number(String(r || '').slice(0, 4))).filter(y => y >= 1990 && y <= 2100);
  const agree = refYears.filter(y => Math.abs(y - year) <= 1).length;
  if (refYears.length && agree * 2 < refYears.length) {
    return `has the year ${year}, but the document's other dates say ${Array.from(new Set(refYears)).join(' / ')}`;
  }
  return '';
};

// The status tab a document is listed under. 'Review' is split in two by the date check.
function tabOfDoc(d: { status: string; date_verified_at?: string | null }): FilterTab {
  if (d.status === 'Review') return isDateVerified(d) ? 'date-verified' : 'to-file';
  return (Object.keys(STATUS_MAP) as FilterTab[]).find(k => STATUS_MAP[k].includes(d.status)) || 'all';
}

const FOLDER_EMOJI: Record<string, string> = {
  bank: '🏦', medical: '🏥', tax: '📋', receipts: '🧾', legal: '⚖️',
  insurance: '🛡️', personal: '👤', business: '💼', education: '🎓',
  property: '🏠', investment: '📈', other: '📁',
};

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

// A folder name should "fill" the first line (break mid-token) ONLY when it holds a
// long unbreakable token — e.g. machine-generated "Sysco Regina_48598_152052", where
// space-wrapping would drop the whole long token to line 2 and leave line 1 with just
// "Sysco". Normal multi-word names (every token short) keep clean space-wrapping.
const LONG_TOKEN_LEN = 15;
function nameHasLongToken(name: string): boolean {
  return !!name && name.split(/\s+/).some(tok => tok.length >= LONG_TOKEN_LEN);
}

// Folder depth = parent depth + 1 (0 for roots). Used when re-parenting via drag-drop.
function computeFolderDepth(parentFolderId: number | null, allFolders: FolderRow[]): number {
  if (parentFolderId == null) return 0;
  const parent = allFolders.find(f => f.folder_id === parentFolderId);
  return parent ? (Number(parent.folder_depth) || 0) + 1 : 0;
}

// Materialized id-path "/1/3/" built from ancestor ids (walks parent links).
function computeFolderPath(folderId: number, allFolders: FolderRow[]): string {
  const parts: number[] = [folderId];
  let current = allFolders.find(f => f.folder_id === folderId);
  let depth = 0;
  while (current && current.parent_folder_id != null && depth < 20) {
    parts.unshift(Number(current.parent_folder_id));
    current = allFolders.find(f => f.folder_id === current!.parent_folder_id);
    depth++;
  }
  return '/' + parts.join('/') + '/';
}

// CSV cell escaping (wrap in quotes, double embedded quotes)
function csvCell(v: any): string {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Persist UI pane-collapse preferences (Record Room + document list) so a user's
// last layout choice survives reloads. Stored as '1'/'0' under akt_ keys.
const PREF_RECORD_ROOM_OPEN = 'akt_pr_record_room_open';
const PREF_DOCLIST_OPEN = 'akt_pr_doclist_open';
const PREF_RIGHT_PANEL_OPEN = 'akt_pr_right_panel_open';

// Document-list pane width is drag-resizable. Stored in sessionStorage (NOT localStorage)
// so a widened pane persists within the session but resets to the default on a new session.
const PREF_DOCLIST_WIDTH = 'akt_pr_doclist_width';
const DOCLIST_MIN_W = 260;
const DOCLIST_MAX_W = 640;
const DOCLIST_DEFAULT_W = 320;

// Document-list sort options. Default = most recent uploads on top.
const PREF_SORT = 'akt_pr_doc_sort';
type SortKey =
  | 'recent' | 'oldest'
  | 'name-asc' | 'name-desc'
  | 'docdate-desc' | 'docdate-asc'
  | 'type' | 'party'
  | 'amount-desc' | 'amount-asc'
  | 'modified' | 'status'
  | 'pending-approval'
  | 'duplicates';
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recent',       label: 'Most recent (uploaded)' },
  { value: 'oldest',       label: 'Oldest uploaded' },
  { value: 'name-asc',     label: 'Name (A–Z)' },
  { value: 'name-desc',    label: 'Name (Z–A)' },
  { value: 'docdate-desc', label: 'Document date (newest)' },
  { value: 'docdate-asc',  label: 'Document date (oldest)' },
  { value: 'type',         label: 'Type / Kind' },
  { value: 'party',        label: 'Party / Vendor' },
  { value: 'amount-desc',  label: 'Amount (high → low)' },
  { value: 'amount-asc',   label: 'Amount (low → high)' },
  { value: 'modified',     label: 'Last modified' },
  { value: 'status',       label: 'Status' },
  // Documents waiting for the user to approve something auto-read could not settle by
  // itself (today: a handwritten date that differs from the printed one) — listed first.
  { value: 'pending-approval', label: 'Pending User Approvals' },
  { value: 'duplicates',   label: 'Duplicates (grouped)' },
];
function readBoolPref(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}
function writeBoolPref(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* localStorage unavailable — ignore, preference just won't persist */
  }
}

// =============================================
// Helpers
// =============================================
function getCurrentUser(usersData: Record<string, any>[]) {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null || userRow.owner_id === '' ? null : Number(userRow.owner_id),
      role: session.role || '',
      user_type_id: Number(userRow.user_type_id) || 0,
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getAccountOwner(usersData: Record<string, any>[]): Record<string, any> | null {
  return usersData.find((u: any) => u.owner_id == null || u.owner_id === '') || usersData[0] || null;
}

function getAccountOwnerForUser(userId: number, usersData: Record<string, any>[]): Record<string, any> | null {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find((u: any) => u.user_id == userId);
  if (!user) return null;
  if (user.owner_id == null || user.owner_id === '') return user;
  // eslint-disable-next-line eqeqeq
  return usersData.find((u: any) => u.user_id == user.owner_id) || null;
}

function getOwnerEntities(accountOwnerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];

  // eslint-disable-next-line eqeqeq
  const owner = usersData.find((u: any) => u.user_id == accountOwnerId);
  if (owner) {
    entities.push({
      owner_type: 'account_owner',
      owner_id: Number(owner.user_id),
      display_name: `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || 'Account Owner',
      badge: 'Owner',
    });
  }

  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => {
      entities.push({
        owner_type: 'family_member',
        owner_id: Number(u.user_id),
        display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family Member',
        badge: 'Family',
      });
    });

  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == accountOwnerId && b.is_deleted !== 1 && b.is_client_business !== 1)
    .forEach((b: any) => {
      entities.push({
        owner_type: 'business',
        owner_id: Number(b.business_id),
        display_name: b.busi_legal_name || b.busi_operating_name || 'Business',
        badge: 'Business',
      });
    });

  return entities;
}

// Build the SELECTED CLIENT's entities (the client, the client's family, the
// client's businesses) — used as the document-owner scope under Client's Accounts.
function getClientEntities(clientId: number | string, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];
  // eslint-disable-next-line eqeqeq
  const client = usersData.find((u: any) => u.user_id == clientId);
  const clientName = client ? (`${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email || 'Client') : 'Client';
  if (client) {
    entities.push({ owner_type: 'client_personal', owner_id: Number(client.user_id), display_name: clientName, badge: 'Client' });
  }
  usersData
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == clientId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => entities.push({
      owner_type: 'client_personal',
      owner_id: Number(u.user_id),
      display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family',
      badge: `${clientName}'s Family`,
    }));
  businessesData
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == clientId && b.is_deleted !== 1)
    .forEach((b: any) => entities.push({
      owner_type: 'client_business',
      owner_id: Number(b.business_id),
      display_name: b.busi_legal_name || b.busi_operating_name || 'Business',
      badge: `${clientName}'s Biz`,
    }));
  return entities;
}

// Does a document belong to a given owner entity? Owner/family docs are keyed by
// `user_id`; businesses and clients by polymorphic `owner_type` + `owner_id`
// (mirrors the per-owner dropdown filter).
function docMatchesEntity(d: DocRow, e: OwnerEntity): boolean {
  if (e.owner_type === 'account_owner' || e.owner_type === 'family_member') {
    // eslint-disable-next-line eqeqeq
    return d.user_id == e.owner_id;
  }
  // eslint-disable-next-line eqeqeq
  return d.owner_type === e.owner_type && d.owner_id == e.owner_id;
}

function getOwnerName(ownerType: string | null, ownerId: number | null, usersData: Record<string, any>[], businessesData: Record<string, any>[]): string {
  if (!ownerType || !ownerId) return '';
  if (ownerType === 'account_owner' || ownerType === 'family_member') {
    // eslint-disable-next-line eqeqeq
    const u = usersData.find((u: any) => u.user_id == ownerId);
    if (u) return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '';
  }
  if (ownerType === 'business') {
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((b: any) => b.business_id == ownerId);
    if (b) return b.busi_legal_name || b.busi_operating_name || '';
  }
  return '';
}

// Owner nickname (Profile `users.nickname` / business acronym) — shown as a
// small initials badge on the left of each folder in the Record Room tree.
function getOwnerNickname(ownerType: string | null, ownerId: number | null, usersData: Record<string, any>[], businessesData: Record<string, any>[]): string {
  if (!ownerType || !ownerId) return '';
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((b: any) => b.business_id == ownerId);
    return b ? String(b.busi_acronym || b.acronym || b.busi_acro || '') : '';
  }
  // eslint-disable-next-line eqeqeq
  const u = usersData.find((u: any) => u.user_id == ownerId);
  return u ? String(u.nickname || '') : '';
}

// =============================================
// Main Component
// =============================================
interface ProcessReceiptsTabProps {
  // Pillar scope: 'owner' = Your Accounts (self entities), 'clients' = the picked client's entities
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
  // DMS uses this page for filing only (no bookkeeping) — hide the Bookkeeping panel.
  showBookkeeping?: boolean;
  // Folder-browser only: render JUST the Record Room folder pane (full width), hiding
  // the document list / preview / forms. Powers the DMS → Folders → "Folder List" tab,
  // reusing all the Record Room functionality (owner filter, search, tree, drag-drop,
  // context menu, New folder) without duplicating it.
  foldersOnly?: boolean;
  // When set (e.g. drilled in from the Reports dashboard), highlight + expand to this
  // folder once the tree loads.
  initialSelectedFolderId?: number;
}

const ProcessReceiptsTab: React.FC<ProcessReceiptsTabProps> = ({ viewMode = 'owner', selectedClientId = null, showBookkeeping = true, foldersOnly = false, initialSelectedFolderId }) => {
  // Reference data loaded from API
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwner = useMemo(() => {
    if (!currentUser) return null;
    // Resolve the account owner as the LOGGED-IN user's own account (matches how
    // UploadDocsPage stamps account_owner_id). For an owner (owner_id == null) this
    // is themselves; for a sub-user it's their owner. Using getAccountOwner() — the
    // first owner_id==null user globally — was a multi-tenancy bug: with seed/other
    // owners present (e.g. a lower-id account on staging/live), it resolved to the
    // WRONG owner, so the user's own documents were filtered out of the list.
    return getAccountOwnerForUser(currentUser.user_id, usersData);
  }, [currentUser, usersData]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;

  // Owner entities for the document owner dropdown — scoped to the active pillar.
  const ownerEntities = useMemo(() => {
    if (!accountOwnerId) return [];
    if (viewMode === 'clients') {
      // Client's Accounts → only the picked client's own entities.
      if (!selectedClientId) return [];
      return getClientEntities(selectedClientId, usersData, businessesData);
    }
    // Your Accounts → owner + family + owner's businesses (no clients).
    return getOwnerEntities(accountOwnerId, usersData, businessesData)
      .filter((e: any) => e.owner_type !== 'client_personal' && e.owner_type !== 'client_business');
  }, [accountOwnerId, usersData, businessesData, viewMode, selectedClientId]);

  // Folder storage tables — Client's Accounts uses the per-client folder trees
  // (dms_client_folders), Your Accounts/DMS uses the owner's own (dms_folders).
  // Folder creation is delegated to the Manage Folders popup, so this tab only
  // reads these tables (+ rename/delete existing folders).
  const FOLDERS_TABLE = viewMode === 'clients' ? 'dms_client_folders' : 'dms_folders';
  const FOLDER_OWNERS_TABLE = viewMode === 'clients' ? 'dms_client_folder_owners' : 'dms_folder_owners';

  // State
  const [documents, setDocuments] = useState<DocRow[]>([]);
  const [bankTxns, setBankTxns] = useState<BankTxnRow[]>([]);
  const [locationsData, setLocationsData] = useState<Record<string, any>[]>([]);
  const [unitsData, setUnitsData] = useState<Record<string, any>[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<CoaRow[]>([]);
  const [glJournals, setGlJournals] = useState<Record<string, any>[]>([]);
  const [txnDrafts, setTxnDrafts] = useState<TxnDraft[]>([]);
  const [txnBusy, setTxnBusy] = useState(false);
  const [bankAccountCoaId, setBankAccountCoaId] = useState<string>('');  // offset Bank/Cash account for posting
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [folderOwners, setFolderOwners] = useState<FolderOwnerRow[]>([]);
  const [docOwners, setDocOwners] = useState<DocOwnerRow[]>([]);
  const [docFolders, setDocFolders] = useState<DocFolderRow[]>([]);
  const [docuTypeRows, setDocuTypes] = useState<DocuTypeRow[]>([]);   // raw acctg_docu_type
  // The kinds of document as this account has them: standard ones under its own names
  // (hidden ones marked removed), plus its own kinds with their base kind's code — so the
  // party side and tax rules work by code unchanged (utils/docKind, migration 142).
  const docuTypes = useMemo<DocuTypeRow[]>(
    () => effectiveDocuTypes(docuTypeRows, accountOwnerId || null) as DocuTypeRow[], [docuTypeRows, accountOwnerId]);
  // Natures (acctg_doc_nature) + the account head each set of books uses for one (migration 142).
  const [natureRows, setNatureRows] = useState<any[]>([]);
  const [natureAccountRows, setNatureAccountRows] = useState<any[]>([]);
  // Expense tagging (migration 097) — universal T2125 buckets + receipt-kind biases
  // + the business-type master (to map a business → its category for bias resolution).
  const [expenseTypes, setExpenseTypes] = useState<any[]>([]);
  const [expenseBiases, setExpenseBiases] = useState<any[]>([]);
  const [busiTypes, setBusiTypes] = useState<any[]>([]);
  // Owner scope is multi-select: keys are "owner_type:owner_id". Empty = show
  // everything the current user is allowed to see. Lives in the Record Room as a
  // compact one-line trigger that opens a searchable, scrollable owner list
  // (keeps the folder tree's vertical space free for 100s of folders).
  const [selectedDocOwnerIds, setSelectedDocOwnerIds] = useState<string[]>([]);
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [ownerFilterText, setOwnerFilterText] = useState('');
  const ownerPickerRef = useRef<HTMLDivElement>(null);
  const [selectedRecordFolderId, setSelectedRecordFolderId] = useState<number | null>(null);
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [folderMenuOpenId, setFolderMenuOpenId] = useState<number | null>(null);
  const folderMenuRef = useRef<HTMLDivElement>(null);
  // Inline folder actions (context menu)
  const [renamingFolderId, setRenamingFolderId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteFolderId, setConfirmDeleteFolderId] = useState<number | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);
  // Adding folders is delegated to the Manage Folders popup (full functionality:
  // mandatory owner selection + validation). folderRefreshKey forces a folder
  // reload after the popup closes so new folders appear in the tree.
  const [showManageFolders, setShowManageFolders] = useState(false);
  // What the popup opens on: a folder to edit / to add a sub-folder under, or null = blank form.
  const [manageFoldersAction, setManageFoldersAction] = useState<ManageFoldersInitialAction | null>(null);
  const [folderRefreshKey, setFolderRefreshKey] = useState(0);
  // Drag-drop folder move (re-parent). dropTargetId = -1 means the "root" drop zone.
  const [draggedFolderId, setDraggedFolderId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [folderMoving, setFolderMoving] = useState(false);
  // Drag a still-unfiled document from the list onto a folder to file it.
  const [draggedDocId, setDraggedDocId] = useState<number | null>(null);
  // Per-document quick actions (operate on the selected doc)
  const [showDocMove, setShowDocMove] = useState(false);
  const [docMoveSearch, setDocMoveSearch] = useState('');
  const docMoveRef = useRef<HTMLDivElement>(null);
  // Keyboard navigation: the doc-list scroll container + the center viewer pane are
  // focusable so arrow/Tab keys drive selection and PDF scrolling.
  const docListRef = useRef<HTMLDivElement>(null);
  // A document opened from another screen (Upload Docs, Reports …) that the list still
  // has to be brought to — see the "navigated document" effect.
  const navDocRef = useRef<{ id: number; pass: number } | null>(null);
  // Arrived through a link to one particular document (e.g. clicked in Upload Docs)?
  // Then the user came for that document — no "remove duplicates?" prompt on arrival.
  const arrivedByLinkRef = useRef(false);
  const centerPaneRef = useRef<HTMLDivElement>(null);
  // Default to the working queue: documents waiting to be filed, newest first.
  // A browser refresh comes back to the same list tab and document (lib/refreshState).
  const [activeFilter, setActiveFilter] = useRefreshState<FilterTab>('pd.listTab', 'to-file',
    v => ['all', 'to-file', 'date-verified', 'filed', 'rejected', 'bk-done', 'duplicates'].includes(v as string));
  const [searchTerm, setSearchTerm] = useState('');
  // Words printed INSIDE documents (their stored auto-read text): document_id -> snippet.
  // Asked from the server 300 ms after typing stops, for words of 3+ characters.
  const [textHits, setTextHits] = useState<Map<number, string>>(new Map());
  // Document-list sort order — persisted; defaults to most recent uploads first.
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const saved = localStorage.getItem(PREF_SORT) as SortKey | null;
    return saved && SORT_OPTIONS.some(o => o.value === saved) ? saved : 'recent';
  });
  useEffect(() => { localStorage.setItem(PREF_SORT, sortKey); }, [sortKey]);
  const [sortOpen, setSortOpen] = useState(false);
  const sortPickerRef = useRef<HTMLDivElement>(null);

  // Drag-resizable document-list width (session-scoped; resets on a new session).
  const [docListWidth, setDocListWidth] = useState<number>(() => {
    const saved = Number(sessionStorage.getItem(PREF_DOCLIST_WIDTH));
    return saved >= DOCLIST_MIN_W && saved <= DOCLIST_MAX_W ? saved : DOCLIST_DEFAULT_W;
  });
  useEffect(() => { sessionStorage.setItem(PREF_DOCLIST_WIDTH, String(docListWidth)); }, [docListWidth]);
  const onDocListResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = docListWidth;
    const onMove = (ev: MouseEvent) => {
      const w = Math.min(DOCLIST_MAX_W, Math.max(DOCLIST_MIN_W, startW + (ev.clientX - startX)));
      setDocListWidth(w);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  // Double-click the handle to snap back to the default width.
  const resetDocListWidth = () => setDocListWidth(DOCLIST_DEFAULT_W);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  // The document open before a refresh: re-opened by the launch selection below when it is
  // still in the restored list, else that list's first document opens as usual.
  const [refreshDocId] = useState(() => readRefreshValue<number>('pd.doc') ?? null);
  useEffect(() => { consumeRefreshValue('pd.doc'); }, []);
  useEffect(() => { if (!foldersOnly) writeRefreshValue('pd.doc', selectedDocId); }, [selectedDocId, foldersOnly]);
  // Verify date & File: the document that just left the list when its date was ticked as
  // verified, so a tick made by mistake can be undone (owner, 2026-10-17). nextId = the
  // document that opened in its place, where ">>" returns to.
  const [leftDoc, setLeftDoc] = useState<{ id: number; filename: string; nextId: number | null } | null>(null);
  // "Prev" in Verify date & File (owner, 2026-10-17): the documents opened there, in order,
  // so Prev walks back one at a time — also to ones that have since left the list. Kept in
  // the browser only (ids, last 50); opening one is the same as clicking it in the list.
  const [viewHistory, setViewHistory] = useState<number[]>([]);
  const lastViewedRef = useRef<number | null>(null);
  const goingBackRef = useRef(false);
  useEffect(() => {
    const prev = lastViewedRef.current;
    lastViewedRef.current = selectedDocId;
    if (goingBackRef.current) { goingBackRef.current = false; return; }
    if (activeFilter !== 'to-file' || prev == null || selectedDocId == null || prev === selectedDocId) return;
    setViewHistory(h => [...h.filter(id => id !== prev), prev].slice(-50));
  }, [selectedDocId, activeFilter]);
  useEffect(() => { setViewHistory([]); }, [activeFilter]);   // history belongs to this tab visit
  const [rightTab, setRightTab] = useRefreshState<RightTab>('pd.rightTab', 'filing',
    v => ['filing', 'bookkeeping', 'banktxn', 'makejournal'].includes(v as string));
  // Guard-then-land: switching the right-panel tab (Filing / Bookkeeping / Make Journal /
  // Bank Txns) must check for unsaved edits in the current sub-form first.
  const switchRightTab = async (next: RightTab) => {
    if (next === rightTab) return;
    if (!(await confirmDiscardChanges())) return;
    setRightTab(next);
  };
  // Record Room (folders) pane visibility — restored from the user's last choice.
  const [showRecordRoom, setShowRecordRoom] = useState(() => readBoolPref(PREF_RECORD_ROOM_OPEN, false));
  // Document-list pane can be collapsed to a thin strip to give the preview more room.
  const [showDocList, setShowDocList] = useState(() => readBoolPref(PREF_DOCLIST_OPEN, true));
  const [showRightPanel, setShowRightPanel] = useState(() => readBoolPref(PREF_RIGHT_PANEL_OPEN, true));
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  // How the folder tree is ordered — in the Folder List (Masters → Folders) and in
  // Process Docs' folder pane alike. Starts on Name; each screen remembers its own choice.
  type FolderSortKey = 'name' | 'owner' | 'umbrella' | 'shared';
  const folderSortPref = foldersOnly ? 'akt_folder_list_sort' : 'akt_process_folder_sort';
  const [folderSort, setFolderSort] = useState<FolderSortKey>(() => {
    try {
      const v = localStorage.getItem(folderSortPref);
      return v === 'owner' || v === 'umbrella' || v === 'shared' ? v : 'name';
    } catch { return 'name'; }
  });
  useEffect(() => { try { localStorage.setItem(folderSortPref, folderSort); } catch { /* not kept */ } }, [folderSort, folderSortPref]);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // PDF viewer controls (hosted in the merged toolbar; PdfCanvasViewer is controlled).
  const [pdfViewMode, setPdfViewMode] = useState<ViewMode>(() => {
    const s = localStorage.getItem('akt_pdf_view_mode');
    return s === 'fit' || s === 'scroll' || s === 'two' || s === 'grid' ? (s as ViewMode) : 'fit';
  });
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  useEffect(() => { localStorage.setItem('akt_pdf_view_mode', pdfViewMode); }, [pdfViewMode]);
  const [pdfModeMenuOpen, setPdfModeMenuOpen] = useState(false);
  const pdfModeRef = useRef<HTMLDivElement>(null);
  // Switching mode resets zoom so the fit is honored.
  const pickPdfMode = useCallback((m: ViewMode) => { setPdfViewMode(m); setPdfZoom(1); setPdfModeMenuOpen(false); }, []);
  useEffect(() => {
    if (!pdfModeMenuOpen) return;
    const h = (e: MouseEvent) => { if (pdfModeRef.current && !pdfModeRef.current.contains(e.target as Node)) setPdfModeMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pdfModeMenuOpen]);
  // Rotate menu (viewer toolbar). After a rotation the viewer re-opens at the page that was turned.
  const [pdfRotateMenuOpen, setPdfRotateMenuOpen] = useState(false);
  const pdfRotateRef = useRef<HTMLDivElement>(null);
  const [pdfRotating, setPdfRotating] = useState(false);
  const [pdfOpenAtPage, setPdfOpenAtPage] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!pdfRotateMenuOpen) return;
    const h = (e: MouseEvent) => { if (pdfRotateRef.current && !pdfRotateRef.current.contains(e.target as Node)) setPdfRotateMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pdfRotateMenuOpen]);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Remember the user's pane-collapse layout across reloads.
  useEffect(() => { writeBoolPref(PREF_RECORD_ROOM_OPEN, showRecordRoom); }, [showRecordRoom]);
  useEffect(() => { writeBoolPref(PREF_DOCLIST_OPEN, showDocList); }, [showDocList]);
  useEffect(() => { writeBoolPref(PREF_RIGHT_PANEL_OPEN, showRightPanel); }, [showRightPanel]);

  // Filing form. Owner + Folder are MULTI-select (a doc can have many owners AND be
  // filed in many folders, independent of each other). filingOwnerKeys[0] / [0] are
  // the primary on dms_documents; the rest live in dms_document_owners / dms_document_folders.
  const [filingOwnerKeys, setFilingOwnerKeys] = useState<string[]>([]);
  const [filingDocuTypeId, setFilingDocuTypeId] = useState('');
  const [filingExpenseTypeId, setFilingExpenseTypeId] = useState('');
  const [filingReceiptKind, setFilingReceiptKind] = useState('');
  const [filingExpenseAuto, setFilingExpenseAuto] = useState(false);  // was the expense type auto-suggested?
  // Nature of Document (migration 142): what the document is FOR — "Meal", "Fuel" — picked
  // from the list or typed new. '' = only the document kind (filingDocuTypeId) is set.
  const [filingNatureId, setFilingNatureId] = useState('');
  const [filingNatureAuto, setFilingNatureAuto] = useState(false);     // suggested, not picked
  const [filingNatureHint, setFilingNatureHint] = useState('');        // the AI reader's nature for this document
  const natureAutoDocRef = useRef<number | null>(null);                // document a nature was suggested for
  const [filingPartyName, setFilingPartyName] = useState('');
  // The name the reader put in Party Name that is NOT in Masters, when the user then picked
  // the right party instead (a misread: "Clearview Auto Ltd" for "Clearview Agro"). Filing
  // the document saves it as an alias of the chosen party, so the same misread is
  // recognised next time. '' = nothing to learn.
  const [filingPartyMisread, setFilingPartyMisread] = useState('');
  const [filingInvoiceRef, setFilingInvoiceRef] = useState('');
  const [filingDate, setFilingDate] = useState('');
  // The user typed or picked the date of THIS document: the reads that finish in the
  // background (auto-read, handwriting, the party's layout) must leave it alone. Without
  // this a slow read put its own (wrong) date over the one just typed and the tick saved
  // it — 3301-11-30 for "Nov 30, 2023" (owner, 2026-10-17).
  const filingDateTypedRef = useRef(false);
  const autoSetFilingDate = useCallback((next: (prev: string) => string) => {
    setFilingDate(prev => (filingDateTypedRef.current ? prev : next(prev)));
  }, []);
  const [filingPaymentMethod, setFilingPaymentMethod] = useState('');
  const [filingAmount, setFilingAmount] = useState('');
  const [filingCurrency, setFilingCurrency] = useState('');
  // The base-currency default last applied to the open document (see filingBaseCurrency).
  const autoCurrencyRef = useRef<{ docId: number | null; value: string }>({ docId: null, value: '' });
  const [filingPurpose, setFilingPurpose] = useState('');
  const [filingTags, setFilingTags] = useState('');   // comma list — the account's tag dictionary
  // Tags last put in by the app (from the party / folders), so a later pick can replace
  // them while they are still untouched. sourceKey = the party + folders they came from.
  const autoTagsRef = useRef<{ docId: number | null; sourceKey: string | null; value: string }>({ docId: null, sourceKey: null, value: '' });
  const [filingFilename, setFilingFilename] = useState('');   // rename field (persists in real time)
  const [filingFolderIds, setFilingFolderIds] = useState<number[]>([]);
  // Set when the owner / folder was PRE-FILLED by the document auto-matcher (vs a
  // saved value or a manual pick) — drives the "AI suggested" cue and the submit gate.
  // Part 2: payment-method / tax matching + date disambiguation + directory link.
  const [filingPaymentMethodId, setFilingPaymentMethodId] = useState('');
  // The owner's bank / credit-card account it was paid from (migration 143) — read off the
  // receipt by card digits / how the account is printed; '' = only the generic method.
  const [filingPaymentCoaId, setFilingPaymentCoaId] = useState('');
  const [filingPaymentCoaWhy, setFilingPaymentCoaWhy] = useState<PaymentMatchWhy | null>(null);
  const [filingTaxTypeId, setFilingTaxTypeId] = useState('');
  const [filingTaxRateId, setFilingTaxRateId] = useState('');
  const [filingTaxAmount, setFilingTaxAmount] = useState('');
  const [filingDateFormatId, setFilingDateFormatId] = useState<number | null>(null);
  const [filingRawDate, setFilingRawDate] = useState('');
  const [filingGlobalBusiId, setFilingGlobalBusiId] = useState<number | null>(null);
  // Date disambiguation: when the raw date can't be resolved from digits alone.
  const [filingDateAmbiguous, setFilingDateAmbiguous] = useState(false);
  // Masters → Vendors, Customers & Parties: the names offered in Party Name. Choosing one
  // brings its related folder and its nickname (used in the suggested file name).
  const [partyMasters, setPartyMasters] = useState<PartyMaster[]>([]);
  const partyAppliedRef = useRef('');   // `${docId}:${kind}:${id}` whose folder was already offered
  // A date later than today must be confirmed before filing: the value the user said is right.
  const [futureDateConfirmed, setFutureDateConfirmed] = useState('');
  // The Date verified tick stopped on a date that looks wrong (suspectDocDate): the
  // question shown under the date, and the date the user said is right after all.
  const [dateCheckPrompt, setDateCheckPrompt] = useState<{ date: string; reason: string; stay: boolean } | null>(null);
  const dateCheckOkRef = useRef('');
  // Handwritten date (utils/handwrittenDate.ts): the date written on the document by
  // hand wins over the printed one; where the two differ the user must say which is right.
  const [filingHandwrittenDate, setFilingHandwrittenDate] = useState('');
  const [filingPrintedDate, setFilingPrintedDate] = useState('');
  const [filingPrintedFrom, setFilingPrintedFrom] = useState<'text' | 'filename'>('text');
  // How the user settled the date on this document in this sitting ('' = not yet).
  const [filingDateChoice, setFilingDateChoice] = useState<'' | 'handwritten' | 'printed' | 'user'>('');
  const [filingUndated, setFilingUndated] = useState(false);   // migration 135
  // A row change made by the Date verified / Undated ticks themselves must not re-run the
  // Filing pre-fill (it would wipe auto-filled values not saved yet): it is skipped once.
  const skipPrefillForRef = useRef<number | null>(null);
  const dateVerifiedTickRef = useRef<HTMLInputElement | null>(null);
  const filingDateInputRef = useRef<HTMLInputElement>(null);
  // The document the handwritten / printed dates above belong to. On the render right
  // after a document switch they still hold the PREVIOUS document's dates.
  const [filingDatesDocId, setFilingDatesDocId] = useState<number | null>(null);
  // Handwriting checks made in this sitting, by document: is an approval still needed?
  // (Kept beside `documents` so a finished check never resets the open Filing form.)
  const [handwritingResults, setHandwritingResults] = useState<Map<number, boolean>>(() => new Map());
  const handwritingAskedRef = useRef<Set<number>>(new Set());   // documents already sent for a check this sitting
  const docNeedsApproval = useCallback((d: DocRow): boolean => {
    if (d.status === 'Filed' || isDateSettled(d.date_source)) return false;
    const fresh = handwritingResults.get(d.document_id);
    return fresh !== undefined ? fresh : Number(d.date_confirm_needed) === 1;
  }, [handwritingResults]);
  const pendingApprovalCount = useMemo(
    () => documents.filter(d => Number(d.is_deleted) !== 1 && docNeedsApproval(d)).length,
    [documents, docNeedsApproval],
  );
  const [filingDateCandidates, setFilingDateCandidates] = useState<{ order: string; iso: string; formatId: number | null }[]>([]);

  // ---- Filing drafts (server-side, per user) ----
  // Unsaved Filing edits autosave to the server and are laid back over the saved
  // values when the document is reopened — across document switches, reloads, a
  // closed browser or another device — until it is filed or the draft is discarded.
  const filingDraftsRef = useRef<Map<number, FilingDraft>>(new Map());   // this user's drafts by document id
  const [draftDocIds, setDraftDocIds] = useState<Set<number>>(new Set()); // drives the "Draft" badge + note
  const [draftsLoaded, setDraftsLoaded] = useState(false);
  const [draftResetNonce, setDraftResetNonce] = useState(0);              // bump → re-fill the form from saved values
  const draftPendingRef = useRef<{ docId: number; data: FilingDraft } | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftTouchedRef = useRef<Map<number, number>>(new Map());         // when each draft was last changed (ms)
  const draftSeqRef = useRef(getEditSeq());                               // last user-edit count already drafted
  // The form with no user edits (saved values + auto-fill), to tell a real change from
  // an edit that was put back. null = unknown (a restored draft is laid over the form).
  const draftBaselineRef = useRef<{ docId: number; snap: string } | null>(null);
  const draftLastSnapRef = useRef<string | null>(null);
  const prefillDocIdRef = useRef<number | null>(null);
  const filingVisibleRef = useRef(true);
  // The Folder List (foldersOnly) reuses this screen with no Filing form: nothing there
  // can be "unsaved", so a restored draft or a new folder must not raise the prompt.
  filingVisibleRef.current = !foldersOnly && (!showBookkeeping || rightTab === 'filing');

  // Lay a draft over the form. `onlyFilled` skips blank draft values (used after
  // auto-fill when the draft was taken mid-extraction, so it doesn't blank what
  // auto-fill found; a draft taken after auto-fill keeps its deliberate blanks).
  const applyFilingDraft = useCallback((d: FilingDraft, onlyFilled = false) => {
    const put = <T,>(v: T | undefined, set: (value: T) => void) => {
      if (v === undefined) return;
      if (onlyFilled && (v === '' || v === null || (Array.isArray(v) && v.length === 0))) return;
      set(v);
    };
    put(Array.isArray(d.ownerKeys) ? d.ownerKeys : undefined, setFilingOwnerKeys);
    put(Array.isArray(d.folderIds) ? d.folderIds : undefined, setFilingFolderIds);
    put(d.docuTypeId, setFilingDocuTypeId);
    put(d.natureId, setFilingNatureId);
    put(d.expenseTypeId, setFilingExpenseTypeId);
    put(d.receiptKind, setFilingReceiptKind);
    put(d.partyName, setFilingPartyName);
    put(d.invoiceRef, setFilingInvoiceRef);
    put(d.date, setFilingDate);
    put(d.paymentMethod, setFilingPaymentMethod);
    put(d.paymentMethodId, setFilingPaymentMethodId);
    put(d.paymentCoaId, setFilingPaymentCoaId);
    put(d.taxTypeId, setFilingTaxTypeId);
    put(d.taxRateId, setFilingTaxRateId);
    put(d.taxAmount, setFilingTaxAmount);
    put(d.dateFormatId, setFilingDateFormatId);
    put(d.rawDate, setFilingRawDate);
    put(d.globalBusiId, setFilingGlobalBusiId);
    put(d.amount, setFilingAmount);
    put(d.currency, setFilingCurrency);
    put(d.purpose, setFilingPurpose);
    put(d.tags, setFilingTags);
    if (d.date) setFilingDateAmbiguous(false);   // the user already settled the date
  }, []);

  // Send the pending (debounced) draft now. `keepalive` = the page is closing.
  const flushFilingDraft = useCallback((keepalive = false) => {
    if (draftTimerRef.current) { clearTimeout(draftTimerRef.current); draftTimerRef.current = null; }
    const pending = draftPendingRef.current;
    if (!pending) return;
    draftPendingRef.current = null;
    saveDocDraft(pending.docId, FILING_DRAFT_FORM, pending.data, keepalive);
  }, []);

  // Forget a document's draft (it was filed / deleted / discarded).
  const dropFilingDraft = useCallback((docId: number) => {
    if (draftPendingRef.current?.docId === docId) {
      draftPendingRef.current = null;
      if (draftTimerRef.current) { clearTimeout(draftTimerRef.current); draftTimerRef.current = null; }
    }
    if (!filingDraftsRef.current.delete(docId)) return;
    setDraftDocIds(new Set(filingDraftsRef.current.keys()));
    clearDocDraft(docId, FILING_DRAFT_FORM);
  }, []);

  // Drop a draft and put the form back to the document's saved values ("Discard changes"
  // in the unsaved-changes modal).
  const discardFilingDraft = useCallback((docId: number) => {
    dropFilingDraft(docId);
    setFormDirty(false);
    setDraftResetNonce(n => n + 1);
  }, [dropFilingDraft]);
  // Part 2 master tables (reference data; localStorage seed for account owners).
  const [paymentMethods, setPaymentMethods] = useState<Record<string, any>[]>([]);
  const [taxTypes, setTaxTypes] = useState<Record<string, any>[]>([]);
  const [taxRates, setTaxRates] = useState<Record<string, any>[]>([]);
  const [statesData, setStatesData] = useState<Record<string, any>[]>([]);
  const [locationHistory, setLocationHistory] = useState<Record<string, any>[]>([]);

  // state_id → province code (AB, BC, …) for tax labels + province filtering.
  const statesById = useMemo(() => {
    const m = new Map<number, string>();
    // eslint-disable-next-line eqeqeq
    for (const s of statesData) m.set(Number(s.state_id), String(s.state_code || '').toUpperCase());
    return m;
  }, [statesData]);

  // Address of the document's primary owner — drives which province's taxes show.
  // For a BUSINESS, the province is resolved AS OF the document's date from the
  // location history (so a doc from before a move keeps the old province), falling
  // back to the business's current address when there's no matching history period.
  const taxOwnerAddress = useMemo(
    () => ownerTaxLocation(filingOwnerKeys[0], usersData, businessesData, locationHistory, (filingDate || '').slice(0, 10)),
    [filingOwnerKeys, usersData, businessesData, locationHistory, filingDate]);

  // The primary owner's industry (business) type + category — drives expense-type
  // bias resolution (e.g. fuel → Motor vehicle vs Fuel costs depending on business).
  const ownerIndustry = useMemo(() => {
    const key = filingOwnerKeys[0];
    if (!key) return { busiTypeId: null as number | null, busiCategoryId: null as number | null };
    const [oType, oIdStr] = key.split(':');
    if (oType !== 'business' && oType !== 'client_business') return { busiTypeId: null, busiCategoryId: null };
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((x: any) => x.business_id == Number(oIdStr));
    const typeId = b?.industry_type_id ? Number(b.industry_type_id) : null;
    // eslint-disable-next-line eqeqeq
    const t = typeId ? busiTypes.find((x: any) => x.busi_type_id == typeId) : null;
    return { busiTypeId: typeId, busiCategoryId: t ? Number(t.busi_category_id) : null };
  }, [filingOwnerKeys, businessesData, busiTypes]);

  // Auto-suggest an expense type from receipt text. Two-layer model (migration 097):
  //   1. detect a business-agnostic receipt_kind from keywords (fuel, meals, ...);
  //   2. resolve that kind → expense type via acctg_expense_type_bias, biased by the
  //      owner's business (most specific wins: type > category > global default).
  // If no kind is detected, fall back to a direct match_aliases hit on the master.
  const resolveExpenseSuggestion = useCallback((text: string): { expenseTypeId: number; receiptKind: string } | null => {
    const hay = ` ${(text || '').toLowerCase()} `;
    if (!hay.trim() || expenseTypes.length === 0) return null;

    // receipt_kind detectors (ordered; first hit wins)
    const KIND_KEYWORDS: [string, RegExp][] = [
      ['fuel',           /\b(gas|fuel|gasoline|diesel|petro[- ]?canada|petrocanada|esso|shell|husky|ultramar|chevron|mobil|fas gas|costco gas)\b/],
      ['vehicle_repair', /\b(oil change|tire|tires|muffler|brakes?|mr[.\s]?lube|jiffy lube|canadian tire auto)\b/],
      ['materials',      /\b(home depot|rona|lowe'?s|lumber|building suppl|kent building|castle building|drywall|plywood)\b/],
      ['meals',          /\b(restaurant|cafe|café|coffee|tim hortons|starbucks|mcdonald|a&w|subway|pizza|grill|diner)\b/],
      ['tools',          /\b(princess auto|power tool|hand tool|drill|dewalt|milwaukee|makita)\b/],
      ['shipping',       /\b(purolator|ups|fedex|canada post|dhl|freight|courier)\b/],
      ['utilities',      /\b(hydro|enbridge|rogers|bell canada|telus|shaw|internet|wireless)\b/],
      ['office',         /\b(staples|office depot|printer ink|toner)\b/],
      ['insurance',      /\b(insurance|premium|policy)\b/],
      ['travel',         /\b(air canada|westjet|hotel|motel|airbnb|via rail)\b/],
    ];
    let kind = '';
    for (const [k, re] of KIND_KEYWORDS) { if (re.test(hay)) { kind = k; break; } }

    const activeTypes = expenseTypes.filter((e: any) => Number(e.is_deleted) !== 1 && Number(e.is_active) !== 0);
    if (kind) {
      const forKind = expenseBiases.filter((b: any) => b.receipt_kind === kind && Number(b.is_deleted) !== 1 && Number(b.is_active) !== 0);
      // most specific first: exact business type, then category, then global (both NULL)
      const pick =
        forKind.find((b: any) => ownerIndustry.busiTypeId && Number(b.busi_type_id) === ownerIndustry.busiTypeId) ||
        forKind.find((b: any) => ownerIndustry.busiCategoryId && b.busi_type_id == null && Number(b.busi_category_id) === ownerIndustry.busiCategoryId) ||
        forKind.find((b: any) => b.busi_type_id == null && b.busi_category_id == null);
      if (pick) return { expenseTypeId: Number(pick.expense_type_id), receiptKind: kind };
    }

    // Fallback: direct keyword hit on a type's pipe-separated match_aliases.
    for (const e of activeTypes) {
      const aliases = String(e.match_aliases || '').split('|').map(s => s.trim()).filter(Boolean);
      if (aliases.some(a => hay.includes(` ${a} `) || hay.includes(a))) {
        return { expenseTypeId: Number(e.expense_type_id), receiptKind: kind };
      }
    }
    return null;
  }, [expenseTypes, expenseBiases, ownerIndustry]);

  // Tax dropdown options, scoped to the owner's province + abbreviated labels.
  // A province shows its own rates (HST/PST/QST) PLUS federal GST — unless it's an
  // HST province (where HST replaces GST). No province → federal/country-wide only.
  const taxOptionInfo = useMemo(() => {
    // The document's date drives WHICH rate is in effect (effective_from..effective_to);
    // the province rule lives in utils/salesTaxApplicable (shared with Masters → Taxes).
    const ownerState = taxOwnerAddress.stateCode;
    const { rates: applicable, scopedByProvince } = applicableSalesTaxRates({
      rates: taxRates, types: taxTypes, statesById,
      stateCode: ownerState, hasOwner: taxOwnerAddress.hasOwner,
      onDate: (filingDate || '').slice(0, 10),
    });
    const options = applicable.map(r => ({ value: String(r.tax_rate_id), label: salesTaxRateLabel(r, taxTypes, statesById) }));
    return {
      options,
      scopedByProvince,
      // Prompt to add an address only once an owner is chosen but has no province.
      needsAddress: taxOwnerAddress.hasOwner && !ownerState,
      ownerState,
    };
  }, [taxRates, taxTypes, statesById, taxOwnerAddress, filingDate]);
  const [dateFormats, setDateFormats] = useState<Record<string, any>[]>([]);

  // Bookkeeping form
  const [bkOwnerKey, setBkOwnerKey] = useState('');
  const [bkDocuTypeId, setBkDocuTypeId] = useState('');
  const [bkNatureId, setBkNatureId] = useState('');
  // Account head + memo (migration 142). "Auto" = filled in by the app and still untouched,
  // so it follows the nature / party / payment method until the user changes it.
  const [bkCoaId, setBkCoaId] = useState('');
  const [bkCoaAuto, setBkCoaAuto] = useState(false);
  const [bkMemo, setBkMemo] = useState('');
  const [bkMemoAuto, setBkMemoAuto] = useState(false);
  const [bkPartyName, setBkPartyName] = useState('');
  const [bkInvoiceRef, setBkInvoiceRef] = useState('');
  const [bkDate, setBkDate] = useState('');
  const [bkDueDate, setBkDueDate] = useState('');
  const [bkAmount, setBkAmount] = useState('');
  const [bkCurrency, setBkCurrency] = useState('');
  const [bkTaxRate, setBkTaxRate] = useState('');

  // Auto-fill (transcription)
  const [extractedText, setExtractedText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [autoFillApplied, setAutoFillApplied] = useState<number | null>(null); // doc id that was auto-filled

  // Business facts extracted on open (address/phone/email/website/category) — pushed to
  // the global directory at submit (kept in a ref to avoid re-render churn).
  const filingBusinessFactsRef = useRef<Record<string, any> | null>(null);

  // =============================================
  // Owner scope (multi-select) + role gating
  // =============================================
  // Only the account owner (owner_id == null) / admin may scope across other
  // owners; a regular user (family member) is locked to their own folders/docs.
  const canPickOwners = !!currentUser && currentUser.owner_id == null;

  // The current user's own owner key (used to lock a regular user to themselves).
  const selfOwnerKey = useMemo(() => {
    if (!currentUser) return '';
    const e = ownerEntities.find((e: OwnerEntity) =>
      // eslint-disable-next-line eqeqeq
      e.owner_id == currentUser.user_id && (e.owner_type === 'account_owner' || e.owner_type === 'family_member'));
    return e ? `${e.owner_type}:${e.owner_id}` : `family_member:${currentUser.user_id}`;
  }, [currentUser, ownerEntities]);

  // Entities offered in the owner picker — all for the account owner, just self otherwise.
  const pickableOwnerEntities = useMemo(() => {
    if (canPickOwners) return ownerEntities;
    if (!currentUser) return [];
    return ownerEntities.filter((e: OwnerEntity) =>
      // eslint-disable-next-line eqeqeq
      e.owner_id == currentUser.user_id && (e.owner_type === 'account_owner' || e.owner_type === 'family_member'));
  }, [canPickOwners, ownerEntities, currentUser]);

  // The owner keys actually applied to the document list + folder tree. A
  // non-owner is ALWAYS pinned to self, regardless of the (hidden) selection.
  const scopedOwnerKeys = useMemo(
    () => canPickOwners ? selectedDocOwnerIds : (selfOwnerKey ? [selfOwnerKey] : []),
    [canPickOwners, selectedDocOwnerIds, selfOwnerKey]
  );

  // Toggle one owner in/out of the multi-select scope.
  const toggleOwner = useCallback((key: string) => {
    setSelectedDocOwnerIds(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setSelectedDocId(null);
  }, []);

  // Does a document belong to ANY of the given owner keys? (matches the
  // historical single-select semantics: account_owner → user_id, else owner_type+owner_id).
  const docMatchesOwnerKeys = useCallback((d: DocRow, keys: string[]): boolean => {
    return keys.some(key => {
      const [oType, oIdStr] = key.split(':');
      const oId = Number(oIdStr);
      // eslint-disable-next-line eqeqeq
      if (oType === 'account_owner') return d.user_id == oId;
      // eslint-disable-next-line eqeqeq
      return d.owner_type === oType && d.owner_id == oId;
    });
  }, []);

  // Active secondary-owner rows (dms_document_owners) grouped by document. Each row
  // gives an extra owner visibility of the SAME stored doc plus its own folder
  // placement — so one document surfaces under every owner & inside every folder.
  const docOwnersByDoc = useMemo(() => {
    const m = new Map<number, DocOwnerRow[]>();
    for (const r of docOwners) {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.is_active == 0) continue;
      const arr = m.get(r.document_id) || [];
      arr.push(r);
      m.set(r.document_id, arr);
    }
    return m;
  }, [docOwners]);

  // Owner keys ("type:id") a document is shared with via dms_document_owners.
  const docOwnerKeySet = useCallback((d: DocRow): Set<string> =>
    new Set((docOwnersByDoc.get(d.document_id) || []).map(r => `${r.owner_type}:${r.owner_id}`)),
  [docOwnersByDoc]);

  // Owner-scope match incl. shared owners: primary (dms_documents) OR any shared row.
  const docMatchesOwnerKeysX = useCallback((d: DocRow, keys: string[]): boolean => {
    if (docMatchesOwnerKeys(d, keys)) return true;
    const shared = docOwnerKeySet(d);
    return keys.some(k => shared.has(k));
  }, [docMatchesOwnerKeys, docOwnerKeySet]);

  const docMatchesEntityX = useCallback((d: DocRow, e: OwnerEntity): boolean =>
    docMatchesEntity(d, e) || docOwnerKeySet(d).has(`${e.owner_type}:${e.owner_id}`),
  [docOwnerKeySet]);

  // Active multi-folder rows (dms_document_folders) grouped by document — the doc is
  // filed in each of these folders, independent of its owners.
  const docFoldersByDoc = useMemo(() => {
    const m = new Map<number, DocFolderRow[]>();
    for (const r of docFolders) {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.is_active == 0) continue;
      const arr = m.get(r.document_id) || [];
      arr.push(r);
      m.set(r.document_id, arr);
    }
    return m;
  }, [docFolders]);

  // Folder ids a document is filed into: the primary folder (dms_documents) PLUS the
  // multi-folder rows (dms_document_folders) PLUS — for back-compat with docs filed
  // before the folders table — each shared owner's own folder.
  const docInFolderSet = useCallback((d: DocRow, folderIds: Set<number>): boolean => {
    if (d.dms_folders_id != null && folderIds.has(Number(d.dms_folders_id))) return true;
    if ((docFoldersByDoc.get(d.document_id) || []).some(r => folderIds.has(Number(r.folder_id)))) return true;
    return (docOwnersByDoc.get(d.document_id) || []).some(
      r => r.folder_id != null && folderIds.has(Number(r.folder_id))
    );
  }, [docOwnersByDoc, docFoldersByDoc]);

  // Load data from API (falls back to localStorage)
  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [usersRows, bizRows, docsRows, docuTypesRows, bankTxnRows, coaRows, glRows,
               payMethodRows, taxTypeRows, taxRateRows, dateFmtRows, statesRows, locationHistRows, docOwnerRows, docFolderRows,
               expenseTypeRows, expenseBiasRows, busiTypeRows,
               locationRows, unitRows, natureTableRows, natureAccountTableRows] = await Promise.all([
          loadTable('users'),
          loadTable('user_businesses'),
          loadTable('dms_documents'),
          loadTable('acctg_docu_type'),
          loadTable('acctg_bank_txn'),
          loadTable('acctg_coa'),
          loadTable('acctg_gl_journal'),
          loadTable('acctg_payment_method'),
          loadTable('acctg_salestax_type'),
          loadTable('acctg_salestax_rate'),
          loadTable('site_mstr_date_formats'),
          loadTable('site_mstr_states').catch(() => [] as any[]),
          loadTable('user_business_location_history').catch(() => [] as any[]),
          // Isolated: a failure to load shared-owner rows must NOT reject the whole
          // batch (which would drop documents/folders to stale localStorage and make
          // filed docs look unfiled). Degrade to [] instead.
          loadTable('dms_document_owners').catch(() => [] as any[]),
          loadTable('dms_document_folders').catch(() => [] as any[]),
          // Expense tagging masters (migration 097) — degrade to [] if unavailable.
          loadTable('acctg_expense_type').catch(() => [] as any[]),
          loadTable('acctg_expense_type_bias').catch(() => [] as any[]),
          loadTable('acctg_busi_type').catch(() => [] as any[]),
          loadTable('user_business_location').catch(() => [] as any[]),
          loadTable('user_business_unit').catch(() => [] as any[]),
          loadTable('acctg_doc_nature').catch(() => [] as any[]),
          loadTable('acctg_doc_nature_account').catch(() => [] as any[]),
        ]);
        if (cancelled) return;
        setNatureRows(natureTableRows as any[]);
        setNatureAccountRows(natureAccountTableRows as any[]);
        setLocationsData(locationRows as any[]);
        setUnitsData(unitRows as any[]);
        setExpenseTypes(expenseTypeRows as any[]);
        setExpenseBiases(expenseBiasRows as any[]);
        setBusiTypes(busiTypeRows as any[]);
        setDocOwners(docOwnerRows as DocOwnerRow[]);
        setDocFolders(docFolderRows as DocFolderRow[]);
        setUsersData(usersRows);
        setBusinessesData(bizRows);
        setGlJournals(glRows);
        setPaymentMethods(payMethodRows);
        setTaxTypes(taxTypeRows);
        setTaxRates(taxRateRows);
        setDateFormats(dateFmtRows);
        setStatesData(statesRows);
        setLocationHistory(locationHistRows);
        // Strip file_base64 from docs to keep memory lean
        const docsWithoutBase64 = docsRows.map((d: any) => {
          const { file_base64, ...rest } = d;
          return rest;
        });
        setDocuments(docsWithoutBase64 as DocRow[]);
        setDocuTypes(docuTypesRows as DocuTypeRow[]);
        setBankTxns(bankTxnRows as BankTxnRow[]);
        setCoaAccounts(coaRows as CoaRow[]);
        setDataLoaded(true);
      } catch {
        // Fallback to localStorage if API fails
        if (cancelled) return;
        setUsersData(getTableData('users'));
        setBusinessesData(getTableData('user_businesses'));
        const docs = getTableData('dms_documents', { omit: ['file_base64'] }) as DocRow[];
        setDocuments(docs);
        setDocuTypes(getTableData('acctg_docu_type') as DocuTypeRow[]);
        setBankTxns(getTableData('acctg_bank_txn') as BankTxnRow[]);
        setCoaAccounts(getTableData('acctg_coa') as CoaRow[]);
        setGlJournals(getTableData('acctg_gl_journal'));
        setPaymentMethods(getTableData('acctg_payment_method'));
        setTaxTypes(getTableData('acctg_salestax_type'));
        setTaxRates(getTableData('acctg_salestax_rate'));
        setDateFormats(getTableData('site_mstr_date_formats'));
        setStatesData(getTableData('site_mstr_states'));
        setLocationHistory(getTableData('user_business_location_history'));
        setLocationsData(getTableData('user_business_location'));
        setUnitsData(getTableData('user_business_unit'));
        setDocOwners(getTableData('dms_document_owners') as DocOwnerRow[]);
        setDocFolders(getTableData('dms_document_folders') as DocFolderRow[]);
        setNatureRows(getTableData('acctg_doc_nature'));
        setNatureAccountRows(getTableData('acctg_doc_nature_account'));
        setDataLoaded(true);
      }

      // Check if navigated here from Upload page with a specific doc
      const navDocId = sessionStorage.getItem('akt_navigate_to_doc');
      if (navDocId) {
        sessionStorage.removeItem('akt_navigate_to_doc');
        const id = Number(navDocId);
        if (!isNaN(id)) { setSelectedDocId(id); navDocRef.current = { id, pass: 0 }; arrivedByLinkRef.current = true; }
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  // Party masters (best-effort: Filing works without them, as free text).
  const [partyReloadKey, setPartyReloadKey] = useState(0);
  // Party > document type > date layout (Masters). Kept in refs for the auto-read, which
  // must not re-run when the masters finish loading.
  const partyMastersRef = useRef<PartyMaster[]>([]);
  const partyDocTypesRef = useRef<PartyDocType[]>([]);
  const dateFormatsRef = useRef<Record<string, any>[]>([]);
  partyMastersRef.current = partyMasters;
  dateFormatsRef.current = dateFormats;
  const [partyDocTypesTick, setPartyDocTypesTick] = useState(0);   // bumps when the document types (re)load
  useEffect(() => {
    let cancelled = false;
    loadTable('acctg_party_doc_type').catch(() => [] as any[]).then(rows => {
      if (cancelled) return;
      partyDocTypesRef.current = (rows as any[])
        // eslint-disable-next-line eqeqeq
        .filter(r => r.is_deleted != 1 && r.is_active != 0)
        .map(toPartyDocType);
      setPartyDocTypesTick(k => k + 1);
    });
    return () => { cancelled = true; };
  }, [partyReloadKey]);
  // The date layout Masters holds for this party's kind of document: the party is found
  // by name / legal name / nickname / alias; its document type by the words on the page
  // and, when the printed date is known, by which of the party's layouts that date fits.
  const masterDateFormat = useCallback((partyName: string, text: string, rawDate?: string): { formatId: number; label: string } | null => {
    const q = partyName.trim().toLowerCase();
    if (!q) return null;
    const parties = partyMastersRef.current.filter(m => m.account_owner_id === accountOwnerId &&
      (m.name.toLowerCase() === q || m.legal_name.toLowerCase() === q || (m.nickname && m.nickname.toLowerCase() === q) || m.aliases.some(a => a.toLowerCase() === q)));
    const formats = dateFormatsRef.current as any[];
    for (const m of parties) {
      const types = partyDocTypesRef.current.filter(t => t.party_kind === PARTY_KIND_KEY[m.kind] && t.party_id === m.id && t.date_format_id);
      const hit = rawDate
        // eslint-disable-next-line eqeqeq
        ? pickPartyDocType(types, text, t => { const f = formats.find(x => x.date_format_id == t.date_format_id); return !!f && formatFitsRawDate(rawDate, f, formats); })
        : matchPartyDocType(types, text);
      if (hit && hit.date_format_id) return { formatId: hit.date_format_id, label: `${m.name} › ${hit.doc_type_name}` };
    }
    return null;
  }, [accountOwnerId]);
  const [filingDateLayoutFrom, setFilingDateLayoutFrom] = useState('');   // "Vendor › Document type" when Masters supplied the layout
  useEffect(() => {
    let cancelled = false;
    Promise.all(PARTY_MASTER_TABLES.map(t => loadTable(t.table).catch(() => [] as any[]))).then(lists => {
      if (cancelled) return;
      const all: PartyMaster[] = [];
      lists.forEach((rows, i) => {
        const t = PARTY_MASTER_TABLES[i];
        (rows as any[]).forEach(r => {
          // eslint-disable-next-line eqeqeq
          if (r.is_deleted == 1 || r.is_active == 0 || !r[t.nameCol]) return;
          all.push({
            kind: t.kind, id: Number(r[t.pk]), account_owner_id: Number(r.account_owner_id),
            owner_type: String(r.owner_type || ''), owner_id: Number(r.owner_id),
            name: String(r[t.nameCol]).trim(), legal_name: String(r.legal_name || '').trim(),
            nickname: String(r.nickname || '').trim(),
            aliases: String(r.alias_names || '').split(',').map(a => a.trim()).filter(Boolean), tags: String(r.tags || ''), folder_id: r.folder_id != null ? Number(r.folder_id) : null,
            default_coa_id: r.default_coa_id != null ? Number(r.default_coa_id) : null,
          });
        });
      });
      setPartyMasters(all);
    });
    return () => { cancelled = true; };
  }, [partyReloadKey]);

  // Load the folder trees — re-runs when the storage table changes (i.e. when the
  // pillar switches between Your Accounts and Client's Accounts, or the client changes).
  useEffect(() => {
    let cancelled = false;
    async function fetchFolders() {
      try {
        const [foldersRows, folderOwnersRows] = await Promise.all([
          loadTable(FOLDERS_TABLE),
          loadTable(FOLDER_OWNERS_TABLE),
        ]);
        if (cancelled) return;
        setFolders(foldersRows as FolderRow[]);
        setFolderOwners(folderOwnersRows as FolderOwnerRow[]);
      } catch {
        if (cancelled) return;
        setFolders(getTableData(FOLDERS_TABLE) as FolderRow[]);
        setFolderOwners(getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[]);
      }
    }
    fetchFolders();
    return () => { cancelled = true; };
  }, [FOLDERS_TABLE, FOLDER_OWNERS_TABLE, folderRefreshKey]);

  // Close the document Move popover on outside click
  useEffect(() => {
    if (!showDocMove) return;
    const handler = (e: MouseEvent) => {
      if (docMoveRef.current && !docMoveRef.current.contains(e.target as Node)) {
        setShowDocMove(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDocMove]);

  // Close the owner picker popover on outside click
  useEffect(() => {
    if (!ownerPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (ownerPickerRef.current && !ownerPickerRef.current.contains(e.target as Node)) {
        setOwnerPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ownerPickerOpen]);

  useEffect(() => {
    if (!sortOpen) return;
    const handler = (e: MouseEvent) => {
      if (sortPickerRef.current && !sortPickerRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortOpen]);

  // Close folder context menu on outside click
  useEffect(() => {
    if (folderMenuOpenId === null) return;
    const handler = (e: MouseEvent) => {
      if (folderMenuRef.current && !folderMenuRef.current.contains(e.target as Node)) {
        setFolderMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [folderMenuOpenId]);

  // Clear success message after a few seconds
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), successMsg.includes(' Masters: ') ? 9000 : 3000);   // a Masters note takes longer to read
    return () => clearTimeout(t);
  }, [successMsg]);

  // =============================================
  // Folder descendant helper (for recursive folder filtering)
  // =============================================
  const getFolderAndDescendantIds = useCallback((folderId: number): Set<number> => {
    const ids = new Set<number>([folderId]);
    const activeFolders = folders.filter(f => f.is_deleted !== 1 && f.is_active !== 0);
    const queue = [folderId];
    while (queue.length > 0) {
      const parentId = queue.shift()!;
      activeFolders.forEach(f => {
        if (f.parent_folder_id === parentId && !ids.has(f.folder_id)) {
          ids.add(f.folder_id);
          queue.push(f.folder_id);
        }
      });
    }
    return ids;
  }, [folders]);

  // =============================================
  // Folder actions (Record Room context menu)
  // =============================================
  // Notify the account owner when a non-owner mutates a shared folder.
  const notifyOwnerOfFolderChange = useCallback((summary: string) => {
    if (!currentUser || currentUser.user_id === accountOwnerId) return;
    addNotification({
      account_owner_id: accountOwnerId,
      type: 'folder_changed',
      title: 'Folder updated',
      message: `${currentUser.first_name} ${summary}`,
      actor_user_id: currentUser.user_id,
      actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
    });
  }, [currentUser, accountOwnerId]);

  // --- Add folder: delegate to the Manage Folders popup ---
  // Folder creation (root or sub-folder) goes through the full Manage Folders UI
  // so owner selection + validation are enforced (the old inline add allowed a
  // sub-folder with no owner). On close we reload the tree to show new folders.
  const openManageFolders = useCallback(() => {
    setFolderMenuOpenId(null);
    setRenamingFolderId(null);
    setConfirmDeleteFolderId(null);
    setManageFoldersAction(null);
    setShowManageFolders(true);
  }, []);

  // From a folder's own menu: "Edit Folder" / "Add Sub Folder…" open the popup already
  // on that folder (edit it, or a new folder with it as the parent).
  const openManageFoldersFor = useCallback((type: ManageFoldersInitialAction['type'], folderId: number) => {
    setFolderMenuOpenId(null);
    setRenamingFolderId(null);
    setConfirmDeleteFolderId(null);
    setManageFoldersAction({ type, folderId });
    setShowManageFolders(true);
  }, []);

  // Opened from the Filing "Select folder(s)" field ("Add new folder…"): remember which
  // folders existed, so the one created in the popup can be selected when it closes.
  const newFolderWatchRef = useRef<{ docId: number; known: Set<number>; closedAt: number } | null>(null);
  const openManageFoldersFromFiling = useCallback(() => {
    if (selectedDocId != null) {
      newFolderWatchRef.current = { docId: selectedDocId, known: new Set(folders.map(f => f.folder_id)), closedAt: 0 };
    }
    openManageFolders();
  }, [selectedDocId, folders, openManageFolders]);

  const closeManageFolders = useCallback(() => {
    setShowManageFolders(false);
    if (newFolderWatchRef.current) newFolderWatchRef.current.closedAt = Date.now();
    setFolderRefreshKey(k => k + 1);   // re-fetch folders so new ones appear
  }, []);

  // --- Rename ---
  const startRename = useCallback((folder: FolderRow) => {
    setFolderMenuOpenId(null);
    setConfirmDeleteFolderId(null);
    setRenamingFolderId(folder.folder_id);
    setRenameValue(folder.folder_name);
  }, []);

  const submitRename = useCallback(async () => {
    const id = renamingFolderId;
    const name = renameValue.trim();
    if (!id || !name) return;
    const target = folders.find(f => f.folder_id === id);
    if (target && name === target.folder_name) { setRenamingFolderId(null); return; }
    const dup = folders.some(f =>
      f.folder_id !== id && f.parent_folder_id === (target?.parent_folder_id ?? null) &&
      f.is_deleted !== 1 && f.folder_name.trim().toLowerCase() === name.toLowerCase()
    );
    if (dup) { setSuccessMsg(`A folder named "${name}" already exists here.`); return; }

    setFolderBusy(true);
    try {
      await saveRow(FOLDERS_TABLE, id, { folder_name: name, updated_at: NOW_ISO() }, 'folder_id');
      setFolders(prev => prev.map(f => f.folder_id === id ? { ...f, folder_name: name } : f));
      setRenamingFolderId(null);
      notifyOwnerOfFolderChange(`renamed a folder to "${name}"`);
      setSuccessMsg('Folder renamed.');
    } catch {
      setSuccessMsg('Could not rename the folder. Please try again.');
    } finally {
      setFolderBusy(false);
    }
  }, [renamingFolderId, renameValue, folders, FOLDERS_TABLE, notifyOwnerOfFolderChange]);

  // --- Delete (soft-delete folder + sub-folders, keeps documents) ---
  const startDeleteFolder = useCallback((folderId: number) => {
    setFolderMenuOpenId(null);
    setRenamingFolderId(null);
    // Safety: only an EMPTY folder (no documents in it or any sub-folder) may be
    // deleted. If it still holds documents, surface them in the list pane and block.
    const ids = getFolderAndDescendantIds(folderId);
    const docCount = documents.filter(d =>
      // eslint-disable-next-line eqeqeq
      d.is_deleted != 1 && d.is_active == 1 &&
      d.dms_folders_id != null && ids.has(d.dms_folders_id)
    ).length;
    if (docCount > 0) {
      const name = folders.find(f => f.folder_id === folderId)?.folder_name || 'folder';
      setActiveFilter('all');
      setSelectedRecordFolderId(folderId);   // doc pane now lists this folder's docs (incl. sub-folders)
      setSelectedDocId(null);
      setConfirmDeleteFolderId(null);
      setSuccessMsg(`"${name}" still has ${docCount} document${docCount === 1 ? '' : 's'} — empty it first (move or reject them), then delete.`);
      return;
    }
    setConfirmDeleteFolderId(folderId);
  }, [getFolderAndDescendantIds, documents, folders]);

  const confirmDeleteFolder = useCallback(async () => {
    const folderId = confirmDeleteFolderId;
    if (!folderId || !currentUser) return;
    setFolderBusy(true);
    const now = NOW_ISO();
    const ids = Array.from(getFolderAndDescendantIds(folderId));
    const folderName = folders.find(f => f.folder_id === folderId)?.folder_name || 'folder';
    try {
      for (const id of ids) {
        await saveRow(FOLDERS_TABLE, id, { is_deleted: 1, updated_at: now }, 'folder_id');
        const owners = folderOwners.filter(o => o.folder_id === id && o.is_deleted !== 1);
        for (const o of owners) {
          await saveRow(FOLDER_OWNERS_TABLE, o.folder_owner_id, { is_deleted: 1, updated_at: now }, 'folder_owner_id');
        }
      }
      const idSet = new Set(ids);
      setFolders(prev => prev.map(f => idSet.has(f.folder_id) ? { ...f, is_deleted: 1 } : f));
      setFolderOwners(prev => prev.map(o => idSet.has(o.folder_id) ? { ...o, is_deleted: 1 } : o));
      if (selectedRecordFolderId !== null && idSet.has(selectedRecordFolderId)) setSelectedRecordFolderId(null);
      setConfirmDeleteFolderId(null);
      notifyOwnerOfFolderChange(`deleted folder "${folderName}"`);
      setSuccessMsg(`Folder "${folderName}" deleted.`);
    } catch {
      setSuccessMsg('Could not delete the folder. Please try again.');
    } finally {
      setFolderBusy(false);
    }
  }, [confirmDeleteFolderId, currentUser, getFolderAndDescendantIds, folders, folderOwners, selectedRecordFolderId, FOLDERS_TABLE, FOLDER_OWNERS_TABLE, notifyOwnerOfFolderChange]);

  // Stable "owner signature" of a folder (sorted "type:id" keys of its active owners).
  // Two folders with the same signature belong to the same owner(s).
  const folderOwnerKey = useCallback((folderId: number): string => {
    return folderOwners
      .filter(o => o.folder_id === folderId && o.is_deleted !== 1)
      .map(o => `${o.owner_type}:${o.owner_id}`)
      .sort()
      .join('|');
  }, [folderOwners]);

  // A folder may be dropped onto a target ONLY when the target is an umbrella folder
  // (umbrellas exist to group folders across owners) OR shares the dragged folder's
  // exact owner set. This blocks moving one user's folder under another user's folder.
  const canDropFolderInto = useCallback((draggedId: number, targetId: number): boolean => {
    const target = folders.find(f => f.folder_id === targetId);
    if (!target) return false;
    if (Number(target.umbrella_folder) === 1) return true;        // umbrella → any owner welcome
    return folderOwnerKey(draggedId) === folderOwnerKey(targetId); // else: same owner only
  }, [folders, folderOwnerKey]);

  // --- Move folder (drag-drop re-parent) — maintains the parent/child owner rule ---
  // Re-parents the folder, recomputes depth/path for its whole subtree, and (when
  // dropped under a NON-umbrella parent) re-owns the subtree to the parent's owner(s).
  const moveFolder = useCallback(async (folderId: number, newParentId: number | null) => {
    const moved = folders.find(f => f.folder_id === folderId);
    if (!moved || !currentUser) return;
    // eslint-disable-next-line eqeqeq
    if (moved.parent_folder_id == newParentId) return;                 // no change
    const subtree = getFolderAndDescendantIds(folderId);
    if (newParentId != null && subtree.has(newParentId)) {
      setSuccessMsg("Can't move a folder into itself or one of its sub-folders.");
      return;
    }
    // Owner guard: only same-owner targets (or umbrella folders) accept the drop —
    // a folder owned by one user can't be moved under a different user's folder.
    if (newParentId != null && !canDropFolderInto(folderId, newParentId)) {
      setSuccessMsg(`"${moved.folder_name}" can only be moved under an umbrella folder or a folder with the same owner.`);
      setDraggedFolderId(null);
      setDropTargetId(null);
      return;
    }
    const newParent = newParentId != null ? folders.find(f => f.folder_id === newParentId) : null;
    const parentNonUmbrella = !!newParent && Number(newParent.umbrella_folder) !== 1;
    const parentOwners = parentNonUmbrella
      ? folderOwners.filter(o => o.folder_id === newParent!.folder_id && o.is_deleted !== 1)
      : null;

    setFolderMoving(true);
    const now = NOW_ISO();
    const ids = Array.from(subtree);                                   // BFS order, self first
    try {
      // 1. Re-parent the dragged folder
      await saveRow(FOLDERS_TABLE, folderId, { parent_folder_id: newParentId, umbrella_folder: 0, updated_at: now }, 'folder_id');
      // 2. Recompute depth + path for the whole subtree (parents before children)
      const local = folders.map(f => f.folder_id === folderId ? { ...f, parent_folder_id: newParentId } : { ...f });
      for (const id of ids) {
        const node = local.find(f => f.folder_id === id);
        if (!node) continue;
        const depth = computeFolderDepth(node.parent_folder_id, local);
        const path = computeFolderPath(id, local);
        node.folder_depth = depth;                                     // children read the updated parent depth
        await saveRow(FOLDERS_TABLE, id, { folder_depth: depth, folder_path: path, updated_at: now }, 'folder_id');
      }
      // 3. Owner inheritance: a non-umbrella parent forces its owner(s) onto the subtree
      if (parentOwners) {
        for (const id of ids) {
          const existing = folderOwners.filter(o => o.folder_id === id && o.is_deleted !== 1);
          for (const o of existing) {
            await saveRow(FOLDER_OWNERS_TABLE, o.folder_owner_id, { is_deleted: 1, updated_at: now }, 'folder_owner_id');
          }
          for (let i = 0; i < parentOwners.length; i++) {
            const po = parentOwners[i];
            await addRow(FOLDER_OWNERS_TABLE, {
              folder_id: id, owner_type: po.owner_type, owner_id: po.owner_id,
              is_primary: i === 0 ? 1 : 0, access_level: 'manage',
              is_active: 1, is_deleted: 0, added_by: currentUser.user_id,
              created_at: now, updated_at: now,
            }, 'folder_owner_id');
          }
        }
      }
      setFolderRefreshKey(k => k + 1);                                 // re-fetch the tree
      notifyOwnerOfFolderChange(`moved folder "${moved.folder_name}"`);
      setSuccessMsg(`Moved "${moved.folder_name}".`);
    } catch {
      setSuccessMsg('Could not move the folder. Please try again.');
    } finally {
      setFolderMoving(false);
      setDraggedFolderId(null);
      setDropTargetId(null);
    }
  }, [folders, folderOwners, currentUser, getFolderAndDescendantIds, canDropFolderInto, FOLDERS_TABLE, FOLDER_OWNERS_TABLE, notifyOwnerOfFolderChange]);

  // =============================================
  // Drag a still-unfiled document onto a folder to file it.
  // Rules: only documents NOT yet in a folder; the document's owner must match the
  // folder's owner — UNLESS the document has no owner assigned (raw), which may be
  // dropped into any folder. (Defined before the folder drag handlers, which depend
  // on these for combined folder-or-document drop handling.)
  // =============================================
  const canDropDocIntoFolder = useCallback((docId: number, folderId: number): boolean => {
    const doc = documents.find(d => d.document_id === docId);
    if (!doc) return false;
    if (doc.dms_folders_id != null) return false;                    // already filed
    if (!(doc.owner_type && doc.owner_id)) return true;              // raw → any folder
    const docKey = `${doc.owner_type}:${doc.owner_id}`;
    return folderOwners
      .filter(o => o.folder_id === folderId && o.is_deleted !== 1)
      .some(o => `${o.owner_type}:${o.owner_id}` === docKey);
  }, [documents, folderOwners]);

  const moveDocToFolder = useCallback(async (docId: number, folderId: number) => {
    if (!canDropDocIntoFolder(docId, folderId)) return;
    const doc = documents.find(d => d.document_id === docId);
    const folder = folders.find(f => f.folder_id === folderId);
    const updates: Record<string, any> = { dms_folders_id: folderId, updated_at: NOW_ISO() };

    // A still-raw document (no owner) inherits the folder's primary owner on drop —
    // it's no longer raw afterward. (Umbrella folders carry no owner → nothing to
    // inherit; the doc stays owner-less but is now filed.) For person owners the
    // owner_id IS a user_id, so mirror it into user_id to keep owner-scoping correct.
    const wasRaw = !(doc?.owner_type && doc?.owner_id);
    let assignedName = '';
    if (wasRaw) {
      const fOwners = folderOwners.filter(o => o.folder_id === folderId && o.is_deleted !== 1);
      const primary = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
      if (primary) {
        updates.owner_type = primary.owner_type;
        updates.owner_id = primary.owner_id;
        if (primary.owner_type === 'account_owner' || primary.owner_type === 'family_member') {
          updates.user_id = primary.owner_id;
        }
        assignedName = getOwnerName(primary.owner_type, primary.owner_id, usersData, businessesData);
      }
    }

    try {
      await saveRow('dms_documents', docId, updates, 'document_id');
    } catch { /* dataAccess handles localStorage fallback */ }
    setDocuments(prev => prev.map(d => d.document_id === docId ? { ...d, ...updates } : d));
    setSuccessMsg(assignedName
      ? `Filed "${doc?.filename || 'document'}" into "${folder?.folder_name || 'folder'}" — owner set to ${assignedName}.`
      : `Filed "${doc?.filename || 'document'}" into "${folder?.folder_name || 'folder'}".`);
  }, [canDropDocIntoFolder, documents, folders, folderOwners, usersData, businessesData]);

  const handleFolderDragStart = useCallback((e: React.DragEvent, folderId: number) => {
    setDraggedFolderId(folderId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(folderId)); } catch { /* ignore */ }
  }, []);
  const handleFolderDragOver = useCallback((e: React.DragEvent, folderId: number) => {
    // Document drag (filing an unfiled doc into a folder) takes precedence.
    if (draggedDocId != null) {
      if (canDropDocIntoFolder(draggedDocId, folderId)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTargetId(prev => (prev === folderId ? prev : folderId));
      } else {
        e.dataTransfer.dropEffect = 'none';
        setDropTargetId(prev => (prev === folderId ? null : prev));
      }
      return;
    }
    if (draggedFolderId == null || draggedFolderId === folderId) return;
    // Invalid target (different owner, non-umbrella) → show a no-drop cursor and
    // don't highlight it. Not calling preventDefault means the drop won't fire here.
    if (!canDropFolderInto(draggedFolderId, folderId)) {
      e.dataTransfer.dropEffect = 'none';
      setDropTargetId(prev => (prev === folderId ? null : prev));
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(prev => (prev === folderId ? prev : folderId));
  }, [draggedFolderId, draggedDocId, canDropFolderInto, canDropDocIntoFolder]);
  const handleFolderDrop = useCallback((e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedDocId != null) {
      const did = draggedDocId;
      setDropTargetId(null);
      setDraggedDocId(null);
      moveDocToFolder(did, folderId);
      return;
    }
    const src = draggedFolderId;
    setDropTargetId(null);
    setDraggedFolderId(null);
    if (src != null && src !== folderId) moveFolder(src, folderId);
  }, [draggedFolderId, draggedDocId, moveFolder, moveDocToFolder]);
  const handleFolderDragEnd = useCallback(() => { setDraggedFolderId(null); setDropTargetId(null); }, []);

  // --- Export folder document list to CSV ---
  const handleExportCsv = useCallback((folder: FolderRow) => {
    setFolderMenuOpenId(null);
    const folderIds = getFolderAndDescendantIds(folder.folder_id);
    const docs = documents.filter(d =>
      d.is_deleted !== 1 && d.is_active !== 0 &&
      d.dms_folders_id != null && folderIds.has(d.dms_folders_id)
    );
    if (docs.length === 0) {
      setSuccessMsg('No documents in this folder to export.');
      return;
    }
    const docuTypeName = (id: number | null) =>
      id == null ? '' : (docuTypes.find(dt => dt.docu_type_id === id)?.docu_type_name || '');
    const headers = ['Document ID', 'Filename', 'Owner', 'Document Type', 'Party Name', 'Invoice/Ref', 'Document Date', 'Amount', 'Currency', 'Status', 'Upload Date'];
    const lines = [headers.map(csvCell).join(',')];
    docs.forEach(d => {
      lines.push([
        d.document_id,
        d.filename || d.original_filename || '',
        getOwnerName(d.owner_type, d.owner_id, usersData, businessesData),
        docuTypeName(d.docu_type_id),
        d.party_name || '',
        d.invoice_ref || '',
        d.date_of_document || '',
        d.total_amount != null ? d.total_amount : '',
        d.currency || '',
        d.status || '',
        d.upload_date || '',
      ].map(csvCell).join(','));
    });
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${folder.folder_name.replace(/[^a-zA-Z0-9_-]/g, '_')}_documents.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccessMsg(`${docs.length} document(s) exported to CSV.`);
  }, [documents, getFolderAndDescendantIds, docuTypes, usersData, businessesData]);

  // --- Push to Google Drive (integration pending) ---
  const handlePushToDrive = useCallback(() => {
    setFolderMenuOpenId(null);
    setSuccessMsg('Google Drive sync is coming soon — your files stay safe here in the meantime.');
  }, []);

  // =============================================
  // Filtered documents
  // =============================================
  // ── Duplicate detection ──────────────────────────────────────────────────
  // Cluster this account owner's documents (within the active pillar + owner
  // scope) into duplicate groups, independent of the status/folder/search filters
  // so a doc's "duplicate" flag is stable wherever it appears. New uploads carry a
  // stored content_minhash + file_hash (migration 087); legacy docs fall back to a
  // field-based signature computed here so they still participate.
  // Local (fallback) clustering — used in localStorage / offline mode, or if the
  // server endpoint is unreachable. Scoped to the active pillar + owner scope.
  const computeLocalDupInfo = useCallback((): Map<number, DupGroupInfo> => {
    let docs = documents.filter(d => d.is_deleted !== 1 && d.is_active === 1);
    // eslint-disable-next-line eqeqeq
    docs = docs.filter(d => d.account_owner_id == accountOwnerId);
    if (viewMode === 'clients') {
      docs = docs.filter(d => ownerEntities.some(e => docMatchesEntityX(d, e)));
    } else {
      docs = docs.filter(d => d.owner_type !== 'client_personal' && d.owner_type !== 'client_business');
    }
    if (scopedOwnerKeys.length > 0) {
      docs = docs.filter(d => docMatchesOwnerKeysX(d, scopedOwnerKeys));
    }
    // Guard against an O(n²) blow-up on huge accounts.
    if (docs.length < 2 || docs.length > 1500) return new Map();

    const candidates: DupCandidate[] = docs.map(d => {
      let sig = parseMinHash(d.content_minhash);
      if (!sig) {
        // Legacy/no-text doc → derive a signature from the stored Filing fields.
        const toks = buildContentTokens({
          partyName: d.party_name,
          totalAmount: d.total_amount,
          dateOfDocument: d.date_of_document,
          invoiceRef: d.invoice_ref,
          currency: d.currency,
        });
        sig = toks.length ? computeMinHash(toks) : null;
      }
      const draft = filingDraftsRef.current.get(d.document_id);
      return {
        id: d.document_id,
        createdAt: d.created_at || d.upload_date,
        fileHash: d.file_hash || null,
        sig,
        date: (draft && draft.date) || d.date_of_document,
        amount: (draft && draft.amount) || d.total_amount,
        ref: (draft && draft.invoiceRef) || d.invoice_ref,
      };
    });
    return computeDuplicateGroups(candidates, DUP_THRESHOLD) as Map<number, DupGroupInfo>;
  }, [documents, accountOwnerId, viewMode, ownerEntities, scopedOwnerKeys, docMatchesEntityX, docMatchesOwnerKeysX]);

  // Duplicate groups — prefer the SERVER (it clusters from the persisted fingerprint
  // columns only, never reading the document blobs), falling back to local
  // computation when there's no API/session. Recomputes when the doc set changes.
  const [dupInfoRaw, setDupInfo] = useState<Map<number, DupGroupInfo>>(new Map());
  const [dupInfoReady, setDupInfoReady] = useState(false);   // the groups have been worked out at least once
  const [dupRefreshNonce, setDupRefreshNonce] = useState(0);   // bump to re-ask the server (after "Not a duplicate")
  // "Not a duplicate": the user confirms the open document differs from the rest of its group
  // (asked once more before it is saved — dms_document_not_duplicate, migration 136).
  const [notDupConfirmFor, setNotDupConfirmFor] = useState<number | null>(null);
  const [notDupSaving, setNotDupSaving] = useState(false);
  useEffect(() => { setNotDupConfirmFor(null); }, [selectedDocId]);
  const confirmNotDuplicate = useCallback(async (docId: number, groupId: number | string) => {
    const others: number[] = [];
    dupInfoRaw.forEach((info, id) => { if (info.groupId === groupId && id !== docId) others.push(id); });
    if (others.length === 0) { setNotDupConfirmFor(null); return; }
    setNotDupSaving(true);
    const ok = await markNotDuplicate(docId, others);
    setNotDupSaving(false);
    if (!ok) {
      setSuccessMsg('');
      setErrorMsg('Could not save "Not a duplicate" — check you are online and try again.');
      return;
    }
    setNotDupConfirmFor(null);
    setErrorMsg('');
    setSuccessMsg('Marked as not a duplicate — these documents will not be flagged again.');
    setDupRefreshNonce(n => n + 1);
  }, [dupInfoRaw]);
  // The members of each duplicate group (by group id), from the raw groups.
  const dupGroupMembers = useMemo(() => {
    const groups = new Map<number | string, number[]>();
    dupInfoRaw.forEach((info, id) => {
      const ids = groups.get(info.groupId);
      if (ids) ids.push(id); else groups.set(info.groupId, [id]);
    });
    return groups;
  }, [dupInfoRaw]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isApiReady()) {
        const serverGroups = await fetchDedupGroups();
        if (!cancelled && serverGroups) { setDupInfo(serverGroups); setDupInfoReady(true); return; }
      }
      if (!cancelled) { setDupInfo(computeLocalDupInfo()); setDupInfoReady(true); }
    })();
    return () => { cancelled = true; };
  }, [computeLocalDupInfo, dupRefreshNonce]);

  // What a document already has SAVED, in the shape of a Filing draft.
  const savedFilingOf = useCallback((d: DocRow): FilingDraft => {
    const live = (r: { is_deleted: number; is_active: number }) => Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0;
    const ownerKey = d.owner_type && d.owner_id ? `${d.owner_type}:${d.owner_id}` : '';
    const ownerKeys = Array.from(new Set([ownerKey,
      ...docOwners.filter(r => r.document_id === d.document_id && live(r)).map(r => `${r.owner_type}:${r.owner_id}`)].filter(Boolean)));
    const folderIds = Array.from(new Set([
      ...(d.dms_folders_id ? [Number(d.dms_folders_id)] : []),
      ...docFolders.filter(r => r.document_id === d.document_id && live(r)).map(r => Number(r.folder_id)),
    ]));
    const str = (v: unknown) => (v == null ? '' : String(v));
    return {
      ownerKeys, folderIds,
      docuTypeId: str(d.docu_type_id), expenseTypeId: str(d.expense_type_id), receiptKind: str(d.receipt_kind),
      partyName: str(d.party_name), invoiceRef: str(d.invoice_ref), date: str(d.date_of_document).slice(0, 10),
      paymentMethod: str(d.payment_method), paymentMethodId: str(d.payment_method_id),
      taxTypeId: str(d.tax_type_id), taxRateId: str(d.tax_rate_id), taxAmount: str(d.tax_amount),
      amount: str(d.total_amount), currency: str(d.currency), purpose: str(d.purpose), tags: str(d.tags),
    };
  }, [docOwners, docFolders]);
  // Documents somebody has started on: an unsaved Draft, already put in a folder, or
  // past "to file" altogether. (An owner alone does not count — it is set at upload.)
  const docsById = useMemo(() => new Map(documents.map(d => [d.document_id, d] as const)), [documents]);
  const dupWorkedOn = useCallback((id: number): 0 | 1 | 2 => {
    const d = docsById.get(id);
    if (!d || Number(d.is_deleted) === 1) return 0;
    if (d.status !== 'Review') return 2;                       // filed / BK done / rejected
    return draftDocIds.has(id) || (savedFilingOf(d).folderIds || []).length > 0 ? 1 : 0;
  }, [docsById, draftDocIds, savedFilingOf]);
  // Which copy is "the original" is, by default, the one uploaded first. But the copy
  // that has been worked on is the one to keep — so THAT one is shown as the original and
  // the untouched one as the duplicate: a filed copy first, else one with a Draft or a
  // folder. Several worked-on copies: the first-uploaded of them (what they hold is
  // combined below).
  const dupInfo = useMemo(() => {
    if (dupInfoRaw.size === 0) return dupInfoRaw;
    let shown: Map<number, DupGroupInfo> | null = null;
    dupGroupMembers.forEach(ids => {
      const current = ids.find(id => dupInfoRaw.get(id)!.isOriginal);
      const next = ids.slice().sort((a, b) =>
        dupWorkedOn(b) - dupWorkedOn(a) || Number(b === current) - Number(a === current) || a - b)[0];
      if (next === current || dupWorkedOn(next) === 0) return;
      const nextPct = dupInfoRaw.get(next)!.matchPct;
      if (!shown) shown = new Map(dupInfoRaw);
      ids.forEach(id => {
        const info = dupInfoRaw.get(id)!;
        shown!.set(id, {
          ...info,
          originalId: next,
          isOriginal: id === next,
          // match % is against the original: the old original now carries the new one's figure
          matchPct: id === next ? 100 : id === current ? nextPct : info.matchPct,
        });
      });
    });
    return shown || dupInfoRaw;
  }, [dupInfoRaw, dupGroupMembers, dupWorkedOn]);
  // Two (or more) copies of the same document that have BOTH been worked on: what they
  // hold is combined, so whichever copy is opened or filed carries everything.
  //   * A field the user changed in a Draft on one copy goes to every copy's draft
  //     (the most recently changed draft wins where two drafts differ).
  //   * A field SAVED on one copy and still blank on another (e.g. the owner) is offered
  //     on the other through its draft. A saved value is never replaced by another
  //     copy's saved value — the same receipt can rightly belong to two owners.
  // Nothing is written to the documents themselves: it all arrives as a Draft the user
  // sees, can change, and can discard. Re-checked when a document is opened.
  useEffect(() => {
    if (!draftsLoaded) return;
    const drafts = filingDraftsRef.current;
    let openChanged = false;
    let idsChanged = false;
    dupGroupMembers.forEach(ids => {
      const members = ids.map(id => docsById.get(id)).filter((d): d is DocRow => !!d && Number(d.is_deleted) !== 1);
      if (members.filter(d => dupWorkedOn(d.document_id) > 0).length < 2) return;
      // What the user changed, newest draft first (without what a draft merely borrowed).
      const edited = members.filter(d => drafts.has(d.document_id))
        .sort((a, b) => (draftTouchedRef.current.get(b.document_id) || 0) - (draftTouchedRef.current.get(a.document_id) || 0))
        .map(d => {
          const { fromCopy, ...own } = drafts.get(d.document_id)!;
          const rest: Record<string, unknown> = { ...own };
          (fromCopy || []).forEach(k => { delete rest[k]; });
          return rest as FilingDraft;
        });
      const changed = combineFilingDrafts(edited) as Record<string, unknown>;
      const saved = new Map(members.map(d => [d.document_id, savedFilingOf(d) as Record<string, unknown>] as const));
      // Saved values are taken from the copy that got furthest.
      const bySavedRank = members.slice().sort((a, b) => dupWorkedOn(b.document_id) - dupWorkedOn(a.document_id) || a.document_id - b.document_id);
      members.filter(d => d.status === 'Review').forEach(d => {
        const id = d.document_id;
        const own = saved.get(id)!;
        const hadDraft = drafts.has(id);
        const target: Record<string, unknown> = {};
        const fromCopy: string[] = [];
        DRAFT_SHARED_KEYS.forEach(key => {
          if (draftValueFilled(changed[key])) { target[key] = changed[key]; return; }
          if (draftValueFilled(own[key])) return;
          const giver = bySavedRank.find(o => o.document_id !== id && draftValueFilled(saved.get(o.document_id)![key]));
          if (giver) { target[key] = saved.get(giver.document_id)![key]; fromCopy.push(key); }
        });
        // A copy with no draft of its own only gets one if it would learn something new.
        if (!hadDraft) DRAFT_SHARED_KEYS.forEach(key => { if (key in target && draftValueSame(target[key], own[key])) delete target[key]; });
        if (Object.keys(target).length === 0) return;
        if (changed.autoFilled) target.autoFilled = true;
        if (fromCopy.length > 0) target.fromCopy = fromCopy.filter(k => k in target);
        if (JSON.stringify(drafts.get(id)) === JSON.stringify(target)) return;
        // A save still waiting for this document would overwrite the combined draft.
        if (draftPendingRef.current?.docId === id) draftPendingRef.current = null;
        drafts.set(id, target as FilingDraft);
        saveDocDraft(id, FILING_DRAFT_FORM, target);
        if (!hadDraft) idsChanged = true;
        if (id === selectedDocId) openChanged = true;
      });
    });
    if (idsChanged) setDraftDocIds(new Set(drafts.keys()));
    // The open document's draft gained values from its copy → lay it over the form again.
    if (openChanged) setDraftResetNonce(n => n + 1);
  }, [dupGroupMembers, draftDocIds, draftsLoaded, selectedDocId, docsById, dupWorkedOn, savedFilingOf]);

  const filteredDocs = useMemo(() => {
    let docs = documents.filter(d => d.is_deleted !== 1 && d.is_active === 1);
    // Filter by account owner (the manager)
    // eslint-disable-next-line eqeqeq
    docs = docs.filter(d => d.account_owner_id == accountOwnerId);

    // Scope to the active pillar: Client's Accounts shows ONLY the picked client's
    // documents; Your Accounts excludes client-owned docs.
    if (viewMode === 'clients') {
      docs = docs.filter(d => ownerEntities.some(e => docMatchesEntityX(d, e)));
    } else {
      docs = docs.filter(d => d.owner_type !== 'client_personal' && d.owner_type !== 'client_business');
    }

    // Filter by selected owner scope (multi-select; non-owner is pinned to self)
    if (scopedOwnerKeys.length > 0) {
      docs = docs.filter(d => docMatchesOwnerKeysX(d, scopedOwnerKeys));
    }

    // Filter by selected Record Room folder (includes subfolders). A doc matches if
    // its primary folder OR any shared owner's folder is in the subtree.
    if (selectedRecordFolderId !== null) {
      const folderIds = getFolderAndDescendantIds(selectedRecordFolderId);
      docs = docs.filter(d => docInFolderSet(d, folderIds));
    }

    // Filter by status tab. The "Duplicates" sort ignores the status tab so the
    // full group (original + every copy) shows together regardless of where each
    // copy is in its workflow.
    const statuses = STATUS_MAP[activeFilter];
    const duplicatesView = sortKey === 'duplicates' || activeFilter === 'duplicates';
    if (statuses.length > 0 && !duplicatesView) {
      docs = docs.filter(d => tabOfDoc(d) === activeFilter);
    }

    // Search — anything about the document (utils/docSearch): name, tags, party and its
    // alias names, ref, amounts, dates, type, folders, owners, notes; plus words printed
    // inside it (textHits, from the server).
    if (searchTerm.trim()) {
      const folderName = new Map(folders.map(f => [Number(f.folder_id), f.folder_name] as [number, string]));
      const ownerName = new Map(ownerEntities.map(e => [`${e.owner_type}:${e.owner_id}`, e.display_name] as [string, string]));
      const typeName = new Map(docuTypes.map(t => [Number(t.docu_type_id), t.docu_type_name] as [number, string]));
      const expenseName = new Map(expenseTypes.map((t: any) => [Number(t.expense_type_id), t.type_name] as [number, string]));
      const partyNames = new Map<string, string[]>();
      partyMasters.forEach(m => {
        const names = [m.name, m.legal_name, m.nickname, ...m.aliases].map(v => String(v || '').trim()).filter(Boolean);
        // the party's tags find its documents too
        const found = [...names, ...parseTags(m.tags)];
        names.forEach(n => partyNames.set(n.toLowerCase(), [...(partyNames.get(n.toLowerCase()) || []), ...found]));
      });
      const folderTags = new Map(folders.map(f => [Number(f.folder_id), parseTags((f as any).tags)] as [number, string[]]));
      // A GL account on the document — its account head or the account it was paid from —
      // is found by its name, number, nickname, alias names and tags.
      const accountWords = new Map(coaAccounts.map(a => [Number(a.coa_id), [
        a.account_name, a.account_number, (a as any).nickname,
        ...parseTags((a as any).alias_names), ...parseTags((a as any).tags),
      ]] as [number, unknown[]]));
      docs = docs.filter(d => {
        if (textHits.has(d.document_id)) return true;
        const party = String(d.party_name || '').trim();
        return docMatchesSearch({
          texts: [
            d.filename, d.original_filename, ...parseTags(d.tags), party, ...(partyNames.get(party.toLowerCase()) || []),
            d.invoice_ref, d.purpose, d.notes, d.payment_method, d.currency, d.receipt_kind, d.status,
            d.docu_type_id != null ? typeName.get(Number(d.docu_type_id)) : '',
            d.expense_type_id != null ? expenseName.get(Number(d.expense_type_id)) : '',
            d.dms_folders_id != null ? folderName.get(Number(d.dms_folders_id)) : '',
            ...(docFoldersByDoc.get(d.document_id) || []).map(r => folderName.get(Number(r.folder_id))),
            ...(d.dms_folders_id != null ? folderTags.get(Number(d.dms_folders_id)) || [] : []),
            ...(docFoldersByDoc.get(d.document_id) || []).flatMap(r => folderTags.get(Number(r.folder_id)) || []),
            ...((d as any).coa_id != null ? accountWords.get(Number((d as any).coa_id)) || [] : []) as string[],
            ...((d as any).payment_coa_id != null ? accountWords.get(Number((d as any).payment_coa_id)) || [] : []) as string[],
            ownerName.get(`${d.owner_type}:${d.owner_id}`),
            ...(docOwnersByDoc.get(d.document_id) || []).map(r => ownerName.get(`${r.owner_type}:${r.owner_id}`)),
          ],
          amounts: [d.total_amount, d.tax_amount],
          dates: [d.date_of_document, d.due_date],
        }, searchTerm);
      });
    }

    // Sort (default: most recent uploads on top)
    const ts = (s?: string | null) => { if (!s) return 0; const t = Date.parse(s); return isNaN(t) ? 0 : t; };
    const nameOf = (d: DocRow) => (d.original_filename || d.filename || '').toLowerCase();
    const typeOf = (d: DocRow) => (docuTypes.find(dt => dt.docu_type_id === d.docu_type_id)?.docu_type_name || '').toLowerCase();
    const amt = (d: DocRow) => (d.total_amount == null ? 0 : Number(d.total_amount));
    const byRecent = (a: DocRow, b: DocRow) => ts(b.created_at) - ts(a.created_at);
    const needsApproval = (d: DocRow) => docNeedsApproval(d);

    // "Duplicates" sort: keep only docs that belong to a duplicate group, then lay
    // out group-by-group — original first, copies after it (highest match first).
    // Groups are ordered by their original's recency.
    if (duplicatesView) {
      const dupDocs = docs.filter(d => dupInfo.has(d.document_id));
      const groupRecency = (originalId: number) => {
        const orig = docs.find(d => d.document_id === originalId);
        return orig ? ts(orig.created_at) : 0;
      };
      return dupDocs.sort((a, b) => {
        const ia = dupInfo.get(a.document_id)!;
        const ib = dupInfo.get(b.document_id)!;
        if (ia.groupId !== ib.groupId) {
          // newer original group first, stable by group id
          const r = groupRecency(ib.groupId as number) - groupRecency(ia.groupId as number);
          return r !== 0 ? r : Number(ia.groupId) - Number(ib.groupId);
        }
        // within a group: original on top, then by match % desc, then recency
        if (ia.isOriginal !== ib.isOriginal) return ia.isOriginal ? -1 : 1;
        return (ib.matchPct - ia.matchPct) || byRecent(a, b);
      });
    }

    const sorted = [...docs];
    switch (sortKey) {
      case 'oldest':       sorted.sort((a, b) => ts(a.created_at) - ts(b.created_at)); break;
      case 'name-asc':     sorted.sort((a, b) => nameOf(a).localeCompare(nameOf(b))); break;
      case 'name-desc':    sorted.sort((a, b) => nameOf(b).localeCompare(nameOf(a))); break;
      case 'docdate-desc': sorted.sort((a, b) => ts(b.date_of_document) - ts(a.date_of_document) || byRecent(a, b)); break;
      case 'docdate-asc':  sorted.sort((a, b) => ts(a.date_of_document) - ts(b.date_of_document) || byRecent(a, b)); break;
      case 'type':         sorted.sort((a, b) => typeOf(a).localeCompare(typeOf(b)) || nameOf(a).localeCompare(nameOf(b))); break;
      case 'party':        sorted.sort((a, b) => (a.party_name || '').toLowerCase().localeCompare((b.party_name || '').toLowerCase()) || byRecent(a, b)); break;
      case 'amount-desc':  sorted.sort((a, b) => amt(b) - amt(a)); break;
      case 'amount-asc':   sorted.sort((a, b) => amt(a) - amt(b)); break;
      case 'modified':     sorted.sort((a, b) => ts(b.updated_at) - ts(a.updated_at)); break;
      case 'status':       sorted.sort((a, b) => (a.status || '').localeCompare(b.status || '') || byRecent(a, b)); break;
      case 'pending-approval':
        sorted.sort((a, b) => Number(needsApproval(b)) - Number(needsApproval(a)) || byRecent(a, b)); break;
      case 'recent':
      default:             sorted.sort(byRecent); break;
    }
    return sorted;
  }, [documents, accountOwnerId, viewMode, ownerEntities, scopedOwnerKeys, docMatchesOwnerKeysX, docMatchesEntityX, docInFolderSet, selectedRecordFolderId, getFolderAndDescendantIds, activeFilter, searchTerm, sortKey, docuTypes, dupInfo, docNeedsApproval, textHits, folders, expenseTypes, partyMasters, docFoldersByDoc, docOwnersByDoc, coaAccounts]);

  // Server search of the words printed inside documents (migration 139).
  useEffect(() => {
    const terms = searchTerms(searchTerm).filter(t => t.length >= 3);
    if (!terms.length || !accountOwnerId || !isApiReady()) { setTextHits(prev => (prev.size ? new Map() : prev)); return; }
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const resp = await apiClient.get<{ matches: { document_id: number; snippet: string }[] }>(
          '/dms/extract-search', { q: searchTerm, account_owner_id: String(accountOwnerId) });
        if (stale) return;
        setTextHits(new Map((resp.data?.matches || []).map(m => [Number(m.document_id), String(m.snippet || '')] as [number, string])));
      } catch { if (!stale) setTextHits(new Map()); }   // offline / older server: the other fields still search
    }, 300);
    return () => { stale = true; clearTimeout(timer); };
  }, [searchTerm, accountOwnerId]);

  // Selected document
  const selectedDoc = useMemo(
    () => documents.find(d => d.document_id === selectedDocId) || null,
    [documents, selectedDocId]
  );

  // Current index for navigation
  const currentIndex = useMemo(
    () => selectedDocId ? filteredDocs.findIndex(d => d.document_id === selectedDocId) : -1,
    [filteredDocs, selectedDocId]
  );

  // Counts per filter
  const scopedDocs = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    let docs = documents.filter(d => d.is_deleted != 1 && d.is_active == 1);
    // eslint-disable-next-line eqeqeq
    docs = docs.filter(d => d.account_owner_id == accountOwnerId);
    // Same pillar scoping as the document list.
    if (viewMode === 'clients') {
      docs = docs.filter(d => ownerEntities.some(e => docMatchesEntityX(d, e)));
    } else {
      docs = docs.filter(d => d.owner_type !== 'client_personal' && d.owner_type !== 'client_business');
    }
    if (scopedOwnerKeys.length > 0) {
      docs = docs.filter(d => docMatchesOwnerKeysX(d, scopedOwnerKeys));
    }
    if (selectedRecordFolderId !== null) {
      const folderIds = getFolderAndDescendantIds(selectedRecordFolderId);
      docs = docs.filter(d => docInFolderSet(d, folderIds));
    }
    return docs;
  }, [documents, accountOwnerId, viewMode, ownerEntities, scopedOwnerKeys, docMatchesOwnerKeysX, docMatchesEntityX, docInFolderSet, selectedRecordFolderId, getFolderAndDescendantIds]);

  const filterCounts = useMemo(() => {
    const docs = scopedDocs;
    return {
      all: docs.length,
      'to-file': docs.filter(d => tabOfDoc(d) === 'to-file').length,
      'date-verified': docs.filter(d => tabOfDoc(d) === 'date-verified').length,
      filed: docs.filter(d => d.status === 'Filed').length,
      rejected: docs.filter(d => d.status === 'Rejected').length,
      'bk-done': docs.filter(d => d.status === 'BK Done').length,
      duplicates: docs.filter(d => dupInfo.has(d.document_id)).length,
    };
  }, [scopedDocs, dupInfo]);

  // When the last "To File" document is PROCESSED (filed / rejected / BK-done) and the
  // queue is now empty, hand the workflow over to the "Rejected" tab. Gated on an
  // action flag (set by the processing handlers) so it fires only on real processing —
  // never on initial load or when owner/folder scope changes leave To File empty.
  const justProcessedRef = useRef(false);
  const selectFirstRejectedRef = useRef(false);
  useEffect(() => {
    if (!justProcessedRef.current) return;
    justProcessedRef.current = false;
    if (filterCounts['to-file'] === 0 && activeFilter === 'to-file') {
      setActiveFilter('rejected');
      selectFirstRejectedRef.current = true;   // pick the first Rejected doc once the list reflows
    }
  }, [filterCounts, activeFilter]);

  // Clicking a list tab (ALL / Date Verified / To File …) opens the FIRST document of that
  // list — or nothing when the list is empty — once the list has switched over.
  const selectFirstOnTabRef = useRef<FilterTab | null>(null);
  const [tabClickNonce, setTabClickNonce] = useState(0);   // re-clicking the open tab re-selects too
  useEffect(() => {
    const tab = selectFirstOnTabRef.current;
    if (!tab || tab !== activeFilter) return;
    selectFirstOnTabRef.current = null;
    setSelectedDocId(filteredDocs.length > 0 ? filteredDocs[0].document_id : null);
  }, [filteredDocs, activeFilter, tabClickNonce]);

  // After the To File → Rejected hand-off, auto-select the first Rejected document
  // (runs once filteredDocs has switched to the Rejected list).
  useEffect(() => {
    if (!selectFirstRejectedRef.current || activeFilter !== 'rejected') return;
    selectFirstRejectedRef.current = false;
    setSelectedDocId(filteredDocs.length > 0 ? filteredDocs[0].document_id : null);
  }, [filteredDocs, activeFilter]);

  // The last duplicate was dealt with while the Duplicates tab was open: there is nothing
  // left to show there, so go back to the To File queue (keeping the open document if it
  // is in that queue, else opening its first one). Only on the count DROPPING to zero —
  // opening the Duplicates tab when it is already empty stays put.
  const prevDupCountRef = useRef(0);
  const leftDuplicatesRef = useRef(false);
  useEffect(() => {
    const count = filterCounts.duplicates;
    if (activeFilter === 'duplicates' && count === 0 && prevDupCountRef.current > 0) {
      leftDuplicatesRef.current = true;
      setActiveFilter('to-file');
    }
    prevDupCountRef.current = count;
  }, [filterCounts.duplicates, activeFilter]);
  useEffect(() => {
    if (!leftDuplicatesRef.current || activeFilter !== 'to-file') return;
    leftDuplicatesRef.current = false;
    if (!filteredDocs.some(d => d.document_id === selectedDocId)) {
      setSelectedDocId(filteredDocs.length > 0 ? filteredDocs[0].document_id : null);
    }
  }, [filteredDocs, activeFilter, selectedDocId]);

  // Persist a document rename to PostgreSQL (filename column) and reflect it in the
  // in-memory list immediately. Used by the Filing tab's "Rename document" field —
  // saved in real time (debounced while typing + on blur), no Submit needed.
  const renameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistRename = useCallback(async (docId: number, rawName: string) => {
    const name = rawName.trim();
    if (!name) return;                                  // ignore empty — keep last good name
    const current = documents.find(d => d.document_id === docId);
    if (current && current.filename === name) return;   // nothing changed
    try {
      const now = NOW_ISO();
      await saveRow('dms_documents', docId, { filename: name, updated_at: now }, 'document_id');
      // saveRow (API mode) doesn't touch the local cache — update state directly so the
      // doc list / viewer title reflect the new name without a full reload.
      setDocuments(prev => prev.map(d => d.document_id === docId ? { ...d, filename: name, updated_at: now } : d));
    } catch {
      // best-effort; a real failure leaves the field showing the typed value (the
      // next blur/submit retries). Renaming never blocks the user.
    }
  }, [documents]);

  // Fetch file_base64 on-demand for a single document (not held in state to keep
  // memory lean). Fetch ONLY that row — pulling the whole dms_documents table just to
  // read one doc's bytes returns multiple MB (every doc's base64), blows the
  // localStorage cache quota, and was leaving the viewer blank. Single-row API first,
  // then the local cache as a fallback.
  const getDocBase64 = useCallback(async (docId: number): Promise<string | null> => {
    let row: any = null;
    // Document BYTES live ONLY on the server (the PG row's file_path → blob store);
    // the localStorage cache never holds them (blobs exceed the quota). So ALWAYS
    // try the API for a fresh row — do NOT gate on isApiReady(), which can be
    // transiently false right after a backend blip/restart and would otherwise leave
    // us with a cached row that has no file_path/bytes → a permanent blank preview.
    // Only fall back to the cached row if the API is genuinely unreachable.
    try { row = await fetchTableRow('dms_documents', docId); } catch { /* fall through to local cache */ }
    if (!row) {
      const all = getTableData('dms_documents');
      row = all.find((d: any) => d.document_id === docId) || null;
    }
    // Prefer the blob store (file_path) and fall back to inline base64; legacy
    // rows migrate into the blob store on first read.
    return resolveDocBase64(row);
  }, []);

  // Load PDF bytes when selecting a document
  useEffect(() => {
    if (!selectedDoc) {
      setPdfBytes(null);
      return;
    }
    let cancelled = false;
    setPdfLoading(true);
    getDocBase64(selectedDoc.document_id)
      .then(base64 => {
        if (cancelled || !base64) {
          if (!cancelled) setPdfBytes(null);
          return;
        }
        return decompressBase64ToBytes(base64).then(bytes => {
          if (!cancelled) setPdfBytes(bytes);
        });
      })
      .catch(() => { if (!cancelled) setPdfBytes(null); })
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => { cancelled = true; };
    // Only a different document or a new file reloads the viewer — not a row edit (ticks, rename …).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc?.document_id, selectedDoc?.file_path, getDocBase64]);

  // Pre-fill right panel forms when selecting a document
  useEffect(() => {
    // Leaving a document: send its pending draft now, and don't let the field
    // changes below be mistaken for user edits.
    flushFilingDraft();
    draftSeqRef.current = getEditSeq();
    if (!selectedDoc) {
      prefillDocIdRef.current = null;
      // Nothing selected (e.g. the last doc was just deleted) — blank both forms
      // so they don't keep showing the previous document's data.
      setFilingOwnerKeys([]);
      setFilingDocuTypeId('');
      setFilingNatureId('');
      setFilingNatureAuto(false);
      setFilingNatureHint('');
      setFilingPartyName('');
      setFilingInvoiceRef('');
      setFilingDate('');
      filingDateTypedRef.current = false;
      setFilingHandwrittenDate('');
      setFilingPrintedDate('');
      setFilingDateChoice('');
      setFilingUndated(false);
      setFilingPaymentMethod('');
      setFilingAmount('');
      setFilingCurrency('');
      setFilingPurpose('');
      setFilingTags('');
      setFilingFilename('');
      setFilingFolderIds([]);
      setFilingPaymentMethodId('');
      setFilingPaymentCoaId('');
      setFilingPaymentCoaWhy(null);
      setFilingTaxTypeId('');
      setFilingTaxRateId('');
      setFilingTaxAmount('');
      setFilingDateFormatId(null);
      setFilingDateLayoutFrom('');
      setFilingRawDate('');
      setFilingGlobalBusiId(null);
      setFilingDateAmbiguous(false);
      setFilingDateCandidates([]);
      setFutureDateConfirmed('');
      filingBusinessFactsRef.current = null;
      setBkOwnerKey('');
      setBkDocuTypeId('');
      setBkNatureId('');
      setBkCoaId(''); setBkCoaAuto(false);
      setBkMemo(''); setBkMemoAuto(false);
      setBkPartyName('');
      setBkInvoiceRef('');
      setBkDate('');
      setBkDueDate('');
      setBkAmount('');
      setBkCurrency('');
      setBkTaxRate('');
      setExtractedText('');
      setAutoFillApplied(null);
      setErrorMsg('');
      setFormDirty(false);
      return;
    }
    if (skipPrefillForRef.current === selectedDoc.document_id) { skipPrefillForRef.current = null; return; }
    skipPrefillForRef.current = null;
    // Pre-fill filing form from saved data first
    const ownerKey = selectedDoc.owner_type && selectedDoc.owner_id
      ? `${selectedDoc.owner_type}:${selectedDoc.owner_id}`
      : '';
    // Owners (multi): primary on dms_documents + every active dms_document_owners row.
    const extraOwnerKeys = docOwners
      .filter(r => r.document_id === selectedDoc.document_id && Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0)
      .map(r => `${r.owner_type}:${r.owner_id}`);
    setFilingOwnerKeys(Array.from(new Set([ownerKey, ...extraOwnerKeys].filter(Boolean))));
    setFilingDocuTypeId(selectedDoc.docu_type_id ? String(selectedDoc.docu_type_id) : '');
    setFilingNatureId(selectedDoc.nature_id ? String(selectedDoc.nature_id) : '');
    setFilingNatureAuto(false);
    setFilingNatureHint('');
    setFilingExpenseTypeId(selectedDoc.expense_type_id ? String(selectedDoc.expense_type_id) : '');
    setFilingReceiptKind(selectedDoc.receipt_kind || '');
    setFilingExpenseAuto(false);
    setFilingPartyName(selectedDoc.party_name || '');
    setFilingPartyMisread('');
    setFilingInvoiceRef(selectedDoc.invoice_ref || '');
    setFilingDate(selectedDoc.date_of_document || '');
    filingDateTypedRef.current = false;
    setFilingHandwrittenDate(String(selectedDoc.handwritten_date || '').slice(0, 10));
    setFilingPrintedDate(String(selectedDoc.printed_date || '').slice(0, 10));
    setFilingPrintedFrom(selectedDoc.date_source === 'filename' ? 'filename' : 'text');
    setFilingDateChoice('');
    setFilingUndated(Number(selectedDoc.date_undated) === 1);
    setFilingDatesDocId(selectedDoc.document_id);
    setFilingPaymentMethod(selectedDoc.payment_method || '');
    setFilingAmount(selectedDoc.total_amount != null ? String(selectedDoc.total_amount) : '');
    // Not filed yet: the stored currency is only what the reader guessed at upload (it was
    // often wrong) — start blank so the owner's base currency fills it. Filed: keep it.
    const notFiled = selectedDoc.status === 'Review';
    setFilingCurrency(notFiled ? '' : (selectedDoc.currency || ''));
    setFilingPurpose(selectedDoc.purpose || '');
    setFilingTags(selectedDoc.tags || '');
    setFilingFilename(selectedDoc.filename || '');
    // Folders (multi): primary on dms_documents + every active dms_document_folders row
    // + (back-compat) any folder carried on the doc's owner rows.
    const primaryFolder = selectedDoc.dms_folders_id ? Number(selectedDoc.dms_folders_id) : null;
    const extraFolderIds = docFolders
      .filter(r => r.document_id === selectedDoc.document_id && Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0)
      .map(r => Number(r.folder_id));
    const legacyOwnerFolderIds = docOwners
      .filter(r => r.document_id === selectedDoc.document_id && Number(r.is_deleted) !== 1 && Number(r.is_active) !== 0 && r.folder_id != null)
      .map(r => Number(r.folder_id));
    setFilingFolderIds(Array.from(new Set([
      ...(primaryFolder != null ? [primaryFolder] : []),
      ...extraFolderIds, ...legacyOwnerFolderIds,
    ])));
    // Part 2 saved values
    setFilingPaymentMethodId(selectedDoc.payment_method_id ? String(selectedDoc.payment_method_id) : '');
    setFilingPaymentCoaId(selectedDoc.payment_coa_id ? String(selectedDoc.payment_coa_id) : '');
    setFilingPaymentCoaWhy(null);
    setFilingTaxTypeId(selectedDoc.tax_type_id ? String(selectedDoc.tax_type_id) : '');
    setFilingTaxRateId(selectedDoc.tax_rate_id ? String(selectedDoc.tax_rate_id) : '');
    setFilingTaxAmount(selectedDoc.tax_amount != null ? String(selectedDoc.tax_amount) : '');
    setFilingDateFormatId(selectedDoc.date_format_id || null);
    setFilingDateLayoutFrom('');
    setFilingRawDate(selectedDoc.raw_date_string || '');
    setFilingGlobalBusiId(selectedDoc.global_busi_id || null);
    setFilingDateAmbiguous(false);
    setFilingDateCandidates([]);
    setFutureDateConfirmed('');
    filingBusinessFactsRef.current = null;

    // Pre-fill bookkeeping form
    setBkOwnerKey(ownerKey);
    setBkDocuTypeId(selectedDoc.docu_type_id ? String(selectedDoc.docu_type_id) : '');
    setBkNatureId(selectedDoc.nature_id ? String(selectedDoc.nature_id) : '');
    setBkCoaId(selectedDoc.coa_id ? String(selectedDoc.coa_id) : ''); setBkCoaAuto(false);
    setBkMemo(selectedDoc.bk_memo || ''); setBkMemoAuto(false);
    setBkPartyName(selectedDoc.party_name || '');
    setBkInvoiceRef(selectedDoc.invoice_ref || '');
    setBkDate(selectedDoc.date_of_document || '');
    setBkDueDate(selectedDoc.due_date || '');
    setBkAmount(selectedDoc.total_amount != null ? String(selectedDoc.total_amount) : '');
    setBkCurrency(notFiled ? '' : (selectedDoc.currency || ''));
    setBkTaxRate(selectedDoc.tax_rate != null ? String(selectedDoc.tax_rate) : '');

    // Reset extracted text + auto-match cues (the saved values above are not "AI suggested")
    setExtractedText('');
    setAutoFillApplied(null);
    setErrorMsg('');

    // This user's unsaved Filing draft wins over the saved values.
    const draft = filingDraftsRef.current.get(selectedDoc.document_id);
    if (draft) applyFilingDraft(draft);
    if (draft && notFiled && draft.currency && selectedDoc.currency
      && String(draft.currency).toUpperCase() === String(selectedDoc.currency).toUpperCase()) {
      setFilingCurrency('');   // the draft only carried the reader's guess — use the base currency
    }
    if (prefillDocIdRef.current !== selectedDoc.document_id) {
      prefillDocIdRef.current = selectedDoc.document_id;
      // The previous document's edits are safe in its draft, so the unsaved flag
      // now reflects THIS document.
      if (filingVisibleRef.current) setFormDirty(!!draft);
    }
    draftSeqRef.current = getEditSeq();
  }, [selectedDoc, docOwners, docFolders, draftResetNonce, applyFilingDraft, flushFilingDraft]);

  // Read one document: fetch its bytes, pull the text (OCR for a scan), guess the fields.
  // The slow part — done once per document and then remembered (utils/extractCache).
  const runDocExtraction = useCallback(async (doc: { document_id: number; filename?: string; original_filename?: string }) => {
    const base64 = await getDocBase64(doc.document_id);
    if (!base64) return null;
    return localExtractFilingFields(base64, doc.filename || doc.original_filename || '');
  }, [getDocBase64]);

  // A document that has never been looked at for a handwritten date (uploaded before the
  // feature, or with the free engine) is checked once when it is opened: one short AI
  // call, and the answer is kept with the document's stored read (extractCache) so it is
  // never asked again. Skipped when the user chose the free-only engine.
  useEffect(() => {
    if (!selectedDoc || selectedDoc.status === 'Filed') return;
    if (Number(selectedDoc.handwritten_checked) === 1 || isDateSettled(selectedDoc.date_source)) return;
    if (!isApiReady() || !handwritingCheckAllowed()) return;
    const doc = selectedDoc;
    if (handwritingAskedRef.current.has(doc.document_id)) return;
    handwritingAskedRef.current.add(doc.document_id);
    let cancelled = false;
    (async () => {
      const parsed = await loadDocExtract(doc.document_id, () => runDocExtraction(doc)).catch(() => null);
      let handwrittenDate = '';
      if (parsed?.handwrittenChecked) {
        handwrittenDate = parsed.handwrittenDate || '';
      } else {
        const base64 = await getDocBase64(doc.document_id);
        const handwritten = base64 ? await readHandwrittenDate(base64) : null;
        if (!handwritten) { handwritingAskedRef.current.delete(doc.document_id); return; }   // could not be checked — ask again later
        handwrittenDate = handwritten.date;
        amendDocExtract(doc.document_id, { handwrittenChecked: true, handwrittenDate });
      }
      const printed = String(doc.printed_date || parsed?.dateOfDocument || doc.date_of_document || '').slice(0, 10);
      const decided = decideDocDate(printed, handwrittenDate, parsed?.dateSource || 'text');
      setHandwritingResults(prev => new Map(prev).set(doc.document_id, decided.confirmNeeded));
      if (!cancelled) {
        setFilingHandwrittenDate(handwrittenDate);
        if (printed) setFilingPrintedDate(prev => prev || printed);
      }
      // Only a date that needs the user's approval is written to the document itself, so
      // "Pending User Approvals" still lists it tomorrow and on another device. Anything
      // else stays in the stored read: updating the row would mark an untouched upload
      // as changed (the database stamps updated_at on every update).
      if (decided.confirmNeeded && Number(doc.date_confirm_needed) !== 1) {
        try {
          await saveRow('dms_documents', doc.document_id, {
            handwritten_checked: 1,
            handwritten_date: handwrittenDate,
            printed_date: printed || null,
            date_confirm_needed: 1,
            date_source: doc.date_source || decided.source,
          }, 'document_id');
        } catch { /* best-effort: it is found again the next time the document is opened */ }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDoc, getDocBase64, runDocExtraction]);

  // Auto-fill empty fields from the document's auto-read result. The read itself is
  // done once per document (memory → server → extract); reopening a document, or
  // opening one that was pre-read in the background, fills straight away.
  useEffect(() => {
    if (!selectedDoc) return;
    // Don't re-apply if already done for this doc
    if (autoFillApplied === selectedDoc.document_id) return;

    let cancelled = false;
    const docToRead = selectedDoc;
    // "Reading document…" only when there is actual reading to do.
    setExtracting(!hasCachedExtract(docToRead.document_id));

    loadDocExtract(docToRead.document_id, () => runDocExtraction(docToRead)).then(parsedOrNull => {
      if (cancelled || !parsedOrNull) {
        if (!cancelled) setExtracting(false);
        return;
      }

    return Promise.resolve(parsedOrNull)
      .then((parsed) => {
        if (cancelled) return;
        setExtractedText(parsed.rawText || '');

        // Auto-fill ONLY empty fields (don't overwrite saved data)
        // Filing form
        setFilingPartyName(prev => prev || parsed.partyName);
        setFilingInvoiceRef(prev => prev || parsed.invoiceRef);
        autoSetFilingDate(prev => prev || parsed.dateOfDocument);
        setFilingPrintedDate(prev => prev || parsed.dateOfDocument || '');
        if (parsed.dateSource === 'filename') setFilingPrintedFrom('filename');
        setFilingAmount(prev => prev || parsed.totalAmount);
        // Currency is not taken from the read (it guessed GBP / EUR on Canadian receipts):
        // the owner's base currency is the default and other currencies are picked from the list.
        setFilingPaymentMethod(prev => prev || parsed.paymentMethod);
        setFilingPurpose(prev => prev || parsed.purpose);

        // Kind of document from the read (utils/docKind: the reader's label, else the
        // text itself — receipt marks, cheque, e-transfer wording, deposit slip, bill).
        // A document still to be filed that the upload set to "Other Document" (the old
        // reader sent almost everything there) gets a fresh guess too.
        const otherId = docuTypes.find(dt => dt.docu_type_code === 'OTHR')?.docu_type_id;
        const kindOpen = !selectedDoc.docu_type_id
          || (selectedDoc.status === 'Review' && otherId != null && selectedDoc.docu_type_id === otherId);
        if (kindOpen) {
          const dtId = resolveDocuTypeId(parsed.docTypeHint || '', docuTypes, parsed.rawText || '');
          if (dtId != null) {
            const unset = (prev: string) => !prev || (otherId != null && prev === String(otherId));
            setFilingDocuTypeId(prev => (unset(prev) ? String(dtId) : prev));
            setBkDocuTypeId(prev => (unset(prev) ? String(dtId) : prev));
          }
        }
        setFilingNatureHint(parsed.natureHint || '');

        // Bookkeeping form
        setBkPartyName(prev => prev || parsed.partyName);
        setBkInvoiceRef(prev => prev || parsed.invoiceRef);
        setBkDate(prev => prev || parsed.dateOfDocument);
        setBkAmount(prev => prev || parsed.totalAmount);
        setBkCurrency(prev => (parsed.currency && (!prev || prev === autoCurrencyRef.current.value) ? parsed.currency : prev));

        // Conservative owner/folder auto-match (Part 1). Owner is only SUGGESTED when
        // the document itself names an account entity (otherwise left blank — the
        // standing "uploader ≠ owner" rule). A folder match is suggested independently
        // but does NOT drag in an owner from the folder: when the folder is AI-suggested
        // and no owner was named, the submit gate makes the user confirm the owner.
        const matchText = `${parsed.rawText || ''} ${selectedDoc.filename || ''} ${selectedDoc.original_filename || ''}`;
        const ownerSug = !selectedDoc.owner_type
          ? matchOwner(matchText, ownerMatchCandidates)
          : null;
        if (ownerSug) {
          setFilingOwnerKeys(prev => prev.length ? prev : [ownerSug]);   // a saved/just-set owner wins
        }
        if (!selectedDoc.dms_folders_id) {
          const folderSug = matchFolder(matchText, folderMatchCandidates, ownerSug);
          if (folderSug != null) {
            setFilingFolderIds(prev => prev.length ? prev : [folderSug]);   // already filed / set
          }
        }

        // Capture the OTHER party's PUBLIC business facts for the directory (pushed at submit).
        filingBusinessFactsRef.current = {
          address_1: parsed.businessAddress || '',
          website: parsed.businessWebsite || '',
          general_phone: parsed.businessPhone || '',
          general_email: parsed.businessEmail || '',
          category_code: parsed.businessCategory || '',
          city_name: parsed.businessCity || '',
          state_name: parsed.businessState || '',
          postal_code: parsed.businessPostalCode || '',
          general_fax: parsed.businessFax || '',
          general_mobile: parsed.businessMobile || '',
          tax_id: parsed.businessTaxId || '',
          tax_id_label: parsed.businessTaxIdLabel || '',
          tax_id_2: parsed.businessTaxId2 || '',
          tax_id_2_label: parsed.businessTaxId2Label || '',
        };

        // Part 2: payment method, tax, and date disambiguation (empty fields only).
        if (!selectedDoc.payment_method_id) {
          const pmId = matchPaymentMethod(matchText, paymentMethods as any);
          if (pmId) setFilingPaymentMethodId(prev => prev || String(pmId));
        }
        if (!selectedDoc.tax_rate_id) {
          // Resolve the owner's province so same-% rates (e.g. 15% HST in NB/NL/PE)
          // disambiguate to the owner's jurisdiction.
          let ownerStateId: number | null = null;
          {
            const ot = selectedDoc.owner_type;
            let sc = '';
            if (ot === 'business' || ot === 'client_business') {
              // eslint-disable-next-line eqeqeq
              const b = businessesData.find((x: any) => x.business_id == selectedDoc.owner_id);
              sc = String(b?.busi_address_state || '').trim().toUpperCase();
            } else {
              // eslint-disable-next-line eqeqeq
              const u = usersData.find((x: any) => x.user_id == (selectedDoc.owner_id || selectedDoc.user_id));
              sc = String(u?.address_state || '').trim().toUpperCase();
            }
            // eslint-disable-next-line eqeqeq
            const st = sc ? statesData.find((s: any) => String(s.state_code || '').toUpperCase() === sc) : null;
            if (st) ownerStateId = Number(st.state_id);
          }
          // Only rates in effect on the document's date are candidates (effective-dated).
          const docDate = String(selectedDoc.date_of_document || '').slice(0, 10) || new Date().toISOString().slice(0, 10);
          const ratesOnDate = (taxRates as any[]).filter(r => {
            const from = String(r.effective_from || '').slice(0, 10);
            const to = String(r.effective_to || '').slice(0, 10);
            return !(from && from > docDate) && !(to && to < docDate);
          });
          const tax = matchTax({
            text: parsed.rawText || '', types: taxTypes as any, rates: ratesOnDate,
            ratePercent: parsed.taxRatePercent ? Number(parsed.taxRatePercent) : null,
            taxAmount: parsed.taxAmount ? Number(parsed.taxAmount) : null,
            stateId: ownerStateId,
          });
          if (tax.tax_rate_id) setFilingTaxRateId(prev => prev || String(tax.tax_rate_id));
          if (tax.tax_type_id) setFilingTaxTypeId(prev => prev || String(tax.tax_type_id));
          if (tax.tax_amount != null) setFilingTaxAmount(prev => prev || String(tax.tax_amount));
        }
        // Date: interpret the raw printed token (self → party learned format → ambiguous).
        // Also re-read a date that upload auto-fill guessed but nobody has confirmed yet
        // (not filed, no format recorded) — the guess never saw the party's layout.
        const rawDate = parsed.rawDateString || selectedDoc.raw_date_string || '';
        // A date ticked as verified IS confirmed: re-reading it put the printed misread
        // (3301-11-30) back over the 2023-11-30 the user had verified (owner, 2026-10-17).
        const dateUnconfirmed = selectedDoc.status !== 'Filed' && !selectedDoc.date_format_id && !isDateVerified(selectedDoc);
        if (!rawDate && dateUnconfirmed) {
          // No printed token to re-interpret, but the guessed date may still be in the
          // future (e.g. baked in at upload) → give it the same second look.
          const d0 = new Date();
          const today0 = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, '0')}-${String(d0.getDate()).padStart(2, '0')}`;
          const guessed = String(selectedDoc.date_of_document || parsed.dateOfDocument || '').slice(0, 10);
          if (guessed && guessed > today0) {
            const again = recheckFutureDate(guessed, parsed.rawText || '', { formats: dateFormats as any, maxIso: today0 });
            if (again.iso) {
              const fixed = again.iso;
              autoSetFilingDate(prev => (!prev || prev === guessed ? fixed : prev));
              setFilingPrintedDate(fixed);
            } else if (again.suggestions.length) {
              setFilingDateAmbiguous(true);
              setFilingDateCandidates(again.suggestions.map(c => ({ order: c.why === 'year-repair' ? 'year corrected' : c.order, iso: c.iso, formatId: c.formatId })));
            }
          }
        }
        if (rawDate && (!selectedDoc.date_of_document || dateUnconfirmed)) {
          setFilingRawDate(prev => prev || rawDate);
          const now = new Date();
          const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          // Only replace a value this auto-fill put there — never a date the user typed
          // (or one restored from their unsaved draft).
          const autoValues = new Set<string>(['', String(selectedDoc.date_of_document || '').slice(0, 10), parsed.dateOfDocument || '']);
          const applyRead = (partyFormatId: number | null) => {
            const di = interpretDate(rawDate, { formats: dateFormats as any, partyFormatId, maxIso: todayIso });
            if (di.ambiguous) {
              setFilingDateAmbiguous(true);
              setFilingDateCandidates(di.candidates.map(c => ({
                order: c.order, iso: c.iso, formatId: formatFromConfirmedDate(rawDate, c.iso, dateFormats as any)?.formatId ?? findFormatId(dateFormats as any, c.order as any),
              })));
            } else if (di.iso) {
              let iso = di.iso;
              let formatId = di.formatId;
              let offer: { order: string; iso: string; formatId: number | null }[] = [];
              if (iso > todayIso) {
                // Read as later than today → look again: every date in the document, and a
                // misread century (2823 → 2023). One clear answer is used; several are
                // offered; none leaves the future date for the user to confirm or fix.
                const again = recheckFutureDate(iso, parsed.rawText || rawDate, { formats: dateFormats as any, partyFormatId, maxIso: todayIso });
                if (again.iso) { iso = again.iso; formatId = again.formatId; }
                else offer = again.suggestions.map(c => ({ order: c.why === 'year-repair' ? 'year corrected' : c.order, iso: c.iso, formatId: c.formatId }));
              }
              const finalIso = iso;
              autoSetFilingDate(prev => (autoValues.has(prev) ? finalIso : prev));   // principled read overrides the naive parser guess
              setFilingPrintedDate(finalIso);
              setFilingPrintedFrom('text');
              autoValues.add(finalIso);
              if (formatId) setFilingDateFormatId(formatId);
              setFilingDateAmbiguous(offer.length > 0);
              setFilingDateCandidates(offer);
            }
            return di;
          };
          // The layout Masters holds for this vendor's kind of document comes first.
          const partyForDate = String(selectedDoc.party_name || parsed.partyName || '').trim();
          const fromMasters = selectedDoc.date_format_id ? null
            : masterDateFormat(partyForDate, `${parsed.rawText || ''} ${parsed.docTypeHint || ''} ${selectedDoc.filename || ''} ${selectedDoc.original_filename || ''}`, rawDate);
          const first = applyRead(selectedDoc.date_format_id || fromMasters?.formatId || null);
          setFilingDateLayoutFrom(fromMasters && first.source === 'party' ? fromMasters.label : '');
          // In doubt → ask the vendor master: has this party's date layout been learned
          // from an earlier receipt? (site_mstr_global_busi_db / site_busi_date_format)
          if (partyForDate && first.source !== 'self') {
            apiClient.get<{ candidates: DirectoryCandidate[] }>('/directory/match', { name: partyForDate })
              .then(resp => {
                if (cancelled) return;
                const best = pickBestCandidate((resp.data && resp.data.candidates) || [], partyForDate);
                if (!best) return;
                setFilingGlobalBusiId(prev => prev || Number(best.busi_id));
                if (best.date_format_id && !fromMasters) applyRead(Number(best.date_format_id));   // Masters' layout outranks the learned one
              })
              .catch(() => { /* directory is best-effort (offline / logged out) */ });
          }
        }

        // Expense type (migration 097) — suggest for empty field only. Uses the
        // receipt text + party name, biased by the owner's business type.
        // Bookkeeping-only: the field is hidden in pure DMS, so nothing is suggested there.
        if (showBookkeeping && !selectedDoc.expense_type_id) {
          const sug = resolveExpenseSuggestion(`${matchText} ${parsed.partyName || ''}`);
          if (sug) {
            setFilingExpenseTypeId(prev => prev || String(sug.expenseTypeId));
            if (sug.receiptKind) setFilingReceiptKind(prev => prev || sug.receiptKind);
            setFilingExpenseAuto(true);
          }
        }

        setAutoFillApplied(selectedDoc.document_id);
        setExtracting(false);
      })
      .catch(() => {
        if (!cancelled) setExtracting(false);
      });
    }).catch(() => {
      if (!cancelled) setExtracting(false);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc?.document_id, getDocBase64, runDocExtraction]);

  // Background pre-read: once the open document is filled, quietly read the next few
  // in the list so they open instantly. One at a time, and stopped as soon as the user
  // moves on (a read already under way finishes and is still remembered).
  useEffect(() => {
    if (selectedDocId == null || autoFillApplied !== selectedDocId) return;
    const idx = filteredDocs.findIndex(d => d.document_id === selectedDocId);
    if (idx < 0) return;
    const upcoming = filteredDocs.slice(idx + 1, idx + 1 + PRE_READ_AHEAD).filter(d => !hasCachedExtract(d.document_id));
    if (!upcoming.length) return;
    let stop = false;
    (async () => {
      for (const d of upcoming) {
        await new Promise(r => setTimeout(r, 500));     // let the open document settle first
        if (stop) return;
        try { await loadDocExtract(d.document_id, () => runDocExtraction(d)); } catch { /* read it when opened */ }
        if (stop) return;
      }
    })();
    return () => { stop = true; };
  }, [selectedDocId, autoFillApplied, filteredDocs, runDocExtraction]);

  // Show newly uploaded documents without a page refresh. Rows already here are
  // left untouched (they may hold local changes); only unseen ids are appended.
  // Triggers: an upload finishing in this tab or another tab of this browser, and
  // coming back to this window (covers uploads from another browser or device).
  // Deliberately no timer: a background poll would keep the idle session alive.
  useEffect(() => {
    let cancelled = false;
    let busy = false;
    const onUploaded = async () => {
      if (busy) return;
      busy = true;
      try {
        const rows = await loadTable('dms_documents');
        if (cancelled) return;
        setDocuments(prev => {
          const known = new Set(prev.map(d => d.document_id));
          const fresh = rows
            .filter((d: any) => !known.has(d.document_id))
            .map((d: any) => { const { file_base64, ...rest } = d; return rest as DocRow; });
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      } catch { /* the next full load picks them up */ }
      finally { busy = false; }
    };
    const onVisible = () => { if (!document.hidden) onUploaded(); };
    const unsubscribe = subscribeDocsUploaded(onUploaded);
    window.addEventListener('focus', onUploaded);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('focus', onUploaded);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Load this user's Filing drafts once. Edits made before the list arrives win.
  useEffect(() => {
    let cancelled = false;
    listDocDrafts<FilingDraft>(FILING_DRAFT_FORM).then(rows => {
      if (cancelled) return;
      const map = filingDraftsRef.current;
      rows.forEach(r => {
        if (!r.data || map.has(r.document_id)) return;
        map.set(r.document_id, r.data);
        draftTouchedRef.current.set(r.document_id, Date.parse(r.updated_at) || 0);
      });
      setDraftDocIds(new Set(map.keys()));
      setDraftsLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  // The drafts arrived after a document was already open → restore its draft now.
  useEffect(() => {
    if (!draftsLoaded || selectedDocId == null) return;
    const draft = filingDraftsRef.current.get(selectedDocId);
    if (!draft) return;
    applyFilingDraft(draft);
    if (filingVisibleRef.current) setFormDirty(true);
    draftSeqRef.current = getEditSeq();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftsLoaded]);

  // Auto-fill just ran: the draft's values still win over what it suggested.
  useEffect(() => {
    if (autoFillApplied == null || autoFillApplied !== selectedDocId) return;
    const draft = filingDraftsRef.current.get(autoFillApplied);
    if (draft) applyFilingDraft(draft, !draft.autoFilled);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillApplied]);

  // Autosave the Filing form as a draft after each USER edit (the edit counter
  // moves only on setFormDirty(true), so pre-fill / auto-fill changes don't count).
  useEffect(() => {
    const seq = getEditSeq();
    const data: FilingDraft = {
      ownerKeys: filingOwnerKeys,
      folderIds: filingFolderIds,
      docuTypeId: filingDocuTypeId,
      natureId: filingNatureId,
      expenseTypeId: filingExpenseTypeId,
      receiptKind: filingReceiptKind,
      partyName: filingPartyName,
      invoiceRef: filingInvoiceRef,
      date: filingDate,
      paymentMethod: filingPaymentMethod,
      paymentMethodId: filingPaymentMethodId,
      paymentCoaId: filingPaymentCoaId,
      taxTypeId: filingTaxTypeId,
      taxRateId: filingTaxRateId,
      taxAmount: filingTaxAmount,
      dateFormatId: filingDateFormatId,
      rawDate: filingRawDate,
      globalBusiId: filingGlobalBusiId,
      amount: filingAmount,
      currency: filingCurrency,
      purpose: filingPurpose,
      tags: filingTags,
      autoFilled: autoFillApplied === selectedDocId,
    };
    // What the form holds, minus bookkeeping about the draft itself.
    const { autoFilled: _af, ...fields } = data;
    const snap = JSON.stringify(fields);
    const prevSnap = draftLastSnapRef.current;
    draftLastSnapRef.current = snap;

    if (seq === draftSeqRef.current) {
      // Not a user edit (pre-fill from the saved document, auto-fill, discard): this is
      // what the form looks like with NOTHING changed by the user — the baseline a later
      // edit is compared against. Unknown while a restored draft is laid over the form.
      if (selectedDocId == null) { draftBaselineRef.current = null; return; }
      const hasDraft = filingDraftsRef.current.has(selectedDocId);
      const base = draftBaselineRef.current;
      if (!hasDraft && !isFormDirty()) {
        draftBaselineRef.current = { docId: selectedDocId, snap };
      } else if (base && base.docId === selectedDocId && prevSnap && prevSnap !== snap) {
        // Auto-fill landed after the user had started editing: fold just the fields it
        // changed into the baseline, leaving the user's own edits as differences.
        try {
          const before = JSON.parse(prevSnap), now = JSON.parse(snap), b = JSON.parse(base.snap);
          for (const k of Object.keys(now)) if (JSON.stringify(before[k]) !== JSON.stringify(now[k])) b[k] = now[k];
          draftBaselineRef.current = { docId: selectedDocId, snap: JSON.stringify(b) };
        } catch { /* keep the old baseline */ }
      } else if (!base || base.docId !== selectedDocId) {
        draftBaselineRef.current = null;
      }
      return;
    }
    draftSeqRef.current = seq;
    if (selectedDocId == null || !filingVisibleRef.current || !isFormDirty()) return;
    const docId = selectedDocId;
    // Edited and put back (e.g. the same date typed over itself): nothing actually
    // differs from the untouched form, so there is no draft to keep and nothing unsaved.
    const base = draftBaselineRef.current;
    if (base && base.docId === docId && base.snap === snap) {
      dropFilingDraft(docId);
      setFormDirty(false);
      return;
    }
    filingDraftsRef.current.set(docId, data);
    draftTouchedRef.current.set(docId, Date.now());
    setDraftDocIds(prev => (prev.has(docId) ? prev : new Set(prev).add(docId)));
    draftPendingRef.current = { docId, data };
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => flushFilingDraft(), FILING_DRAFT_DEBOUNCE_MS);
  }, [selectedDocId, filingOwnerKeys, filingFolderIds, filingDocuTypeId, filingNatureId, filingExpenseTypeId, filingReceiptKind,
      filingPartyName, filingInvoiceRef, filingDate, filingPaymentMethod, filingPaymentMethodId, filingPaymentCoaId, filingTaxTypeId,
      filingTaxRateId, filingTaxAmount, filingDateFormatId, filingRawDate, filingGlobalBusiId, filingAmount,
      filingCurrency, filingPurpose, filingTags, autoFillApplied, flushFilingDraft, dropFilingDraft]);

  // Don't lose the last keystrokes: send the pending draft when the tab closes
  // or this screen unmounts.
  useEffect(() => {
    const onHide = () => flushFilingDraft(true);
    window.addEventListener('pagehide', onHide);
    return () => { window.removeEventListener('pagehide', onHide); flushFilingDraft(); };
  }, [flushFilingDraft]);

  // =============================================
  // Navigation
  // =============================================
  const navigateDoc = useCallback((direction: -1 | 1) => {
    // Back on the document that left the list: either way leads back to the one opened in its place.
    if (currentIndex < 0 && leftDoc && leftDoc.nextId != null
      && filteredDocs.some(d => d.document_id === leftDoc.nextId)) {
      setSelectedDocId(leftDoc.nextId);
      return;
    }
    const newIdx = currentIndex + direction;
    if (newIdx >= 0 && newIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[newIdx].document_id);
    }
  }, [currentIndex, filteredDocs, leftDoc]);

  // Keyboard: the document LIST pane. ↑/↓ move the selection (= scroll through the
  // list); Tab hands control to the middle document viewer.
  // Click a document row to TOGGLE its selection: select it if it isn't selected,
  // unselect it if it is. A double-click collapses into a SINGLE toggle — the rapid
  // second click (and the dblclick) within the window is ignored — so single- and
  // double-click both land on the same state (select when off, unselect when on).
  const docClickGuardRef = useRef<{ id: number; t: number }>({ id: -1, t: 0 });
  const toggleSelectDoc = useCallback((id: number) => {
    const now = Date.now();
    const g = docClickGuardRef.current;
    if (g.id === id && now - g.t < 350) { g.t = now; return; }  // 2nd click of a dbl-click → ignore
    docClickGuardRef.current = { id, t: now };
    setSelectedDocId(prev => (prev === id ? null : id));
  }, []);

  const handleListKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); navigateDoc(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); navigateDoc(-1); }
    else if (e.key === 'Enter') {
      // Enter "accepts" the list: collapse it to give the viewer + Filing form room,
      // then move focus to the viewer pane so a following Tab continues into the
      // Filing fields (Enter accepts, Tab advances — consistent with every field).
      // setTimeout(0) rather than rAF: it runs AFTER React commits the collapse (which
      // unmounts the focused list and resets focus to <body>), so the focus sticks.
      e.preventDefault();
      setShowDocList(false);
      setTimeout(() => centerPaneRef.current?.focus(), 0);
    }
    else if (e.key === 'Tab' && !e.shiftKey && centerPaneRef.current) {
      e.preventDefault();
      centerPaneRef.current.focus();
    }
  }, [navigateDoc]);

  // Keyboard: the middle VIEWER pane. ←/→ step documents; ↑/↓ + PgUp/PgDn (+ Home/End)
  // scroll the open document.
  const handleViewerKeyDown = useCallback((e: React.KeyboardEvent) => {
    const scroller = centerPaneRef.current?.querySelector('.pcv-pages') as HTMLElement | null;
    // In the viewer's "Scroll" (actual-size) mode, ←/→ pan the page horizontally;
    // in every other mode they step documents (page fits, no horizontal scroll).
    const panHorizontally = (typeof localStorage !== 'undefined' && localStorage.getItem('akt_pdf_view_mode') === 'scroll');
    switch (e.key) {
      case 'ArrowRight':
        if (panHorizontally && scroller) { e.preventDefault(); scroller.scrollBy({ left: 120 }); }
        else { e.preventDefault(); navigateDoc(1); }
        break;
      case 'ArrowLeft':
        if (panHorizontally && scroller) { e.preventDefault(); scroller.scrollBy({ left: -120 }); }
        else { e.preventDefault(); navigateDoc(-1); }
        break;
      case 'ArrowDown':  if (scroller) { e.preventDefault(); scroller.scrollBy({ top: 90 }); } break;
      case 'ArrowUp':    if (scroller) { e.preventDefault(); scroller.scrollBy({ top: -90 }); } break;
      case 'PageDown':   if (scroller) { e.preventDefault(); scroller.scrollBy({ top: scroller.clientHeight * 0.9 }); } break;
      case 'PageUp':     if (scroller) { e.preventDefault(); scroller.scrollBy({ top: -scroller.clientHeight * 0.9 }); } break;
      case 'Home':       if (scroller) { e.preventDefault(); scroller.scrollTo({ top: 0 }); } break;
      case 'End':        if (scroller) { e.preventDefault(); scroller.scrollTo({ top: scroller.scrollHeight }); } break;
      default: break;
    }
  }, [navigateDoc]);

  // Keep the keyboard-selected row visible in the list.
  useEffect(() => {
    if (!selectedDocId || !docListRef.current) return;
    const el = docListRef.current.querySelector(`[data-doc-id="${selectedDocId}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedDocId]);

  // A document opened from another screen must also be where the LIST is: showing it in
  // the viewer while the list sits elsewhere (or does not contain it at all) leaves the
  // arrows and the highlight on something else. So whatever is hiding it is lifted —
  // first the search, folder, owner and status-tab filters, then the "Duplicates" sort
  // (which lists only documents that have copies) — and the row is scrolled to and the
  // list focused. Runs until the row is on screen, or gives up after both passes.
  useEffect(() => {
    const nav = navDocRef.current;
    if (!nav || !dataLoaded) return;
    if (selectedDocId !== nav.id) { navDocRef.current = null; return; }   // the user moved on
    const doc = documents.find(d => d.document_id === nav.id);
    if (!doc) { navDocRef.current = null; return; }
    if (filteredDocs.some(d => d.document_id === nav.id)) {
      navDocRef.current = null;
      const row = docListRef.current?.querySelector(`[data-doc-id="${nav.id}"]`) as HTMLElement | null;
      row?.scrollIntoView({ block: 'center' });
      docListRef.current?.focus({ preventScroll: true });
      return;
    }
    if (nav.pass === 0) {
      nav.pass = 1;
      const tab = tabOfDoc(doc);
      const filtered = searchTerm !== '' || selectedRecordFolderId !== null
        || selectedDocOwnerIds.length > 0 || activeFilter !== tab;
      if (filtered) {
        setSearchTerm('');
        setSelectedRecordFolderId(null);
        setSelectedDocOwnerIds([]);
        setActiveFilter(tab);
        return;                    // re-runs once the list has reflowed
      }
    }
    if (nav.pass === 1 && sortKey === 'duplicates') {
      nav.pass = 2;
      setSortKey('recent');
      return;
    }
    navDocRef.current = null;      // not this account's / this pillar's document
  }, [dataLoaded, documents, filteredDocs, selectedDocId, sortKey, searchTerm, selectedRecordFolderId, selectedDocOwnerIds, activeFilter]);

  // On launch, move control to the first document in the list (the To File queue by
  // default) so the viewer + Filing form are populated and the arrows work right away.
  // One-shot; respects a doc opened via navigation (Upload/Reports bridge).
  const didInitialSelectRef = useRef(false);
  useEffect(() => {
    if (didInitialSelectRef.current || !dataLoaded) return;
    if (selectedDocId) { didInitialSelectRef.current = true; return; }  // honor a navigated doc
    if (filteredDocs.length === 0) return;                              // wait for the list to populate
    didInitialSelectRef.current = true;
    const restored = refreshDocId != null && filteredDocs.some(d => d.document_id === refreshDocId);
    setSelectedDocId(restored ? refreshDocId : filteredDocs[0].document_id);
    docListRef.current?.focus();
  }, [dataLoaded, filteredDocs, selectedDocId, refreshDocId]);

  // Download current PDF
  const handleDownloadFile = useCallback(() => {
    if (!pdfBytes || !selectedDoc) return;
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedDoc.filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [pdfBytes, selectedDoc]);

  // Open the document in a new browser tab — PREVIEW, not download. We open our OWN
  // in-app viewer route (/doc-view?doc=<id>) which renders the PDF to <canvas> via
  // pdf.js (PdfCanvasViewer). That always shows inline regardless of Chrome's
  // "Download PDFs instead of opening" setting (which would force a Save-As if we
  // handed Chrome a real .pdf). Each click opens its own tab.
  const handleOpenInNewTab = useCallback(() => {
    if (!selectedDoc) return;
    window.open(`/doc-view?doc=${selectedDoc.document_id}`, '_blank');
  }, [selectedDoc]);

  // --- Per-document quick actions (selected doc) ---
  // ★ Permanent: mark the document so the retention job never auto-deletes it.
  // Accepts an explicit doc (per-row button) or falls back to the selected doc.
  const handleTogglePermanent = useCallback(async (docArg?: DocRow) => {
    const doc = docArg || selectedDoc;
    if (!doc) return;
    const next = Number(doc.is_permanent) === 1 ? 0 : 1;
    // saveRow resolves on success OR offline-queue (network error → outbox); it only
    // throws on a real server rejection (4xx). So apply the optimistic state ONLY after
    // it resolves — a rejection surfaces an error and bails (no false "success").
    try {
      await saveRow('dms_documents', doc.document_id, { is_permanent: next, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setSuccessMsg(`Couldn't update "${doc.filename}". Please try again.`);
      return;
    }
    setDocuments(prev => prev.map(d => d.document_id === doc.document_id ? { ...d, is_permanent: next } : d));
    setSuccessMsg(next
      ? `"${doc.filename}" marked permanent — won't be auto-deleted.`
      : `"${doc.filename}" is no longer marked permanent.`);
  }, [selectedDoc]);

  // Move: re-file the document into another folder.
  const handleMoveDoc = useCallback(async (folderId: number) => {
    if (!selectedDoc) return;
    try {
      await saveRow('dms_documents', selectedDoc.document_id, { dms_folders_id: folderId, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setSuccessMsg(`Couldn't move "${selectedDoc.filename}". Please try again.`);
      return;   // keep the move popover open so the user can retry
    }
    setDocuments(prev => prev.map(d => d.document_id === selectedDoc.document_id ? { ...d, dms_folders_id: folderId } : d));
    setShowDocMove(false);
    setDocMoveSearch('');
    const fname = folders.find(f => f.folder_id === folderId)?.folder_name || 'folder';
    setSuccessMsg(`Moved "${selectedDoc.filename}" to "${fname}".`);
  }, [selectedDoc, folders]);

  // Delete: open a confirmation modal first — never delete silently.
  const [deleteDocTarget, setDeleteDocTarget] = useState<DocRow | null>(null);
  const deleteModalDrag = useDragOffset();
  const dupModalDrag = useDragOffset();
  const manageFoldersDrag = useDragOffset();
  const addPartyDrag = useDragOffset();
  const [deletingDoc, setDeletingDoc] = useState(false);
  const handleDeleteDoc = useCallback((docArg?: DocRow) => {
    const doc = docArg || selectedDoc;
    if (!doc) return;
    deleteModalDrag.reset();           // opens centred each time
    setDeleteDocTarget(doc);
  }, [selectedDoc, deleteModalDrag]);

  // Confirmed: soft-delete the document (reversible; keeps the row).
  const confirmDeleteDoc = useCallback(async () => {
    const doc = deleteDocTarget;
    if (!doc) return;
    setDeletingDoc(true);
    try {
      await saveRow('dms_documents', doc.document_id, { is_deleted: 1, updated_at: NOW_ISO() }, 'document_id');
    } catch {
      setDeletingDoc(false);
      setSuccessMsg(`Couldn't delete "${doc.filename}". Please try again.`);
      return;
    }
    const fname = doc.filename;
    // If the deleted doc was the one open in the viewer, advance to the next doc
    // in the list (or the previous if it was the last) so the viewer + Filing form
    // reload from a fresh document instead of keeping the deleted doc's data.
    const wasSelected = selectedDocId === doc.document_id;
    let nextId: number | null = null;
    if (wasSelected) {
      const idx = filteredDocs.findIndex(d => d.document_id === doc.document_id);
      if (idx !== -1) {
        const after = filteredDocs.slice(idx + 1).find(d => d.document_id !== doc.document_id);
        const before = [...filteredDocs.slice(0, idx)].reverse().find(d => d.document_id !== doc.document_id);
        nextId = (after || before)?.document_id ?? null;
      }
    }
    dropFilingDraft(doc.document_id);
    setDocuments(prev => prev.map(d => d.document_id === doc.document_id ? { ...d, is_deleted: 1 } : d));
    if (wasSelected) {
      setFormDirty(false);          // discard the deleted doc's form state before re-selecting
      setSelectedDocId(nextId);     // null → forms blank (handled by the pre-fill effect)
    }
    setSuccessMsg(`"${fname}" deleted.`);
    setDeletingDoc(false);
    setDeleteDocTarget(null);
  }, [deleteDocTarget, selectedDocId, filteredDocs, dropFilingDraft]);

  // ---- Duplicates found on arrival ----
  // Coming to Process Docs with duplicate documents waiting, the user is reminded of them
  // — every time they arrive, until the duplicates are dealt with. Not when they arrived
  // through a link to one particular document (they came for that document). The prompt
  // removes nothing itself: "Review in list" opens the Duplicates tab, where each copy is
  // looked at and deleted one by one.
  type DupPromptGroup = { original: DocRow; copies: { doc: DocRow; matchPct: number }[] };
  const [dupPrompt, setDupPrompt] = useState<DupPromptGroup[] | null>(null);
  const dupPromptDoneRef = useRef(false);
  useEffect(() => {
    // Only Process Docs reminds about duplicates — not the Folder List (foldersOnly) that reuses this screen.
    if (foldersOnly) { dupPromptDoneRef.current = true; return; }
    if (dupPromptDoneRef.current || !dataLoaded || !dupInfoReady) return;
    if (arrivedByLinkRef.current) { dupPromptDoneRef.current = true; return; }
    // The document list and the duplicate groups arrive separately and settle over a few
    // renders; decide once they have stopped changing, then never again for this arrival.
    const timer = setTimeout(() => {
      if (dupPromptDoneRef.current) return;
      dupPromptDoneRef.current = true;
      const byId = new Map(scopedDocs.map(d => [d.document_id, d] as const));
      const groups = new Map<number, DupPromptGroup>();
      for (const d of scopedDocs) {
        const info = dupInfo.get(d.document_id);
        if (!info || info.isOriginal) continue;
        const original = byId.get(Number(info.originalId));
        if (!original) continue;                       // the original is outside this view — nothing to compare with
        const g = groups.get(original.document_id) || { original, copies: [] };
        g.copies.push({ doc: d, matchPct: info.matchPct });
        groups.set(original.document_id, g);
      }
      const list = Array.from(groups.values());
      if (list.length === 0) return;
      setDupPrompt(list);
    }, 1200);
    return () => clearTimeout(timer);
  }, [dataLoaded, dupInfoReady, dupInfo, scopedDocs, foldersOnly]);

  const closeDupPrompt = useCallback(() => setDupPrompt(null), []);

  const reviewDuplicates = useCallback(() => {
    setActiveFilter('duplicates');
    setSelectedDocId(null);
    setShowDocList(true);                              // the list may be folded away — open it so the copies show
    setDupPrompt(null);
  }, []);

  // ---- Rotate pages (viewer toolbar) ----
  // Turns the page on screen, or every page, and saves the turned PDF as the document's
  // file: a NEW blob is stored and file_path re-pointed (blobs are never overwritten, so
  // the previous file stays in the store and its pointer in the dms_documents log).
  // file_hash is left as it was so re-uploading the original scan is still a duplicate.
  useEffect(() => { setPdfOpenAtPage(undefined); setPdfRotateMenuOpen(false); }, [selectedDoc?.document_id]);
  const rotateDocPages = useCallback(async (scope: 'page' | 'all', by: RotateBy) => {
    const doc = selectedDoc;
    setPdfRotateMenuOpen(false);
    if (!doc || !pdfBytes || pdfRotating) return;
    if (!isPdfBytes(pdfBytes)) {
      setSuccessMsg('');
      setErrorMsg('Only PDF documents can be rotated.');
      return;
    }
    const page = Math.min(Math.max(1, pdfCurrentPage), Math.max(1, pdfNumPages));
    setPdfRotating(true);
    try {
      const rotated = await rotatePdfPages(pdfBytes, scope === 'all' ? 'all' : [page], by);
      const { base64, compressedSize } = await compressBytesToBase64(rotated);
      const key = await putServerBlob(base64, doc.account_owner_id);
      if (!key) throw new Error('the file could not be saved to the server (are you online and signed in?)');
      const updates = { file_path: key, file_size_original: rotated.length, file_size_stored: compressedSize };
      await saveRow('dms_documents', doc.document_id, updates, 'document_id');
      setPdfOpenAtPage(page);
      setPdfBytes(rotated);
      // The document list is deliberately NOT updated: a changed row re-runs the Filing
      // pre-fill, which would wipe auto-filled values not saved yet. Nothing in the list
      // uses file_path — the bytes are always fetched fresh (getDocBase64).
      setErrorMsg('');
      const turn = by === 180 ? '180°' : by > 0 ? '90° right' : '90° left';
      setSuccessMsg(scope === 'all' || pdfNumPages <= 1
        ? `Rotated ${pdfNumPages > 1 ? 'all pages' : 'the page'} ${turn} and saved.`
        : `Rotated page ${page} ${turn} and saved.`);
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not rotate: ${err?.message || 'unknown error'}. The document was not changed.`);
    } finally {
      setPdfRotating(false);
    }
  }, [selectedDoc, pdfBytes, pdfRotating, pdfCurrentPage, pdfNumPages]);

  // ---- Separate pages / combine documents (utils/pageEdit.ts) ----
  // Both make new To File documents and REPLACE the originals (soft-deleted; the dialog
  // warns this cannot be undone). Only for documents not filed yet. If saving a new
  // document fails part-way, the ones already made are removed again and the originals
  // stay as they were.
  const [splitOpen, setSplitOpen] = useState(false);
  const [combineOpen, setCombineOpen] = useState(false);
  const [pageEditBusy, setPageEditBusy] = useState(false);
  useEffect(() => { setSplitOpen(false); setCombineOpen(false); }, [selectedDoc?.document_id]);
  const pageEditAllowed = !!selectedDoc && selectedDoc.status === 'Review' && isPdfBytes(pdfBytes);

  // Put the new rows in the list, drop the replaced ones, open the first new one.
  const applyPageEdit = useCallback((created: Record<string, any>[], replacedIds: number[]) => {
    replacedIds.forEach(id => dropFilingDraft(id));
    setDocuments(prev => [
      ...prev.map(d => (replacedIds.includes(d.document_id) ? { ...d, is_deleted: 1 } : d)),
      ...created.map(r => { const { file_base64, ...rest } = r; return rest as DocRow; }),
    ]);
    setFormDirty(false);
    if (activeFilter !== 'to-file' && activeFilter !== 'all') setActiveFilter('to-file');
    setSelectedDocId(created[0] ? Number(created[0].document_id) : null);
  }, [dropFilingDraft, activeFilter]);

  const saveNewDocs = useCallback(async (like: DocRow, files: { bytes: Uint8Array; name: string }[]) => {
    const created: Record<string, any>[] = [];
    try {
      for (const f of files) created.push(await createDocumentLike(like, f.bytes, f.name, currentUser?.user_id ?? null));
      return created;
    } catch (err) {
      try { await retireDocuments(created.map(r => Number(r.document_id)).filter(Boolean)); } catch { /* best-effort */ }
      throw err;
    }
  }, [currentUser]);

  const separatePages = useCallback(async (groups: number[][]) => {
    const doc = selectedDoc;
    if (!doc || !pdfBytes || groups.length < 2 || pageEditBusy) return;
    setPageEditBusy(true);
    try {
      const pieces = await cutPdfGroups(pdfBytes, groups);
      const created = await saveNewDocs(doc, pieces.map((bytes, i) => ({ bytes, name: nameWithSuffix(doc.filename, pagesSuffix(groups[i])) })));
      await retireDocuments([doc.document_id]);
      applyPageEdit(created, [doc.document_id]);
      setSplitOpen(false);
      setErrorMsg('');
      setSuccessMsg(`"${doc.filename}" separated into ${created.length} documents (now in Verify date & File).`);
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not separate the pages: ${err?.message || 'unknown error'}. The document was not changed.`);
    } finally {
      setPageEditBusy(false);
    }
  }, [selectedDoc, pdfBytes, pageEditBusy, saveNewDocs, applyPageEdit]);

  const combineDocs = useCallback(async (orderedIds: number[]) => {
    const doc = selectedDoc;
    if (!doc || !pdfBytes || orderedIds.length < 2 || pageEditBusy) return;
    setPageEditBusy(true);
    try {
      const parts: Uint8Array[] = [];
      for (const id of orderedIds) {
        if (id === doc.document_id) { parts.push(pdfBytes); continue; }
        const b64 = await getDocBase64(id);
        const name = documents.find(d => d.document_id === id)?.filename || `#${id}`;
        if (!b64) throw new Error(`"${name}" could not be read`);
        const bytes = await decompressBase64ToBytes(b64);
        if (!isPdfBytes(bytes)) throw new Error(`"${name}" is not a PDF`);
        parts.push(bytes);
      }
      const merged = await mergePdfs(parts);
      const created = await saveNewDocs(doc, [{ bytes: merged, name: nameWithSuffix(doc.filename, '_combined') }]);
      await retireDocuments(orderedIds);
      applyPageEdit(created, orderedIds);
      setCombineOpen(false);
      setErrorMsg('');
      setSuccessMsg(`${orderedIds.length} documents combined into "${created[0]?.filename}" (now in Verify date & File).`);
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not combine: ${err?.message || 'unknown error'}. No document was changed.`);
    } finally {
      setPageEditBusy(false);
    }
  }, [selectedDoc, pdfBytes, pageEditBusy, getDocBase64, documents, saveNewDocs, applyPageEdit]);

  // Documents that can be combined with the open one: not filed yet, same account view.
  const combineCandidates = useMemo(() => (combineOpen && selectedDoc
    ? scopedDocs
        .filter(d => d.status === 'Review' && d.document_id !== selectedDoc.document_id)
        .sort((a, b) => (a.filename || '').localeCompare(b.filename || ''))
        .map(d => ({ document_id: d.document_id, filename: d.filename, date_of_document: d.date_of_document, page_count: d.page_count }))
    : []), [combineOpen, selectedDoc, scopedDocs]);

  // ---- Date verified (migration 134) ----
  // The user has checked the date of a To File document without filing it. Saves the
  // date in the field to the document (so "Document date" sorts use it) and moves the
  // document from To File to Date Verified; the rest of the Filing form is left as it
  // is (its draft keeps any other unsaved edits). Undo puts it back in To File.
  const [dateVerifySaving, setDateVerifySaving] = useState(false);
  // Save a few columns of the open document without re-running the Filing pre-fill.
  const saveDocQuietly = useCallback(async (doc: DocRow, updates: Record<string, any>) => {
    await saveRow('dms_documents', doc.document_id, updates, 'document_id');
    skipPrefillForRef.current = doc.document_id;
    setDocuments(prev => prev.map(d => (d.document_id === doc.document_id ? { ...d, ...updates } : d)));
  }, []);
  // stay = true (the tick box): keep the document open so Tab can go on to Undated and the
  // next field — except when ticking it in Verify date & File, where the document leaves the
  // list and the next one opens (owner, 2026-10-17). stay = false (Alt+V): open the next
  // document of the list. Either way the next document's date field gets the focus.
  const toggleDateVerified = useCallback(async (stay = false) => {
    const doc = selectedDoc;
    if (!doc || dateVerifySaving) return;
    const verify = !isDateVerified(doc);
    if (verify && !filingDate && !filingUndated) {
      setSuccessMsg('');
      setErrorMsg('Enter the date of the document (or tick Undated) before marking it as verified.');
      return;
    }
    if (verify && !filingUndated && dateCheckOkRef.current !== filingDate && futureDateConfirmed !== filingDate) {
      const now = new Date();
      const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const fromName = (String(doc.filename || '').match(/(\d{4})-\d{2}-\d{2}/) || [''])[0];
      const reason = suspectDocDate(filingDate, [filingPrintedDate, filingHandwrittenDate, fromName, String(doc.date_of_document || '')], todayIso);
      if (reason) {
        setDateCheckPrompt({ date: filingDate, reason, stay });
        return;
      }
    }
    setDateCheckPrompt(null);
    const updates: Record<string, any> = verify
      ? {
          date_of_document: filingUndated ? null : filingDate,
          date_undated: filingUndated ? 1 : 0,
          date_verified_at: NOW_ISO(),
          date_verified_by: currentUser?.user_id ?? null,
          date_confirm_needed: 0,
          ...(!filingUndated && filingDate !== String(doc.date_of_document || '').slice(0, 10) ? { date_source: 'user' } : {}),
        }
      : { date_verified_at: null, date_verified_by: null };
    // The document leaves the current list (To File <-> Date Verified): with Alt+V open its neighbour.
    const idx = filteredDocs.findIndex(d => d.document_id === doc.document_id);
    const leavesList = activeFilter === 'to-file' || activeFilter === 'date-verified';
    if (verify && activeFilter === 'to-file') stay = false;
    // idx < 0: the document is not in this list (reopened with "Back to previous document") —
    // undoing the tick brings it back here, so it stays open.
    const nextId = !stay && leavesList && idx >= 0 ? (filteredDocs[idx + 1] ?? filteredDocs[idx - 1])?.document_id ?? null : doc.document_id;
    setDateVerifySaving(true);
    try {
      await saveDocQuietly(doc, updates);
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not save: ${err?.message || 'server rejected the change'}.`);
      return;
    } finally {
      setDateVerifySaving(false);
    }
    if (verify) setHandwritingResults(prev => { const next = new Map(prev); next.delete(doc.document_id); return next; });
    setErrorMsg('');
    const inQueue = doc.status === 'Review';   // only To File / Date Verified documents change list
    setSuccessMsg(verify
      ? `${filingUndated ? 'Undated document' : `Date ${filingDate}`} verified${inQueue ? ` — "${doc.filename}" moved to Date Verified` : ''}.`
      : inQueue ? `"${doc.filename}" moved back to Verify date & File.` : 'Date no longer marked as verified.');
    if (verify && activeFilter === 'to-file' && nextId !== doc.document_id) {
      setLeftDoc({ id: doc.document_id, filename: doc.filename, nextId });
    } else if (!verify && leftDoc?.id === doc.document_id) {
      setLeftDoc(null);                       // back in Verify date & File: nothing to go back to
    }
    if (nextId !== doc.document_id) {
      setSelectedDocId(nextId);
      if (nextId != null) setTimeout(() => filingDateInputRef.current?.focus(), 0);
    }
  }, [selectedDoc, dateVerifySaving, filingDate, filingUndated, currentUser, filteredDocs, activeFilter, saveDocQuietly, leftDoc, futureDateConfirmed, filingPrintedDate, filingHandwrittenDate]);
  // The question goes away when the date or the document changes.
  useEffect(() => { setDateCheckPrompt(null); dateCheckOkRef.current = ''; }, [selectedDocId]);
  useEffect(() => { setDateCheckPrompt(p => (p && p.date !== filingDate ? null : p)); }, [filingDate]);

  // Undated: the document has no date. Saved straight away, like the Date verified tick.
  const toggleUndated = useCallback(async () => {
    const doc = selectedDoc;
    if (!doc || dateVerifySaving) return;
    const undated = !filingUndated;
    const updates: Record<string, any> = undated
      ? { date_undated: 1, date_of_document: null, date_source: 'user', date_confirm_needed: 0 }
      : { date_undated: 0 };
    setDateVerifySaving(true);
    try {
      await saveDocQuietly(doc, updates);
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not save: ${err?.message || 'server rejected the change'}.`);
      return;
    } finally {
      setDateVerifySaving(false);
    }
    setFilingUndated(undated);
    if (undated) { setFilingDate(''); setFilingDateAmbiguous(false); }
    setErrorMsg('');
  }, [selectedDoc, dateVerifySaving, filingUndated, saveDocQuietly]);

  // ---- Keyboard shortcuts (work from anywhere on the screen, also while typing in a field) ----
  //   Alt+N  next document        Alt+P  previous document        Ctrl+S / Ctrl+Enter  Submit (Publish on Bookkeeping)
  //   Alt+V  date is correct (To File → Date Verified), or undo it
  // Submit goes through the Submit button itself, so it does exactly what a click does —
  // nothing when the button is disabled (the reason is then shown). Off while a popup is open.
  const filingSubmitBtnRef = useRef<HTMLButtonElement | null>(null);
  const bkPublishBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (deleteDocTarget || dupPrompt || splitOpen || combineOpen) return;
      const key = e.key.toLowerCase();
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && key === 'v') {
        e.preventDefault();
        toggleDateVerified(false);
        return;
      }
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && (key === 'n' || key === 'p')) {
        e.preventDefault();
        navigateDoc(key === 'n' ? 1 : -1);
        return;
      }
      // ←/→ step documents from anywhere on the screen (owner, 2026-10-17), not only from
      // the viewer — which handles them itself first (and pans in Scroll mode). Not while
      // typing or picking: in a text box, date, dropdown or list the arrows keep their own job.
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.defaultPrevented) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        const typing = !!t && (tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable
          || (tag === 'INPUT' && !['checkbox', 'button', 'submit', 'reset'].includes((t as HTMLInputElement).type))
          || !!t.closest('[role="combobox"], [role="listbox"], [role="slider"], [role="tablist"], [role="radiogroup"]'));
        if (typing || document.querySelector('.pr-mf-overlay')) return;   // a popup is open
        e.preventDefault();
        navigateDoc(e.key === 'ArrowRight' ? 1 : -1);
        return;
      }
      // Ctrl+Enter or Ctrl+S (owner, 2026-10-17): Submit on Filing, Publish on Bookkeeping —
      // whichever form is on screen. Ctrl+S never opens the browser's "Save page" here.
      const ctrlS = key === 's' && !e.shiftKey;
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'Enter' || ctrlS)) {
        if (ctrlS) e.preventDefault();
        if (document.querySelector('.pr-mf-overlay')) return;   // a popup (e.g. Add Vendor) has its own Ctrl+S
        const bk = bkPublishBtnRef.current;
        const btn = filingSubmitBtnRef.current || bk;
        if (!btn) return;                       // neither form is on screen
        e.preventDefault();
        if (btn.disabled) {
          setSuccessMsg('');
          setErrorMsg(btn === bk ? 'Choose the owner before publishing.'
            : btn.title && !/Ctrl\+(Enter|S)/.test(btn.title) ? btn.title : 'Choose an owner and a folder before submitting.');
          return;
        }
        btn.click();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigateDoc, deleteDocTarget, dupPrompt, toggleDateVerified, splitOpen, combineOpen]);

  // Download all files in a folder as a zip
  const [downloadingFolderId, setDownloadingFolderId] = useState<number | null>(null);
  const handleDownloadAllFiles = useCallback(async (folderId: number, folderName: string) => {
    setFolderMenuOpenId(null);
    const folderDocs = documents.filter(
      // eslint-disable-next-line eqeqeq
      d => d.dms_folders_id === folderId && d.is_deleted != 1 && d.is_active != 0
    );
    if (folderDocs.length === 0) {
      setSuccessMsg('No documents in this folder to download.');
      setTimeout(() => setSuccessMsg(''), 3000);
      return;
    }
    setDownloadingFolderId(folderId);
    try {
      const zip = new JSZip();
      let allDocs: Record<string, any>[];
      try {
        allDocs = await loadTable('dms_documents');
      } catch {
        allDocs = getTableData('dms_documents');
      }
      let added = 0;
      for (const doc of folderDocs) {
        const fullDoc = allDocs.find((d: any) => d.document_id === doc.document_id);
        // Resolve bytes via the blob store (file_path) with an inline-base64 fallback;
        // on live the bytes live on the server volume, not in the row.
        const docBase64 = await resolveDocBase64(fullDoc);
        if (!docBase64) continue;
        try {
          const bytes = await decompressBase64ToBytes(docBase64);
          zip.file(doc.filename || `document_${doc.document_id}.pdf`, bytes);
          added++;
        } catch { /* skip corrupt files */ }
      }
      if (added === 0) {
        setSuccessMsg('No downloadable files found in this folder.');
        setTimeout(() => setSuccessMsg(''), 3000);
        return;
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName.replace(/[^a-zA-Z0-9_-]/g, '_')}_files.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccessMsg(`${added} file(s) zipped and downloading.`);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch {
      setSuccessMsg('Could not create zip file. Please try again.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } finally {
      setDownloadingFolderId(null);
    }
  }, [documents]);

  // =============================================
  // Filing form — folder helpers
  // =============================================
  const ownerFolders = useMemo(() => {
    return folders.filter(f =>
      // eslint-disable-next-line eqeqeq
      f.is_deleted != 1 && f.is_active == 1 &&
      // eslint-disable-next-line eqeqeq
      f.account_owner_id == accountOwnerId &&
      // Client's Accounts → only this client's folders (dms_client_folders.client_id)
      // eslint-disable-next-line eqeqeq
      (viewMode !== 'clients' || selectedClientId == null || (f as any).client_id == selectedClientId)
    );
  }, [folders, accountOwnerId, viewMode, selectedClientId]);

  // Selectable filing folders = scoped, non-umbrella folders (umbrellas can't hold docs).
  const selectableFolders = useMemo(
    () => ownerFolders.filter(f => Number(f.umbrella_folder) !== 1),
    [ownerFolders],
  );

  // A non-umbrella folder always has an owner. Filing a doc into it should set the
  // document's owner to match the folder's primary owner → returns the "type:id" key.
  const folderOwnerKeyFor = useCallback((folderId: number): string => {
    const fOwners = folderOwners.filter(o => o.folder_id === folderId && o.is_deleted !== 1);
    const primary = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
    return primary ? `${primary.owner_type}:${primary.owner_id}` : '';
  }, [folderOwners]);

  // Primary-owner nickname for a folder (users.nickname / business acronym) — shown
  // in the folder picker in place of the internal folder_path serial.
  const folderNickFor = useCallback((folderId: number): string => {
    // eslint-disable-next-line eqeqeq
    if (Number(folders.find(f => f.folder_id == folderId)?.is_shared) === 1) return 'Shared';
    const key = folderOwnerKeyFor(folderId);
    if (!key) return '';
    const [t, id] = key.split(':');
    return getOwnerNickname(t, Number(id), usersData, businessesData);
  }, [folderOwnerKeyFor, usersData, businessesData, folders]);

  // Owner entities reduced to the names worth matching a document against
  // (display name + nickname/business acronym). Feeds the conservative auto-matcher.
  const ownerMatchCandidates = useMemo<OwnerMatchCandidate[]>(() =>
    ownerEntities.map(e => ({
      key: `${e.owner_type}:${e.owner_id}`,
      names: [e.display_name, getOwnerNickname(e.owner_type, e.owner_id, usersData, businessesData)].filter(Boolean),
      isPerson: e.owner_type !== 'business' && e.owner_type !== 'client_business',
    })),
  [ownerEntities, usersData, businessesData]);

  // Selectable (scoped, non-umbrella) folders reduced to name + primary-owner key,
  // so a matched owner can disambiguate same-named folders across owners. Mirrors
  // the picker's source (ownerFolders sans umbrella) — a suggestion is always pickable.
  const folderMatchCandidates = useMemo<FolderMatchCandidate[]>(() =>
    ownerFolders
      .filter(f => Number(f.umbrella_folder) !== 1)
      .map(f => ({ id: f.folder_id, name: f.folder_name, ownerKey: folderOwnerKeyFor(f.folder_id) })),
  [ownerFolders, folderOwnerKeyFor]);

  // Toggle an owner in the multi-select "Owner Of Document" field.
  const toggleFilingOwner = useCallback((key: string) => {
    setFilingOwnerKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setFormDirty(true);
  }, []);

  // Toggle a folder in the multi-select "Select folder" field.
  const toggleFilingFolder = useCallback((id: number) => {
    setFilingFolderIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setFormDirty(true);
  }, []);

  // A folder was just created from the Select folder(s) field → select it, so the user
  // does not have to find the folder they made a moment ago. The latest one wins if
  // several were created. Only for the document the popup was opened on, and only for
  // the reload that follows the popup closing.
  useEffect(() => {
    const watch = newFolderWatchRef.current;
    if (!watch || showManageFolders || !watch.closedAt) return;
    if (watch.docId !== selectedDocId || Date.now() - watch.closedAt > 15000) { newFolderWatchRef.current = null; return; }
    const created = selectableFolders.filter(f => !watch.known.has(f.folder_id) && Number(f.is_deleted) !== 1);
    if (!created.length) return;                       // folders not reloaded yet (or none was created)
    newFolderWatchRef.current = null;
    const newest = created.reduce((a, b) => (b.folder_id > a.folder_id ? b : a));
    setFilingFolderIds(prev => (prev.includes(newest.folder_id) ? prev : [...prev, newest.folder_id]));
    if (filingVisibleRef.current) setFormDirty(true);
  }, [selectableFolders, showManageFolders, selectedDocId]);

  // ---- Party masters in Filing ----
  // Parties offered for this document: those of the chosen owner(s) — or, before an owner
  // is chosen, of every set of books in view — plus the account's SHARED ones (own books).
  const partyCandidates = useMemo(() => {
    const keys = filingOwnerKeys.length
      ? new Set(filingOwnerKeys)
      : new Set(ownerEntities.map(e => `${e.owner_type}:${e.owner_id}`));
    return partyMasters.filter(m =>
      m.account_owner_id === accountOwnerId &&
      (keys.has(`${m.owner_type}:${m.owner_id}`) || (viewMode !== 'clients' && m.owner_type === 'shared')));
  }, [partyMasters, filingOwnerKeys, ownerEntities, accountOwnerId, viewMode]);

  // The master entry the typed / auto-read party is: by party name, legal name, nickname or alias.
  const matchedParty = useMemo(() => {
    // also "India Palace Restaurant" for the party kept as "India Palace Restaurant, Regina"
    const hits = partiesNamed(partyCandidates, filingPartyName);
    // an entry of these books beats a shared one of the same name
    return hits.find(m => m.owner_type !== 'shared') || hits[0] || null;
  }, [partyCandidates, filingPartyName]);

  // ---- Party Name in Bookkeeping: a list from Masters, by document type ----
  const bkPartySide = useMemo<PartySide>(() => {
    // eslint-disable-next-line eqeqeq
    const code = docuTypes.find(dt => dt.docu_type_id == (bkDocuTypeId as any))?.docu_type_code || '';
    return partySideOf(code);
  }, [docuTypes, bkDocuTypeId]);

  // Names from the chosen owner's books (every set of books in view until one is chosen),
  // plus the account's shared ones. A name on the document that is not in the list yet
  // stays selectable, marked as such.
  const bkPartyInMasters = useMemo(() => {
    const keys = bkOwnerKey ? new Set([bkOwnerKey]) : new Set(ownerEntities.map(e => `${e.owner_type}:${e.owner_id}`));
    const listed = PARTY_SIDES[bkPartySide].listed;
    const byName = new Map<string, PartyMaster>();
    partyMasters
      .filter(m => m.account_owner_id === accountOwnerId && listed.includes(m.kind) &&
        (keys.has(`${m.owner_type}:${m.owner_id}`) || (viewMode !== 'clients' && m.owner_type === 'shared')))
      .forEach(m => { if (!byName.has(m.name.toLowerCase())) byName.set(m.name.toLowerCase(), m); });
    return byName;
  }, [partyMasters, bkOwnerKey, ownerEntities, bkPartySide, accountOwnerId, viewMode]);

  const bkPartyOptions = useMemo(() => {
    const several = PARTY_SIDES[bkPartySide].listed.length > 1;
    const opts = Array.from(bkPartyInMasters.values())
      .sort((a, b) => Number(b.owner_type === 'shared') - Number(a.owner_type === 'shared') || a.name.localeCompare(b.name))   // shared first
      .map(m => ({ value: m.name, label: [m.name, several ? m.kind : '', m.aliases.length ? `also ${m.aliases.join(', ')}` : ''].filter(Boolean).join(' · '), keywords: m.tags }));
    const current = bkPartyName.trim();
    if (current && !bkPartyInMasters.has(current.toLowerCase())) opts.unshift({ value: bkPartyName, label: `${current} · not in Masters`, keywords: '' });
    return opts;
  }, [bkPartyInMasters, bkPartySide, bkPartyName]);

  // ---- Party Name in Filing: the same list, for the Filing document type + owner(s) ----
  const filingPartySide = useMemo<PartySide>(() => {
    // eslint-disable-next-line eqeqeq
    const code = docuTypes.find(dt => dt.docu_type_id == (filingDocuTypeId as any))?.docu_type_code || '';
    return partySideOf(code);
  }, [docuTypes, filingDocuTypeId]);

  const filingPartyInMasters = useMemo(() => {
    const listed = PARTY_SIDES[filingPartySide].listed;
    const byName = new Map<string, PartyMaster>();
    partyCandidates.filter(m => listed.includes(m.kind))
      .forEach(m => { if (!byName.has(m.name.toLowerCase())) byName.set(m.name.toLowerCase(), m); });
    return byName;
  }, [partyCandidates, filingPartySide]);

  // The nickname is in the label so typing it finds the party, as it did in the text box.
  const filingPartyOptions = useMemo(() => {
    const several = PARTY_SIDES[filingPartySide].listed.length > 1;
    const opts = Array.from(filingPartyInMasters.values())
      .sort((a, b) => Number(b.owner_type === 'shared') - Number(a.owner_type === 'shared') || a.name.localeCompare(b.name))   // shared first
      .map(m => ({ value: m.name, label: [m.name, several ? m.kind : '', m.nickname, m.aliases.length ? `also ${m.aliases.join(', ')}` : '', m.owner_type === 'shared' ? 'Shared' : ''].filter(Boolean).join(' · '), keywords: m.tags }));
    const current = filingPartyName.trim();
    if (current && !filingPartyInMasters.has(current.toLowerCase())) opts.unshift({ value: filingPartyName, label: `${current} · not in Masters`, keywords: '' });
    return opts;
  }, [filingPartyInMasters, filingPartySide, filingPartyName]);

  // =============================================
  // Nature of Document → account head + memo (migration 142, utils/docNature)
  // =============================================
  // The picker lists the natures ("what it is for": Meal, Fuel…) and then the fixed
  // document kinds. A nature carries its base kind (Meal → Expense Receipt), so picking
  // one also sets docu_type_id — the party lists and tax reports keep using that.
  const natures = useMemo(() => naturesForAccount(natureRows, accountOwnerId || null), [natureRows, accountOwnerId]);
  // by id — an edited standard nature also by the standard one's id (see natureLookup)
  const natureById = useMemo(() => natureLookup(natures, natureRows), [natures, natureRows]);
  // Kind of Document is its own field, before Nature (owner, 2026-10-18): the kind first
  // (receipt, deposit, cheque copy, e-transfer…), then — for money spent — what it was
  // for. Both pickers can be edited and added to (KindFormModal / NatureFormModal).
  const kindOptions = useMemo<DropdownOption[]>(() => docuTypes
    .filter(dt => dt.is_deleted !== 1)
    .map(dt => ({ value: String(dt.docu_type_id), label: dt.docu_type_name, editable: true })), [docuTypes]);
  // The standard kind a kind is, or works like (an own kind) — natures are tied to those.
  const kindStandardId = useCallback((kindId: string | number | null | undefined): number | null => {
    if (kindId == null || kindId === '') return null;
    // eslint-disable-next-line eqeqeq
    const k = docuTypes.find(dt => dt.docu_type_id == (kindId as any));
    return k ? Number(k.standard_id ?? k.docu_type_id) : Number(kindId);
  }, [docuTypes]);
  // The natures offered for a kind: those of that kind; all of them when it has none.
  const naturesOfKind = useCallback((kindId: string): DocNature[] => {
    const std = kindStandardId(kindId);
    if (std == null) return natures;
    const mine = natures.filter(n => n.docu_type_id === Number(kindId) || kindStandardId(n.docu_type_id) === std);
    return mine.length ? mine : natures;
  }, [natures, kindStandardId]);
  const natureOptionsFor = useCallback((kindId: string): DropdownOption[] =>
    naturesOfKind(kindId).map(n => ({ value: `n:${n.nature_id}`, label: n.nature_name, keywords: n.match_words, editable: true })),
  [naturesOfKind]);
  const natureValue = useCallback((natureId: string) =>
    natureId && natureById.has(Number(natureId)) ? `n:${natureById.get(Number(natureId))!.nature_id}` : '', [natureById]);
  // "Meals & entertainment" under the field once a nature is chosen.
  const natureHint = useCallback((natureId: string, withExpense: boolean, expenseTypeId: string) => {
    const n = natureId ? natureById.get(Number(natureId)) : undefined;
    if (!n || !withExpense) return '';
    // eslint-disable-next-line eqeqeq
    return expenseTypes.find((e: any) => e.expense_type_id == (expenseTypeId || n.expense_type_id))?.type_name || '';
  }, [natureById, expenseTypes]);

  // Filing: a nature picked by the user (null = cleared). It brings its kind when the
  // document has none yet, or one the nature does not belong to.
  const applyFilingNature = useCallback((n: DocNature | null) => {
    setFormDirty(true);
    setFilingNatureAuto(false);
    natureAutoDocRef.current = selectedDocId;   // the user decided — no more suggestions here
    if (!n) { setFilingNatureId(''); setBkNatureId(''); return; }
    setFilingNatureId(String(n.nature_id));
    setBkNatureId(String(n.nature_id));
    if (n.docu_type_id && (!filingDocuTypeId || kindStandardId(filingDocuTypeId) !== kindStandardId(n.docu_type_id))) {
      setFilingDocuTypeId(String(n.docu_type_id)); setBkDocuTypeId(String(n.docu_type_id));
    }
    if (showBookkeeping && n.expense_type_id && (!filingExpenseTypeId || filingExpenseAuto)) {
      setFilingExpenseTypeId(String(n.expense_type_id));
      setFilingExpenseAuto(true);
    }
  }, [selectedDocId, showBookkeeping, filingExpenseTypeId, filingExpenseAuto, filingDocuTypeId, kindStandardId]);
  const pickFilingNature = useCallback((v: string) => {
    if (!v) { applyFilingNature(null); return; }
    const n = natureById.get(Number(v.split(':')[1]));
    if (n) applyFilingNature(n);
  }, [applyFilingNature, natureById]);
  // Filing: a kind picked by the user. A nature of another kind no longer fits → cleared.
  const pickFilingKind = useCallback((v: string) => {
    setFormDirty(true);
    setFilingDocuTypeId(v);
    if (v) setBkDocuTypeId(v);
    const n = filingNatureId ? natureById.get(Number(filingNatureId)) : undefined;
    if (n && (!v || kindStandardId(n.docu_type_id) !== kindStandardId(v))) {
      setFilingNatureId(''); setFilingNatureAuto(false); setBkNatureId('');
      natureAutoDocRef.current = null;   // a nature may be suggested again for the new kind
    }
  }, [filingNatureId, natureById, kindStandardId]);
  // Filing suggests a nature for a document still to be filed, once its kind is money
  // spent (or not known yet), from the natures of that kind, first that applies:
  //   1. the vendor's document line (Masters › Vendor › Document types)
  //   2. the AI reader's pick from this account's natures (filingNatureHint)
  //   3. the party's own name ("India Palace & Buffet" → Meal)
  //   4. for a till RECEIPT only (EXPS): the words on it, then the expense type read off it
  //      (fuel → Motor vehicle → Fuel). Not for invoices / statements / transfers: their
  //      fine print made a Sysco invoice "Bank charges" ("service charge… per month") and
  //      "please print this" made a bank transfer "Advertising".
  // Picking a nature once links it to the vendor, so its next documents get it from (1).
  // Filled by the app → not an unsaved edit.
  const natureSeenDocRef = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedDoc || selectedDoc.status !== 'Review' || selectedDoc.nature_id) return;
    const id = selectedDoc.document_id;
    // The first run for a document still sees the previous one's fields.
    if (natureSeenDocRef.current !== id) { natureSeenDocRef.current = id; return; }
    if (natureAutoDocRef.current === id || filingNatureId || !natures.length) return;
    // eslint-disable-next-line eqeqeq
    const kindCode = filingDocuTypeId ? String(docuTypes.find(dt => dt.docu_type_id == (filingDocuTypeId as any))?.docu_type_code || '') : '';
    if (kindCode && !EXPENSE_KIND_CODES.has(kindCode)) return;
    const pool = filingDocuTypeId ? naturesOfKind(filingDocuTypeId) : natures;
    const inPool = (x: DocNature | null | undefined) => (x && pool.some(p => p.nature_id === x.nature_id) ? x : null);
    let n: DocNature | null | undefined = null;
    if (matchedParty) {
      const kindKey = PARTY_KIND_KEY[matchedParty.kind];
      const lines = partyDocTypesRef.current.filter(t => t.party_kind === kindKey && t.party_id === matchedParty.id && t.nature_id != null && inPool(natureById.get(Number(t.nature_id))));
      const distinct = new Set(lines.map(l => Number(l.nature_id)));
      const hit = distinct.size === 1 ? lines[0] : (lines.length ? matchPartyDocType(lines, extractedText) : null);
      if (hit) n = natureById.get(Number(hit.nature_id));
    }
    if (!n && filingNatureHint) n = inPool(findNatureByName(natures, filingNatureHint));
    if (!n && filingPartyName.trim()) n = suggestNature(pool, filingPartyName);
    if (!n && kindCode === 'EXPS') {
      n = suggestNature(pool, extractedText)
        || (filingExpenseTypeId ? pool.find(p => p.expense_type_id === Number(filingExpenseTypeId)) : null);
    }
    if (!n) return;
    natureAutoDocRef.current = id;
    setFilingNatureId(String(n.nature_id));
    setFilingNatureAuto(true);
    setBkNatureId(prev => prev || String(n!.nature_id));
    if (!filingDocuTypeId && n.docu_type_id) { setFilingDocuTypeId(String(n.docu_type_id)); setBkDocuTypeId(prev => prev || String(n!.docu_type_id)); }
    if (showBookkeeping && n.expense_type_id && !filingExpenseTypeId) {
      setFilingExpenseTypeId(String(n.expense_type_id));
      setFilingExpenseAuto(true);
    }
  }, [selectedDoc, filingNatureId, natures, natureById, matchedParty, extractedText, filingPartyName, filingNatureHint,
      filingExpenseTypeId, filingDocuTypeId, docuTypes, naturesOfKind, showBookkeeping, partyDocTypesTick]);

  // A vendor's document lines learn the nature (and, from Bookkeeping, the account head):
  // the line for this nature gets the account when it has none; else a line of the same
  // name, or one named after the document kind, takes the nature; else a line is added.
  // A line the user removed for this nature is never put back. Returns a note for the
  // "filed" message ('' = nothing changed).
  const linkNatureToParty = useCallback(async (party: PartyMaster, nature: DocNature, coaId: number | null, docId: number, kindName: string): Promise<string> => {
    const kindKey = PARTY_KIND_KEY[party.kind];
    // eslint-disable-next-line eqeqeq
    const rows = ((await loadTable('acctg_party_doc_type')) as any[]).filter(r => r.party_kind === kindKey && r.party_id == party.id);
    // eslint-disable-next-line eqeqeq
    const live = rows.filter(r => r.is_deleted != 1);
    const coa = party.owner_type === 'shared' ? null : coaId;   // a shared party keeps no account
    const now = NOW_ISO();
    // eslint-disable-next-line eqeqeq
    const ids = natureIds(nature);
    let line = live.find(r => r.nature_id != null && ids.includes(Number(r.nature_id)));
    if (line) {
      if (coa == null || line.coa_id != null) return '';
      await saveRow('acctg_party_doc_type', line.party_doc_type_id, { coa_id: coa, updated_at: now }, 'party_doc_type_id');
      return ` Masters: ${party.name} › ${line.doc_type_name} now has its account head.`;
    }
    const lc = (v: any) => String(v || '').trim().toLowerCase();
    line = live.find(r => r.nature_id == null && lc(r.doc_type_name) === lc(nature.nature_name))
      || (kindName ? live.find(r => r.nature_id == null && lc(r.doc_type_name).startsWith(lc(kindName))) : undefined);
    if (line) {
      await saveRow('acctg_party_doc_type', line.party_doc_type_id, {
        nature_id: nature.nature_id, ...(coa != null && line.coa_id == null ? { coa_id: coa } : {}), updated_at: now,
      }, 'party_doc_type_id');
      return ` Masters: ${party.name} › ${line.doc_type_name} is a ${nature.nature_name}.`;
    }
    // eslint-disable-next-line eqeqeq
    if (rows.some(r => r.is_deleted == 1 && r.nature_id != null && ids.includes(Number(r.nature_id)))) return '';
    const taken = new Set(live.map(r => lc(r.doc_type_name)));
    let name = nature.nature_name.slice(0, 100);
    for (let i = 2; taken.has(lc(name)); i++) name = `${nature.nature_name.slice(0, 94)} (${i})`;
    await addRow('acctg_party_doc_type', {
      account_owner_id: accountOwnerId, party_kind: kindKey, party_id: party.id,
      doc_type_name: name, match_words: null, date_format_id: null, sample_document_id: docId,
      nature_id: nature.nature_id, coa_id: coa, sort_order: (live.length + 1) * 10,
      is_active: 1, is_deleted: 0, created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now,
    }, 'party_doc_type_id');
    return ` Masters: ${nature.nature_name} added to ${party.name}'s document types.`;
  }, [accountOwnerId, currentUser]);

  // The account head a set of books uses for a nature (acctg_doc_nature_account): the row
  // for the nature itself first, else one saved for the standard nature it replaces.
  const findNatureAccount = useCallback((books: { owner_type: string; owner_id: number }, n: DocNature) => {
    const rows = natureAccountRows.filter(r =>
      // eslint-disable-next-line eqeqeq
      Number(r.is_deleted) !== 1 && r.owner_type === books.owner_type && r.owner_id == books.owner_id);
    return natureIds(n).map(id => rows.find(r => Number(r.nature_id) === id)).find(Boolean) || null;
  }, [natureAccountRows]);
  // Sets (or with null, clears) the account head these books use for a nature.
  const saveNatureAccount = useCallback(async (books: { owner_type: string; owner_id: number }, n: DocNature, coaId: number | null) => {
    const now = NOW_ISO();
    const existing = findNatureAccount(books, n);
    if (existing && Number(existing.nature_id) === n.nature_id) {
      if (coaId == null) {
        await saveRow('acctg_doc_nature_account', existing.nature_account_id, { is_deleted: 1, updated_at: now }, 'nature_account_id');
        setNatureAccountRows(prev => prev.map(r => (r.nature_account_id === existing.nature_account_id ? { ...r, is_deleted: 1 } : r)));
      } else if (Number(existing.coa_id) !== coaId) {
        await saveRow('acctg_doc_nature_account', existing.nature_account_id, { coa_id: coaId, updated_at: now }, 'nature_account_id');
        setNatureAccountRows(prev => prev.map(r => (r.nature_account_id === existing.nature_account_id ? { ...r, coa_id: coaId } : r)));
      }
      return;
    }
    // none yet for this nature itself (maybe one for the standard it replaces — left as it is)
    if (coaId == null || (existing && Number(existing.coa_id) === coaId)) return;
    const row = await addRow('acctg_doc_nature_account', {
      account_owner_id: accountOwnerId, owner_type: books.owner_type, owner_id: books.owner_id,
      nature_id: n.nature_id, coa_id: coaId, is_active: 1, is_deleted: 0,
      created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now,
    }, 'nature_account_id');
    setNatureAccountRows(prev => [...prev, row]);
  }, [findNatureAccount, accountOwnerId, currentUser]);

  // ---- Bookkeeping: account head + memo ----
  const bkBooks = useMemo(() => {
    const [ot, oid] = bkOwnerKey.split(':');
    return ot && oid ? { owner_type: ot, owner_id: Number(oid) } : null;
  }, [bkOwnerKey]);
  const bkNature = bkNatureId ? natureById.get(Number(bkNatureId)) || null : null;
  const bkMatchedParty = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    const hits = partiesNamed(partyMasters.filter(m => m.account_owner_id == accountOwnerId), bkPartyName);
    return hits.find(m => bkBooks && m.owner_type === bkBooks.owner_type && m.owner_id === bkBooks.owner_id)
      || hits.find(m => m.owner_type === 'shared') || null;
  }, [partyMasters, bkPartyName, accountOwnerId, bkBooks]);
  // A recognised party shows under its Masters name (as in Filing), not "· not in Masters".
  useEffect(() => {
    if (bkMatchedParty && bkPartyName.trim() !== bkMatchedParty.name) setBkPartyName(bkMatchedParty.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bkMatchedParty]);
  // ---- Method of payment = the owner's bank / credit-card accounts (migration 143) ----
  // Statement categories, to tell a Bank / Credit Card account (a method of payment unless
  // the account says otherwise).
  const [finstaCates, setFinstaCates] = useState<any[]>([]);
  useEffect(() => {
    let cancelled = false;
    loadTable('acctg_finsta_cate')
      .then(rows => { if (!cancelled) setFinstaCates(rows); })
      .catch(() => { if (!cancelled) setFinstaCates(getTableData('acctg_finsta_cate')); });
    return () => { cancelled = true; };
  }, []);
  // The payment accounts of the document's owner(s), first owner's first.
  const filingPaymentAccounts = useMemo(() => {
    if (!filingOwnerKeys.length) return [] as CoaRow[];
    // eslint-disable-next-line eqeqeq
    const cateName = (id: unknown) => finstaCates.find((c: any) => c.cate_id == id)?.cate_name || '';
    const rank = (a: CoaRow) => filingOwnerKeys.indexOf(`${a.owner_type}:${a.owner_id}`);
    return coaAccounts
      .filter(a => a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
        // eslint-disable-next-line eqeqeq
        a.account_owner_id == accountOwnerId && rank(a) >= 0 && isPaymentAccount(a as any, cateName(a.finsta_cate_id)))
      .sort((a, b) => rank(a) - rank(b) || paymentAccountLabel(a as any).localeCompare(paymentAccountLabel(b as any)));
  }, [coaAccounts, finstaCates, filingOwnerKeys, accountOwnerId]);
  const filingPaymentAccount = filingPaymentCoaId
    ? coaAccounts.find(a => String(a.coa_id) === filingPaymentCoaId) || null : null;
  const paymentSelectOptions = useMemo<DropdownOption[]>(() => {
    const ownerLabel = (a: CoaRow) =>
      ownerEntities.find(e => e.owner_type === a.owner_type && String(e.owner_id) === String(a.owner_id))?.display_name || '';
    const accts: CoaRow[] = filingPaymentAccounts.slice();
    // A saved account no longer listed (owner changed, flag switched off) stays visible.
    if (filingPaymentAccount && !accts.some(a => a.coa_id === filingPaymentAccount.coa_id)) accts.unshift(filingPaymentAccount);
    const accountOpts = accts.map(a => {
      const nick = String((a as any).nickname || '').trim();
      return {
        value: `c:${a.coa_id}`,
        label: paymentAccountLabel(a as any) + (nick && nick !== a.account_name.trim() ? ` — ${a.account_name}` : ''),
        group: `YOUR ACCOUNTS${ownerLabel(a) ? ` — ${ownerLabel(a).toUpperCase()}` : ''}`,
        keywords: [a.account_name, (a as any).payment_match, (a as any).alias_names, (a as any).tags].filter(Boolean).join(', '),
      };
    });
    const methodOpts = paymentMethods.filter(p => Number(p.is_deleted) !== 1).map(p => ({
      value: `m:${p.payment_method_id}`,
      label: String(p.method_name),
      group: accountOpts.length ? 'OTHER METHODS' : '',
      keywords: String(p.match_aliases || '').split('|').join(', '),
    }));
    return [...accountOpts, ...methodOpts];
  }, [filingPaymentAccounts, filingPaymentAccount, paymentMethods, ownerEntities]);
  const paymentSelectValue = filingPaymentCoaId ? `c:${filingPaymentCoaId}` : filingPaymentMethodId ? `m:${filingPaymentMethodId}` : '';
  // Picking an account also sets the generic method by its card brand (Visa, Mastercard...),
  // so reports by method keep working; the text field carries the account's nickname.
  const choosePaymentAccount = useCallback((coaId: string) => {
    const a = coaAccounts.find(x => String(x.coa_id) === coaId);
    setFilingPaymentCoaId(coaId);
    if (!a) return;
    const brand = accountBrand(a as any);
    const pm = brand ? paymentMethods.find(p => Number(p.is_deleted) !== 1 && String(p.method_name).toLowerCase() === brand.toLowerCase()) : null;
    setFilingPaymentMethodId(pm ? String(pm.payment_method_id) : '');
    setFilingPaymentMethod(paymentAccountLabel(a as any).slice(0, 50));   // dms_documents.payment_method is VARCHAR(50)
  }, [coaAccounts, paymentMethods]);
  // Read the receipt for the account it was paid from: once per document, only while the
  // document has none saved and the user has not picked one.
  const paymentAutoDocRef = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedDoc || !extractedText || !filingPaymentAccounts.length) return;
    if (paymentAutoDocRef.current === selectedDoc.document_id) return;
    paymentAutoDocRef.current = selectedDoc.document_id;
    if (selectedDoc.payment_coa_id || filingPaymentCoaId) return;
    const hit = matchPaymentAccount(extractedText, filingPaymentAccounts as any);
    if (!hit) return;
    choosePaymentAccount(String(hit.coaId));
    setFilingPaymentCoaWhy(hit.why);
  }, [selectedDoc, extractedText, filingPaymentAccounts, filingPaymentCoaId, choosePaymentAccount]);

  // The detail accounts of the books the document is booked in.
  const bkAccounts = useMemo(() => (bkBooks ? coaAccounts.filter(a =>
    a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
    // eslint-disable-next-line eqeqeq
    a.account_owner_id == accountOwnerId && a.owner_type === bkBooks.owner_type && a.owner_id == bkBooks.owner_id) : []),
  [coaAccounts, bkBooks, accountOwnerId]);
  const bkCoaOptions = useMemo<DropdownOption[]>(() => bkAccounts
    .slice()
    .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))
    .map(a => ({
      value: String(a.coa_id),
      label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`,
      keywords: [(a as any).alias_names, (a as any).tags].filter(Boolean).join(', '),
    })), [bkAccounts]);
  // Filing's Account Head: the detail accounts of the books the document is filed in (its
  // first owner). Same value as Bookkeeping's Account Head — choosing it in either place
  // is the same choice, and Filing saves it with the document.
  const filingBooksKey = filingOwnerKeys[0] || '';
  const filingCoaOptions = useMemo<DropdownOption[]>(() => {
    const [ot, oid] = filingBooksKey.split(':');
    if (!ot || !oid) return [];
    return coaAccounts
      .filter(a => a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
        // eslint-disable-next-line eqeqeq
        a.account_owner_id == accountOwnerId && a.owner_type === ot && a.owner_id == (oid as any))
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))
      .map(a => ({
        value: String(a.coa_id),
        label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`,
        keywords: [(a as any).nickname, (a as any).alias_names, (a as any).tags].filter(Boolean).join(', '),
      }));
  }, [coaAccounts, filingBooksKey, accountOwnerId]);

  const bkAccountHead = useMemo<AccountHead | null>(() => {
    if (!bkAccounts.length) return null;
    // eslint-disable-next-line eqeqeq
    const expId = bkNature?.expense_type_id ?? (filingExpenseTypeId ? Number(filingExpenseTypeId) : (selectedDoc?.expense_type_id ?? null));
    // eslint-disable-next-line eqeqeq
    const expense = expId != null ? expenseTypes.find((e: any) => e.expense_type_id == expId) : null;
    const party = bkMatchedParty;
    const line = party && bkNature
      ? partyDocTypesRef.current.find(t => t.party_kind === PARTY_KIND_KEY[party.kind] && t.party_id === party.id && t.nature_id != null && natureIds(bkNature).includes(Number(t.nature_id)) && t.coa_id != null)
      : null;
    const booksRow = bkNature && bkBooks ? findNatureAccount(bkBooks, bkNature) : null;
    // An account of these books that names this nature (COA › Nature of Document).
    const ids = bkNature ? natureIds(bkNature) : [];
    const declared = ids.length ? bkAccounts
      .filter(a => String((a as any).nature_ids || '').split(',').some(x => ids.includes(Number(x))))
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))[0] : null;
    return resolveAccountHead({
      accounts: bkAccounts.map(a => ({ coa_id: Number(a.coa_id), account_name: a.account_name, finsta_cate_id: a.finsta_cate_id, alias_names: (a as any).alias_names })),
      vendorLineCoaId: line?.coa_id ?? null,
      accountNatureCoaId: declared ? Number(declared.coa_id) : null,
      booksCoaId: booksRow ? Number(booksRow.coa_id) : null,
      vendorDefaultCoaId: party?.default_coa_id ?? null,
      names: [bkNature?.nature_name || '', expense?.type_name || ''].filter(Boolean),
      preferCateId: expense?.finsta_cate_id ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bkAccounts, bkNature, bkMatchedParty, bkBooks, natureAccountRows, expenseTypes, filingExpenseTypeId, selectedDoc, partyDocTypesTick]);
  // "Meal at India Palace, paid from Visa" — the nature (else the document kind), the
  // party, and the payment method chosen in Filing.
  const bkAutoMemo = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    const kind = docuTypes.find(dt => dt.docu_type_id == (bkDocuTypeId as any))?.docu_type_name || '';
    const pm = paymentMethods.find(p => String(p.payment_method_id) === filingPaymentMethodId);
    const paidFrom = filingPaymentAccount ? paymentAccountLabel(filingPaymentAccount as any) : pm ? String(pm.method_name) : filingPaymentMethod;
    return buildMemo(bkNature?.nature_name || kind, bkPartyName, paidFrom);
  }, [docuTypes, bkDocuTypeId, paymentMethods, filingPaymentMethodId, filingPaymentMethod, filingPaymentAccount, bkNature, bkPartyName]);
  // While untouched, the account head and memo follow the nature / party / payment method.
  // A value the user changed or cleared on this document stays as they left it.
  const bkAutoDocRef = useRef<number | null>(null);
  const bkTouchedRef = useRef({ coa: false, memo: false });
  useEffect(() => {
    if (selectedDocId == null) return;
    if (bkAutoDocRef.current !== selectedDocId) {   // stale first run for a new document
      bkAutoDocRef.current = selectedDocId;
      bkTouchedRef.current = { coa: false, memo: false };
      return;
    }
    if (!bkTouchedRef.current.coa && (!bkCoaId || bkCoaAuto)) {
      const next = bkAccountHead ? String(bkAccountHead.coaId) : '';
      if (next !== bkCoaId) setBkCoaId(next);
      setBkCoaAuto(!!next);
    }
    if (!bkTouchedRef.current.memo && (!bkMemo || bkMemoAuto)) {
      if (bkAutoMemo !== bkMemo) setBkMemo(bkAutoMemo);
      setBkMemoAuto(!!bkAutoMemo);
    }
  }, [selectedDocId, bkAccountHead, bkAutoMemo, bkCoaId, bkCoaAuto, bkMemo, bkMemoAuto]);

  // Bookkeeping: a nature picked (it brings its kind when that does not fit), or a kind
  // picked (a nature of another kind is cleared).
  const pickBkNature = useCallback((v: string) => {
    setFormDirty(true);
    if (!v) { setBkNatureId(''); return; }
    const n = natureById.get(Number(v.split(':')[1]));
    if (!n) return;
    setBkNatureId(String(n.nature_id));
    if (n.docu_type_id && (!bkDocuTypeId || kindStandardId(bkDocuTypeId) !== kindStandardId(n.docu_type_id))) setBkDocuTypeId(String(n.docu_type_id));
  }, [natureById, bkDocuTypeId, kindStandardId]);
  const pickBkKind = useCallback((v: string) => {
    setFormDirty(true);
    setBkDocuTypeId(v);
    const n = bkNatureId ? natureById.get(Number(bkNatureId)) : undefined;
    if (n && (!v || kindStandardId(n.docu_type_id) !== kindStandardId(v))) setBkNatureId('');
  }, [bkNatureId, natureById, kindStandardId]);

  // ---- Kind of Document: add / edit form (KindFormModal) ----
  // A standard kind is never changed for everyone: renaming / removing it saves this
  // account's own row (overrides_docu_type_id); documents keep the standard id. A new kind
  // is this account's row that works like a standard one (base_docu_type_id) — see
  // utils/docKind. Shares the unsaved-changes flag the same way as the nature form.
  const [kindForm, setKindForm] = useState<{ from: 'filing' | 'bk'; kindId: number | null; name: string } | null>(null);
  const kindFormOpen = kindForm != null;
  const kindFormDirtyRef = useRef(false);
  const openKindForm = useCallback((from: 'filing' | 'bk', kindId: number | null, name = '') => {
    kindFormDirtyRef.current = isFormDirty();
    setFormDirty(false);
    setKindForm({ from, kindId, name });
  }, []);
  const closeKindForm = useCallback((changedForm = false) => {
    setKindForm(null);
    setFormDirty(kindFormDirtyRef.current || changedForm);
  }, []);
  const kindFormRow = kindForm?.kindId != null ? docuTypes.find(dt => dt.docu_type_id === kindForm.kindId) || null : null;

  const saveKindForm = useCallback(async (v: KindFormValues): Promise<boolean> => {
    if (!kindForm || !accountOwnerId) return false;
    const now = NOW_ISO();
    const k = kindFormRow;
    const audit = { created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now };
    const taken = new Set(docuTypeRows.map(r => String(r.docu_type_code || '')));
    try {
      if (k && k.own_row_id != null) {
        // this account's own kind, or its copy of a standard one: change it
        const patch: Record<string, any> = { docu_type_name: v.name.slice(0, 100), updated_at: now };
        if (k.row_kind === 'own' && v.worksLikeId) patch.base_docu_type_id = Number(v.worksLikeId);
        await saveRow('acctg_docu_type', k.own_row_id, patch, 'docu_type_id');
        setDocuTypes(prev => prev.map(r => (Number(r.docu_type_id) === k.own_row_id ? { ...r, ...patch } : r)));
      } else if (k) {
        // a standard kind renamed for this account
        if (v.name.trim().toLowerCase() === String(k.docu_type_name).toLowerCase()) { closeKindForm(); return true; }
        const row = await addRow('acctg_docu_type', {
          account_owner_id: accountOwnerId, docu_type_name: v.name.slice(0, 100),
          docu_type_code: ownKindCode(accountOwnerId, k.docu_type_code, taken),
          overrides_docu_type_id: k.docu_type_id, base_docu_type_id: k.docu_type_id,
          sort_order: Math.round(Number(k.sort_order) || 0), is_system: 0, is_active: 1, is_deleted: 0, ...audit,
        }, 'docu_type_id');
        setDocuTypes(prev => [...prev, row as DocuTypeRow]);
      } else {
        // a new kind of this account
        const base = docuTypes.find(dt => dt.docu_type_id === Number(v.worksLikeId));
        const row = await addRow('acctg_docu_type', {
          account_owner_id: accountOwnerId, docu_type_name: v.name.slice(0, 100),
          docu_type_code: ownKindCode(accountOwnerId, base?.docu_type_code || 'OTHR', taken),
          base_docu_type_id: Number(v.worksLikeId), overrides_docu_type_id: null,
          sort_order: Math.round(Number(base?.sort_order) || 0), is_system: 0, is_active: 1, is_deleted: 0, ...audit,
        }, 'docu_type_id');
        setDocuTypes(prev => [...prev, row as DocuTypeRow]);
        const newId = String(row.docu_type_id);
        const from = kindForm.from;
        closeKindForm(true);
        if (from === 'filing') pickFilingKind(newId); else pickBkKind(newId);
        setSuccessMsg(`Kind “${v.name}” added.`);
        return true;
      }
      closeKindForm();
      setSuccessMsg(`Kind “${v.name}” saved.`);
      return true;
    } catch (err: any) {
      setErrorMsg(`Could not save the kind “${v.name}”: ${err?.message || 'server rejected it'}.`);
      return false;
    }
  }, [kindForm, kindFormRow, accountOwnerId, currentUser, docuTypeRows, docuTypes, closeKindForm, pickFilingKind, pickBkKind]);

  // Delete an own kind; take a standard one off this account's list.
  const removeKindForm = useCallback(async (): Promise<boolean> => {
    const k = kindFormRow;
    if (!k || !accountOwnerId) return false;
    const now = NOW_ISO();
    try {
      if (k.own_row_id != null) {
        const patch = k.row_kind === 'own' ? { is_deleted: 1 } : { is_active: 0 };
        await saveRow('acctg_docu_type', k.own_row_id, { ...patch, updated_at: now }, 'docu_type_id');
        setDocuTypes(prev => prev.map(r => (Number(r.docu_type_id) === k.own_row_id ? { ...r, ...patch } : r)));
      } else {
        const row = await addRow('acctg_docu_type', {
          account_owner_id: accountOwnerId, docu_type_name: k.docu_type_name,
          docu_type_code: ownKindCode(accountOwnerId, k.docu_type_code, new Set(docuTypeRows.map(r => String(r.docu_type_code || '')))),
          overrides_docu_type_id: k.docu_type_id, base_docu_type_id: k.docu_type_id,
          sort_order: Math.round(Number(k.sort_order) || 0), is_system: 0, is_active: 0, is_deleted: 0,
          created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now,
        }, 'docu_type_id');
        setDocuTypes(prev => [...prev, row as DocuTypeRow]);
      }
      closeKindForm();
      setSuccessMsg(`Kind “${k.docu_type_name}” removed from your list.`);
      return true;
    } catch (err: any) {
      setErrorMsg(`Could not remove “${k.docu_type_name}”: ${err?.message || 'server rejected it'}.`);
      return false;
    }
  }, [kindFormRow, accountOwnerId, currentUser, docuTypeRows, closeKindForm]);

  // Drop this account's renaming of a standard kind.
  const restoreStandardKind = useCallback(async (): Promise<boolean> => {
    const k = kindFormRow;
    if (!k || k.row_kind !== 'edited-standard' || k.own_row_id == null) return false;
    try {
      await saveRow('acctg_docu_type', k.own_row_id, { is_deleted: 1, updated_at: NOW_ISO() }, 'docu_type_id');
      setDocuTypes(prev => prev.map(r => (Number(r.docu_type_id) === k.own_row_id ? { ...r, is_deleted: 1 } : r)));
      closeKindForm();
      setSuccessMsg('Back to the standard kind.');
      return true;
    } catch (err: any) {
      setErrorMsg(`Could not undo your changes: ${err?.message || 'server rejected it'}.`);
      return false;
    }
  }, [kindFormRow, closeKindForm]);
  // ---- Nature of Document: add / edit form (NatureFormModal) ----
  // "+ Add new nature / document type" at the top of the picker, "Add “…”" for what was
  // typed, and the pencil on a nature's row open it. A standard nature (everyone's) is
  // never changed: editing it saves this account's own copy, which replaces it in this
  // account's list (overrides_nature_id). The account head is set up for the books the
  // form was opened for. The form shares the app's one unsaved-changes flag, so Filing's
  // flag is put back on close (as for "Add new Vendor").
  const [natureForm, setNatureForm] = useState<{ from: 'filing' | 'bk'; nature: DocNature | null; name: string } | null>(null);
  const natureFormOpen = natureForm != null;
  const pickerFormOpen = natureFormOpen || kindFormOpen;   // a nature / kind form pop-up is open
  const natureFormDirtyRef = useRef(false);
  const openNatureForm = useCallback((from: 'filing' | 'bk', nature: DocNature | null, name = '') => {
    natureFormDirtyRef.current = isFormDirty();
    setFormDirty(false);
    setNatureForm({ from, nature, name });
  }, []);
  const closeNatureForm = useCallback((changedForm = false) => {
    setNatureForm(null);
    setFormDirty(natureFormDirtyRef.current || changedForm);
  }, []);
  const editNatureOption = useCallback((from: 'filing' | 'bk', value: string) => {
    const [kind, id] = value.split(':');
    const n = kind === 'n' ? natureById.get(Number(id)) : undefined;
    if (n) openNatureForm(from, n);
  }, [natureById, openNatureForm]);

  // The set of books the account head is set up for: the document's (first) owner.
  const natureFormBooks = useMemo(() => {
    if (!natureForm || !showBookkeeping) return null;
    const key = natureForm.from === 'bk' ? bkOwnerKey : (filingOwnerKeys[0] || '');
    const [ot, oid] = key.split(':');
    if (!ot || !oid) return null;
    const ent = ownerEntities.find(e => e.owner_type === ot && String(e.owner_id) === oid);
    const options = coaAccounts
      .filter(a => a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
        // eslint-disable-next-line eqeqeq
        a.account_owner_id == accountOwnerId && a.owner_type === ot && a.owner_id == (oid as any))
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))
      .map(a => ({
        value: String(a.coa_id),
        label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`,
        keywords: [(a as any).alias_names, (a as any).tags].filter(Boolean).join(', '),
      }));
    return { owner_type: ot, owner_id: Number(oid), label: ent ? ent.display_name : 'these books', coaOptions: options };
  }, [natureForm, showBookkeeping, bkOwnerKey, filingOwnerKeys, ownerEntities, coaAccounts, accountOwnerId]);

  const natureFormKind: 'own' | 'standard' | 'edited-standard' | null = !natureForm?.nature ? null
    : natureForm.nature.account_owner_id == null ? 'standard'
      : natureForm.nature.overrides_nature_id != null ? 'edited-standard' : 'own';

  // Puts a nature saved from the form into the field it was opened from.
  const putNatureInto = useCallback((from: 'filing' | 'bk', n: DocNature) => {
    if (from === 'filing') { applyFilingNature(n); return; }
    setFormDirty(true);
    setBkNatureId(String(n.nature_id));
    if (n.docu_type_id) setBkDocuTypeId(String(n.docu_type_id));
  }, [applyFilingNature]);

  const saveNatureForm = useCallback(async (v: NatureFormValues): Promise<boolean> => {
    if (!natureForm || !accountOwnerId) return false;
    const now = NOW_ISO();
    const fields = {
      nature_name: v.name.slice(0, 100),
      match_words: v.matchWords.split(',').map(w => w.trim()).filter(Boolean).join(', ') || null,
      docu_type_id: v.docuTypeId ? Number(v.docuTypeId) : null,
      expense_type_id: v.expenseTypeId ? Number(v.expenseTypeId) : null,
    };
    const old = natureForm.nature;
    try {
      let row: any;
      if (old && old.account_owner_id != null) {
        // this account's own nature (or its copy of a standard one): change it
        await saveRow('acctg_doc_nature', old.nature_id, { ...fields, updated_at: now }, 'nature_id');
        row = { ...natureRows.find(r => Number(r.nature_id) === old.nature_id), ...fields };
        setNatureRows(prev => prev.map(r => (Number(r.nature_id) === old.nature_id ? { ...r, ...fields } : r)));
      } else {
        // new, or a standard one edited → this account's own row
        row = await addRow('acctg_doc_nature', {
          account_owner_id: accountOwnerId, ...fields,
          overrides_nature_id: old ? old.nature_id : null,
          is_system: 0, sort_order: old ? old.sort_order : 0, is_active: 1, is_deleted: 0,
          created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now,
        }, 'nature_id');
        setNatureRows(prev => [...prev, row]);
      }
      const saved = toDocNature(row);
      if (natureFormBooks) {
        const before = findNatureAccount(natureFormBooks, saved);
        const was = before ? String(before.coa_id) : '';
        if (v.coaId !== was) {
          try { await saveNatureAccount(natureFormBooks, saved, v.coaId ? Number(v.coaId) : null); }
          catch (err: any) { setErrorMsg(`“${saved.nature_name}” was saved, but not its account head: ${err?.message || 'server rejected it'}.`); }
        }
      }
      const from = natureForm.from;
      // a new nature goes into the field; an edited one only refreshes what is shown
      closeNatureForm(!old);
      if (!old) putNatureInto(from, saved);
      setSuccessMsg(old ? `Nature “${saved.nature_name}” saved.` : `Nature “${saved.nature_name}” added.`);
      return true;
    } catch (err: any) {
      setErrorMsg(`Could not save the nature “${v.name}”: ${err?.message || 'server rejected it'}.`);
      return false;
    }
  }, [natureForm, accountOwnerId, natureRows, currentUser, natureFormBooks, findNatureAccount, saveNatureAccount, closeNatureForm, putNatureInto]);

  // Delete an own nature; take a standard one off this account's list (a switched-off copy).
  const removeNatureForm = useCallback(async (): Promise<boolean> => {
    const n = natureForm?.nature;
    if (!n || !accountOwnerId) return false;
    const now = NOW_ISO();
    try {
      if (n.account_owner_id == null) {
        const row = await addRow('acctg_doc_nature', {
          account_owner_id: accountOwnerId, nature_name: n.nature_name, match_words: n.match_words || null,
          docu_type_id: n.docu_type_id, expense_type_id: n.expense_type_id, overrides_nature_id: n.nature_id,
          is_system: 0, sort_order: n.sort_order, is_active: 0, is_deleted: 0,
          created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now,
        }, 'nature_id');
        setNatureRows(prev => [...prev, row]);
      } else {
        const patch = n.overrides_nature_id != null ? { is_active: 0 } : { is_deleted: 1 };
        await saveRow('acctg_doc_nature', n.nature_id, { ...patch, updated_at: now }, 'nature_id');
        setNatureRows(prev => prev.map(r => (Number(r.nature_id) === n.nature_id ? { ...r, ...patch } : r)));
      }
      closeNatureForm();
      setSuccessMsg(`Nature “${n.nature_name}” removed from your list.`);
      return true;
    } catch (err: any) {
      setErrorMsg(`Could not remove “${n.nature_name}”: ${err?.message || 'server rejected it'}.`);
      return false;
    }
  }, [natureForm, accountOwnerId, currentUser, closeNatureForm]);

  // Drop this account's changes to a standard nature (the copy is deleted).
  const restoreStandardNature = useCallback(async (): Promise<boolean> => {
    const n = natureForm?.nature;
    if (!n || n.overrides_nature_id == null) return false;
    try {
      await saveRow('acctg_doc_nature', n.nature_id, { is_deleted: 1, updated_at: NOW_ISO() }, 'nature_id');
      setNatureRows(prev => prev.map(r => (Number(r.nature_id) === n.nature_id ? { ...r, is_deleted: 1 } : r)));
      closeNatureForm();
      setSuccessMsg('Back to the standard nature.');
      return true;
    } catch (err: any) {
      setErrorMsg(`Could not undo your changes: ${err?.message || 'server rejected it'}.`);
      return false;
    }
  }, [natureForm, closeNatureForm]);

  // Tags start from the party's and the chosen folders' tags (Masters / Manage Folders)
  // while the document is still to be filed and its Tags box is empty — or still holds
  // only what was put in this way, so picking another party swaps them. Filled by the
  // app, so it is not an edit: the form is not marked unsaved (see the draft autosave).
  useEffect(() => {
    const ref = autoTagsRef.current;
    if (!selectedDoc || selectedDoc.status !== 'Review') { ref.docId = null; return; }
    const id = selectedDoc.document_id;
    // The first run for a document still sees the previous one's fields — wait for the
    // pre-fill (it always hands over a new folder list, so this runs again).
    if (ref.docId !== id) { autoTagsRef.current = { docId: id, sourceKey: null, value: '' }; return; }
    const folderTags = filingFolderIds.map(fid => folders.find(f => f.folder_id === fid)?.tags || '');
    const sourceTags = joinTags(parseTags([matchedParty?.tags || '', ...folderTags].join(','))) || '';
    if (ref.sourceKey === null && filingDraftsRef.current.get(id)?.tags !== undefined) {
      ref.sourceKey = sourceTags;   // a Draft's tags are the user's — leave them
      return;
    }
    if (ref.sourceKey === sourceTags) return;
    ref.sourceKey = sourceTags;
    const untouched = !filingTags.trim() || filingTags === ref.value;
    if (!untouched || filingTags === sourceTags) return;
    ref.value = sourceTags;
    setFilingTags(sourceTags);
    // A Draft taken a moment ago (the party / folder pick) carries the old tags.
    const draft = filingDraftsRef.current.get(id);
    if (draft) {
      const next = { ...draft, tags: sourceTags };
      filingDraftsRef.current.set(id, next);
      if (draftPendingRef.current?.docId === id) draftPendingRef.current = { docId: id, data: next };
      else saveDocDraft(id, FILING_DRAFT_FORM, next);
    }
  }, [selectedDoc, matchedParty, filingFolderIds, folders, filingTags]);

  // "Add new …" opens the Masters form on a new entry; the saved name becomes the party of
  // the form it was opened from. The Masters form shares the app's one unsaved-changes
  // flag, so the flag is put back on close (and set when Filing got a new party).
  const [addPartyFor, setAddPartyFor] = useState<'filing' | 'bk' | null>(null);
  const addPartyOpen = addPartyFor != null;
  // Dragged pop-ups open centred again the next time.
  const { reset: resetManageFoldersDrag } = manageFoldersDrag;
  const { reset: resetAddPartyDrag } = addPartyDrag;
  useEffect(() => { if (showManageFolders) resetManageFoldersDrag(); }, [showManageFolders, resetManageFoldersDrag]);
  useEffect(() => { if (addPartyOpen) resetAddPartyDrag(); }, [addPartyOpen, resetAddPartyDrag]);
  const addPartyDirtyRef = useRef(false);
  const addPartyOpenRef = useRef(false);   // set at once on open / close (state lags a render)
  const openAddParty = useCallback((from: 'filing' | 'bk') => {
    addPartyOpenRef.current = true;
    addPartyDirtyRef.current = isFormDirty();
    setFormDirty(false);
    setAddPartyFor(from);
  }, []);
  const closeAddParty = useCallback((savedName?: string) => {
    const from = addPartyFor;
    addPartyOpenRef.current = false;
    setAddPartyFor(null);
    if (savedName) {
      if (from === 'filing') {
        // added from a read name that was then corrected in the form → that read name is a misread
        const before = filingPartyName.trim();
        if (before && before.toLowerCase() !== savedName.trim().toLowerCase()) setFilingPartyMisread(before);
        setFilingPartyName(savedName);
      } else setBkPartyName(savedName);
      setPartyReloadKey(k => k + 1);
    }
    setFormDirty(addPartyDirtyRef.current || (!!savedName && from === 'filing'));
  }, [addPartyFor]);
  // X / click outside: like the form's own Cancel, but a typed entry asks first. "Save &
  // continue" files the entry through the form, which closes the popup itself.
  const dismissAddParty = useCallback(async () => {
    if (!(await confirmDiscardChanges())) return;
    if (addPartyOpenRef.current) closeAddParty();
  }, [closeAddParty]);
  // What the open popup is adding for: the side, whose books, and the name to start with.
  const addPartyCtx = addPartyFor === 'filing'
    ? { side: filingPartySide, ownerKey: filingOwnerKeys[0] || '', name: filingPartyInMasters.has(filingPartyName.trim().toLowerCase()) ? '' : filingPartyName.trim() }
    : { side: bkPartySide, ownerKey: bkOwnerKey, name: bkPartyInMasters.has(bkPartyName.trim().toLowerCase()) ? '' : bkPartyName.trim() };

  // A recognised party brings its related folder (when no folder is chosen yet), and a
  // nickname / legal name typed into Party Name becomes the party's display name. Offered
  // once per document + party, so a folder the user then removes is not put back.
  useEffect(() => {
    if (!matchedParty || selectedDocId == null) return;
    const tag = `${selectedDocId}:${matchedParty.kind}:${matchedParty.id}`;
    if (partyAppliedRef.current === tag) return;
    partyAppliedRef.current = tag;
    if (filingPartyName.trim() !== matchedParty.name) setFilingPartyName(matchedParty.name);
    const folderId = matchedParty.folder_id;
    if (folderId != null && selectableFolders.some(f => f.folder_id === folderId)) {
      setFilingFolderIds(prev => (prev.length ? prev : [folderId]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedParty, selectedDocId, selectableFolders]);

  // An unclear date ("09/10/09 could be…") is referred back to Masters › party › Document
  // types as soon as the party is known — also when the party was picked AFTER the
  // document was read, or its document types were learned in the meantime. Only while the
  // question is still open: a date the user picked or typed is never touched.
  useEffect(() => {
    if (!matchedParty || !selectedDoc || !filingDateAmbiguous || !filingRawDate) return;
    const fromMasters = masterDateFormat(matchedParty.name, `${extractedText} ${selectedDoc.filename || ''} ${selectedDoc.original_filename || ''}`, filingRawDate);
    if (!fromMasters) return;
    const now = new Date();
    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const di = interpretDate(filingRawDate, { formats: dateFormats as any, partyFormatId: fromMasters.formatId, maxIso: todayIso });
    if (di.ambiguous || !di.iso || di.source !== 'party' || di.iso > todayIso) return;
    const iso = di.iso;
    autoSetFilingDate(prev => (filingHandwrittenDate && prev === filingHandwrittenDate ? prev : iso));   // a handwritten date stays in the field
    setFilingPrintedDate(iso);
    setFilingPrintedFrom('text');
    if (di.formatId) setFilingDateFormatId(di.formatId);
    setFilingDateAmbiguous(false);
    setFilingDateCandidates([]);
    setFilingDateLayoutFrom(fromMasters.label);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedParty, filingDateAmbiguous, filingRawDate, extractedText, partyDocTypesTick, dateFormats]);

  // Suggested file name: date _ nickname (else party name) _ reference _ amount _ the
  // nickname of the account it was paid from ("RBC MC 6881", migration 143).
  const suggestedFilename = useMemo(() => {
    const who = (matchedParty?.nickname || filingPartyName).trim();
    const amt = filingAmount.trim() && Number.isFinite(Number(filingAmount)) ? Number(filingAmount).toFixed(2) : '';
    const paidBy = String(filingPaymentAccount?.nickname || '').trim();
    const parts = [filingDate, who, filingInvoiceRef.trim(), amt, paidBy]
      .map(x => String(x || '').replace(FILENAME_UNSAFE, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (!filingDate || !who) return '';             // not enough to build a meaningful name
    const ext = (filingFilename.match(/\.[A-Za-z0-9]{2,5}$/) || ['.pdf'])[0];
    return `${parts.join('_')}${ext}`;
  }, [matchedParty, filingPartyName, filingDate, filingInvoiceRef, filingAmount, filingPaymentAccount, filingFilename]);

  // ---- Future-date guard ----
  // A document cannot normally be dated after today. Auto-read already looks twice; if the
  // field still holds a future date (read or typed), the user must confirm it before filing.
  const filingDateIsFuture = useMemo(() => {
    if (!filingDate) return false;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return filingDate > today;
  }, [filingDate]);
  const futureDateNeedsConfirm = filingDateIsFuture && futureDateConfirmed !== filingDate;

  // ---- Handwritten date ----
  // The handwritten date wins: it replaces a date that auto-read filled in (never one
  // the user typed or already settled). Where it differs from the PRINTED date the user
  // must say which is right before the document can be filed.
  const dateAlreadySettled = !!selectedDoc && (selectedDoc.status === 'Filed' || isDateSettled(selectedDoc.date_source));
  const filingDateDecision = useMemo(
    () => decideDocDate(filingPrintedDate, filingHandwrittenDate, filingPrintedFrom),
    [filingPrintedDate, filingHandwrittenDate, filingPrintedFrom],
  );
  useEffect(() => {
    if (!selectedDoc || dateAlreadySettled || filingDateChoice || !filingHandwrittenDate) return;
    // Just switched document: the dates here are still the previous document's — filling
    // them in would put ITS handwritten date on this one.
    if (filingDatesDocId !== selectedDoc.document_id) return;
    const saved = String(selectedDoc.date_of_document || '').slice(0, 10);
    autoSetFilingDate(prev => (prev === '' || prev === filingPrintedDate || prev === saved ? filingHandwrittenDate : prev));
  }, [selectedDoc, dateAlreadySettled, filingDateChoice, filingHandwrittenDate, filingPrintedDate, filingDatesDocId, autoSetFilingDate]);
  const handwrittenDateNeedsConfirm = filingDateDecision.confirmNeeded && !dateAlreadySettled && !filingDateChoice;
  // The field holds a date that is neither reading (typed earlier and restored from the
  // draft): the user can keep it when both readings turn out to be wrong.
  const dateInFieldIsOwn = !!filingDate && filingDate !== filingHandwrittenDate && filingDate !== filingPrintedDate;
  // Shown beside the field: where the date in it came from.
  const filingDateOrigin: '' | 'handwritten' | 'printed' | 'filename' =
    !filingDate || filingDateChoice === 'user' ? ''
      : filingHandwrittenDate && filingDate === filingHandwrittenDate ? 'handwritten'
        : filingPrintedDate && filingDate === filingPrintedDate ? (filingPrintedFrom === 'filename' ? 'filename' : 'printed')
          : '';

  // ---- Base currency ----
  // The currency the document's owner keeps their books in: a business's Base Currency
  // (Business setup), else the currency of its / the person's country. Follows the first
  // chosen owner; before one is chosen, the document's saved owner, else the client
  // (Client's Accounts) or the account owner.
  const filingBaseCurrency = useMemo(() => {
    const first = filingOwnerKeys[0];
    if (first) {
      const [t, id] = first.split(':');
      return entityBaseCurrency(t, id, usersData, businessesData);
    }
    if (selectedDoc?.owner_type && selectedDoc.owner_id) {
      const c = entityBaseCurrency(selectedDoc.owner_type, selectedDoc.owner_id, usersData, businessesData);
      if (c) return c;
    }
    if (viewMode === 'clients' && selectedClientId != null) {
      const c = entityBaseCurrency('client_personal', selectedClientId, usersData, businessesData);
      if (c) return c;
    }
    return entityBaseCurrency('account_owner', accountOwnerId, usersData, businessesData);
  }, [filingOwnerKeys, selectedDoc, usersData, businessesData, viewMode, selectedClientId, accountOwnerId]);

  // Default the Filing + Bookkeeping currency to it. Only an EMPTY field, or one still
  // holding the default this effect put there (so changing the owner re-defaults), is
  // touched — a saved value, a typed value or a currency read off the document stays.
  useEffect(() => {
    const auto = autoCurrencyRef.current;
    if (auto.docId !== selectedDocId) { auto.docId = selectedDocId; auto.value = ''; }
    if (selectedDocId == null || !filingBaseCurrency) return;
    const prevAuto = auto.value;
    auto.value = filingBaseCurrency;
    setFilingCurrency(prev => (!prev || prev === prevAuto ? filingBaseCurrency : prev));
    setBkCurrency(prev => (!prev || prev === prevAuto ? filingBaseCurrency : prev));
  }, [filingBaseCurrency, selectedDocId, autoFillApplied]);

  // Owner options for the multi-select, with the already-selected pinned to the top.
  // Name only — no "(Owner)/(Family)/(Business)" badge in parentheses.
  const ownerSelectOptions = useMemo(() => {
    const all = ownerEntities.map(e => ({
      value: `${e.owner_type}:${e.owner_id}`,
      label: e.display_name,
    }));
    const sel = (v: string) => filingOwnerKeys.includes(v);
    return [...all.filter(o => sel(o.value)), ...all.filter(o => !sel(o.value))];
  }, [ownerEntities, filingOwnerKeys]);

  // Folder options for the multi-select, with the already-selected pinned to the top.
  const folderSelectOptions = useMemo(() => {
    const nick = (id: number) => (folderNickFor(id) ? ` · ${folderNickFor(id)}` : '');
    const all = selectableFolders.map(f => ({
      value: String(f.folder_id),
      label: `${f.folder_name}${nick(f.folder_id)}`,
      keywords: String((f as any).tags || ''),   // a folder is found by its tags too
    }));
    const sel = (v: string) => filingFolderIds.includes(Number(v));
    return [...all.filter(o => sel(o.value)), ...all.filter(o => !sel(o.value))];
  }, [selectableFolders, folderNickFor, filingFolderIds]);

  // =============================================
  // Filing Submit
  // =============================================
  // A party name on the form that is not in Masters gets a gentle suggestion to pick the
  // right one (a misread is then learned as an alias) or add it. NEVER required — the
  // owner's rule 2026-10-16: many users have no bookkeeping knowledge, filing stays easy.
  const filingPartyNeedsMasters = !!filingPartyName.trim() && !filingPartyInMasters.has(filingPartyName.trim().toLowerCase());

  const handleFilingSubmit = useCallback(async () => {
    if (!selectedDoc) return;
    if (filingOwnerKeys.length === 0) return;   // at least one owner
    if (filingFolderIds.length === 0) return;   // at least one folder
    if (futureDateNeedsConfirm) return;         // a date after today must be confirmed first
    if (handwrittenDateNeedsConfirm) return;    // handwritten vs printed date: the user must pick

    // Primary owner / folder live on dms_documents; the rest in the link tables.
    const primaryOwnerKey = filingOwnerKeys[0];
    const primaryFolderId = filingFolderIds[0];
    const [ownerType, ownerIdStr] = primaryOwnerKey.split(':');
    const ownerId = Number(ownerIdStr);
    const now = NOW_ISO();

    // Best-effort: register/refresh the party in the sitewide directory and learn its
    // date format. Non-fatal — filing must succeed even if the API is unavailable
    // (localStorage/offline mode). Public business facts only; never PII.
    let busiId: number | null = filingGlobalBusiId;
    // The layout to remember is the one the FILED date proves — which reading of the
    // printed date equals what the user is saving. A corrected first guess must not
    // teach the party the wrong layout; an unrelated/unprovable date teaches nothing.
    const learnedDateFormatId: number | null = filingRawDate
      ? (formatFromConfirmedDate(filingRawDate, filingDate, dateFormats as any)?.formatId ?? null)
      : filingDateFormatId;
    if (filingPartyName && filingPartyName.trim()) {
      try {
        const facts = filingBusinessFactsRef.current || {};
        const resp = await apiClient.post<{ busi_id: number; country_id: number }>('/directory/upsert', {
          canonical_name: filingPartyName.trim(),
          country_id: 0,                          // unknown jurisdiction → DEFAULT partition (future: resolve owner/party country)
          address_1: facts.address_1 || undefined,
          website: facts.website || undefined,
          general_phone: facts.general_phone || undefined,
          general_email: facts.general_email || undefined,
          category_code: facts.category_code || undefined,
          city_name: facts.city_name || undefined,
          state_name: facts.state_name || undefined,
          postal_code: facts.postal_code || undefined,
          general_fax: facts.general_fax || undefined,
          general_mobile: facts.general_mobile || undefined,
          tax_id: facts.tax_id || undefined,
          tax_id_label: facts.tax_id_label || undefined,
          tax_id_2: facts.tax_id_2 || undefined,
          tax_id_2_label: facts.tax_id_2_label || undefined,
          date_format_id: learnedDateFormatId || undefined,
          sample_document_id: selectedDoc.document_id,
          source_document_id: selectedDoc.document_id,
        });
        if (resp.data && resp.data.busi_id) busiId = Number(resp.data.busi_id);
        // Learn the party's date format from this filing (so the next ambiguous date resolves).
        if (busiId && learnedDateFormatId) {
          await apiClient.post('/directory/date-format', {
            busi_id: busiId, country_id: 0, date_format_id: learnedDateFormatId,
            sample_document_id: selectedDoc.document_id, sample_raw_date: filingRawDate || undefined,
            evidence_kind: 'user_confirmed',
          });
        }
      } catch { /* directory is best-effort */ }
    }

    const updates = {
      status: 'Filed',
      owner_type: ownerType,
      owner_id: ownerId,
      docu_type_id: filingDocuTypeId ? Number(filingDocuTypeId) : null,
      nature_id: filingNatureId ? natureById.get(Number(filingNatureId))?.nature_id ?? null : null,
      expense_type_id: filingExpenseTypeId ? Number(filingExpenseTypeId) : null,
      receipt_kind: filingReceiptKind || null,
      party_name: filingPartyName || null,
      invoice_ref: filingInvoiceRef || null,
      date_of_document: filingUndated ? null : (filingDate || null),
      date_undated: filingUndated ? 1 : 0,
      payment_method: filingPaymentMethod || null,
      payment_method_id: filingPaymentMethodId ? Number(filingPaymentMethodId) : null,
      payment_coa_id: filingPaymentCoaId ? Number(filingPaymentCoaId) : null,
      // the account head, when one of these books' accounts is chosen (Filing or Bookkeeping)
      ...(showBookkeeping && bkCoaId && filingCoaOptions.some(o => o.value === bkCoaId) ? { coa_id: Number(bkCoaId) } : {}),
      tax_type_id: filingTaxTypeId ? Number(filingTaxTypeId) : null,
      tax_rate_id: filingTaxRateId ? Number(filingTaxRateId) : null,
      tax_amount: filingTaxRateId && filingTaxAmount ? Number(filingTaxAmount) : null,
      date_format_id: learnedDateFormatId,
      // Filing settles the date: record which one the user went with, so it is never asked again.
      date_source: filingDate && filingHandwrittenDate && filingDate === filingHandwrittenDate ? 'handwritten-confirmed'
        : filingDate && filingPrintedDate && filingDate === filingPrintedDate && filingPrintedFrom === 'text' ? 'printed-confirmed'
          : 'user',
      date_confirm_needed: 0,
      // Filing checks the date as well — keep the first time it was checked.
      date_verified_at: selectedDoc.date_verified_at || now,
      date_verified_by: selectedDoc.date_verified_by ?? currentUser?.user_id ?? null,
      raw_date_string: filingRawDate || null,
      global_busi_id: busiId,
      total_amount: filingAmount ? Number(filingAmount) : null,
      currency: filingCurrency || null,
      purpose: filingPurpose || null,
      tags: joinTags(parseTags(filingTags)),
      filename: filingFilename.trim() || selectedDoc.filename,
      dms_folders_id: primaryFolderId,
      updated_at: now,
    };

    // Persist the filing. A real server error (not a network/offline blip, which
    // saveRow queues+applies locally) means the DB did NOT accept it — surface it and
    // bail instead of faking success with an optimistic update that reverts on reload.
    try {
      await saveRow('dms_documents', selectedDoc.document_id, updates, 'document_id');
    } catch (err: any) {
      setSuccessMsg('');
      setErrorMsg(`Could not file "${selectedDoc.filename}": ${err?.message || 'server rejected the change'}. Not filed.`);
      return;
    }
    setErrorMsg('');
    recordTagUse(updates.tags, selectedDoc.tags);   // new tags join the account's dictionary

    // Update local state
    setDocuments(prev => prev.map(d =>
      d.document_id === selectedDoc.document_id ? { ...d, ...updates } : d
    ));
    justProcessedRef.current = true;   // drives the To File → Rejected hand-off

    setHandwritingResults(prev => { const next = new Map(prev); next.delete(selectedDoc.document_id); return next; });

    // Reconcile multi-owner rows (dms_document_owners): one row per selected owner so
    // the same stored document surfaces under every owner. folder_id is left null —
    // folders are now tracked independently in dms_document_folders. Removed owners
    // are soft-deleted.
    const desiredOwners = new Map<string, number>();   // key → is_primary
    filingOwnerKeys.forEach((key, idx) => { if (key && !desiredOwners.has(key)) desiredOwners.set(key, idx === 0 ? 1 : 0); });
    const existingOwnerRows = docOwners.filter(r => r.document_id === selectedDoc.document_id);
    const nextDocOwners = [...docOwners];
    try {
      for (const [key, primary] of Array.from(desiredOwners.entries())) {
        const [ot, oidStr] = key.split(':');
        const oid = Number(oidStr);
        const existing = existingOwnerRows.find(r => `${r.owner_type}:${r.owner_id}` === key);
        if (existing) {
          const patch = { is_primary: primary, is_active: 1, is_deleted: 0, updated_at: now };
          await saveRow('dms_document_owners', existing.doc_owner_id, patch, 'doc_owner_id');
          const i = nextDocOwners.findIndex(x => x.doc_owner_id === existing.doc_owner_id);
          if (i >= 0) nextDocOwners[i] = { ...nextDocOwners[i], ...patch };
        } else {
          const created = await addRow('dms_document_owners', {
            document_id: selectedDoc.document_id,
            owner_type: ot, owner_id: oid,
            is_primary: primary, access_level: 'manage',
            folder_id: null, is_active: 1, is_deleted: 0,
            added_by: currentUser ? currentUser.user_id : null,
            created_at: now, updated_at: now,
          }, 'doc_owner_id');
          nextDocOwners.push(created as DocOwnerRow);
        }
      }
      for (const r of existingOwnerRows) {
        const key = `${r.owner_type}:${r.owner_id}`;
        if (desiredOwners.has(key) || Number(r.is_deleted) === 1) continue;
        const patch = { is_active: 0, is_deleted: 1, updated_at: now };
        await saveRow('dms_document_owners', r.doc_owner_id, patch, 'doc_owner_id');
        const i = nextDocOwners.findIndex(x => x.doc_owner_id === r.doc_owner_id);
        if (i >= 0) nextDocOwners[i] = { ...nextDocOwners[i], ...patch };
      }
      setDocOwners(nextDocOwners);
    } catch {
      // best-effort — the primary owner is already saved on dms_documents
    }

    // Reconcile multi-folder rows (dms_document_folders): one row per selected folder
    // so the doc surfaces inside every folder. Removed folders are soft-deleted.
    const desiredFolders = new Map<number, number>();   // folder_id → is_primary
    filingFolderIds.forEach((fid, idx) => { if (fid != null && !desiredFolders.has(fid)) desiredFolders.set(fid, idx === 0 ? 1 : 0); });
    const existingFolderRows = docFolders.filter(r => r.document_id === selectedDoc.document_id);
    const nextDocFolders = [...docFolders];
    try {
      for (const [fid, primary] of Array.from(desiredFolders.entries())) {
        const existing = existingFolderRows.find(r => Number(r.folder_id) === fid);
        if (existing) {
          const patch = { is_primary: primary, is_active: 1, is_deleted: 0, updated_at: now };
          await saveRow('dms_document_folders', existing.doc_folder_id, patch, 'doc_folder_id');
          const i = nextDocFolders.findIndex(x => x.doc_folder_id === existing.doc_folder_id);
          if (i >= 0) nextDocFolders[i] = { ...nextDocFolders[i], ...patch };
        } else {
          const created = await addRow('dms_document_folders', {
            document_id: selectedDoc.document_id,
            folder_id: fid, is_primary: primary, is_active: 1, is_deleted: 0,
            added_by: currentUser ? currentUser.user_id : null,
            created_at: now, updated_at: now,
          }, 'doc_folder_id');
          nextDocFolders.push(created as DocFolderRow);
        }
      }
      for (const r of existingFolderRows) {
        if (desiredFolders.has(Number(r.folder_id)) || Number(r.is_deleted) === 1) continue;
        const patch = { is_active: 0, is_deleted: 1, updated_at: now };
        await saveRow('dms_document_folders', r.doc_folder_id, patch, 'doc_folder_id');
        const i = nextDocFolders.findIndex(x => x.doc_folder_id === r.doc_folder_id);
        if (i >= 0) nextDocFolders[i] = { ...nextDocFolders[i], ...patch };
      }
      setDocFolders(nextDocFolders);
    } catch {
      // best-effort — the primary folder is already saved on dms_documents
    }

    // Masters learns from the filing: a date format this party is not yet known to print
    // becomes a line of its own under Masters › party › Document types (this document as
    // its sample), so the next unclear date of that kind is read without asking.
    let learnedNote = '';
    if (matchedParty && learnedDateFormatId) {
      try {
        const kindKey = PARTY_KIND_KEY[matchedParty.kind];
        // eslint-disable-next-line eqeqeq
        const mine = ((await loadTable('acctg_party_doc_type')) as any[]).filter(r => r.party_kind === kindKey && r.party_id == matchedParty.id);
        // eslint-disable-next-line eqeqeq
        const fmt = dateFormats.find(f => f.date_format_id == learnedDateFormatId);
        const formatLabel = String(fmt?.display_pattern || fmt?.format_code || `format ${learnedDateFormatId}`);
        // A new line is named after the Nature of Document: the nature when one is chosen.
        const docuTypeName = filingNatureId && natureById.has(Number(filingNatureId))
          ? natureById.get(Number(filingNatureId))!.nature_name
          // eslint-disable-next-line eqeqeq
          : String(docuTypes.find(dt => dt.docu_type_id == (filingDocuTypeId as any))?.docu_type_name || '');
        // eslint-disable-next-line eqeqeq
        const live = mine.filter(r => r.is_deleted != 1);
        const plan = planDocTypeLearning({
          types: live.map(toPartyDocType),
          // eslint-disable-next-line eqeqeq
          removed: mine.filter(r => r.is_deleted == 1).map(toPartyDocType),
          docuTypeName, text: `${extractedText} ${selectedDoc.filename || ''} ${selectedDoc.original_filename || ''}`,
          formatId: learnedDateFormatId, formatLabel,
        });
        if (plan.action === 'add') {
          await addRow('acctg_party_doc_type', {
            account_owner_id: accountOwnerId, party_kind: kindKey, party_id: matchedParty.id,
            doc_type_name: plan.name, match_words: null,
            date_format_id: learnedDateFormatId, sample_document_id: selectedDoc.document_id,
            sort_order: (live.length + 1) * 10,
            is_active: 1, is_deleted: 0, created_by: currentUser ? currentUser.user_id : null, created_at: now, updated_at: now,
          }, 'party_doc_type_id');
          learnedNote = ` Masters: date format ${formatLabel} added to ${matchedParty.name} › ${plan.name}.`;
        } else if (plan.action === 'fill') {
          // eslint-disable-next-line eqeqeq
          const line = live.find(r => r.party_doc_type_id == plan.id);
          await saveRow('acctg_party_doc_type', plan.id, {
            date_format_id: learnedDateFormatId,
            sample_document_id: line && line.sample_document_id != null ? line.sample_document_id : selectedDoc.document_id,
            updated_at: now,
          }, 'party_doc_type_id');
          learnedNote = ` Masters: date format ${formatLabel} set for ${matchedParty.name} › ${line ? line.doc_type_name : 'document type'}.`;
        }
        if (learnedNote) setPartyReloadKey(k => k + 1);
      } catch { /* best-effort — the document is filed either way */ }
    }

    // The party learns what this kind of document is for (Masters › party › Document types).
    const filedNature = filingNatureId ? natureById.get(Number(filingNatureId)) : undefined;
    if (matchedParty && filedNature) {
      try {
        // eslint-disable-next-line eqeqeq
        const kindName = String(docuTypes.find(dt => dt.docu_type_id == (filingDocuTypeId as any))?.docu_type_name || '');
        const note = await linkNatureToParty(matchedParty, filedNature, null, selectedDoc.document_id, kindName);
        if (note) { learnedNote += note; setPartyReloadKey(k => k + 1); }
      } catch { /* best-effort — the document is filed either way */ }
    }

    // A corrected misread becomes an alias of the party, so the next receipt read the same
    // way is recognised straight away.
    if (matchedParty && filingPartyMisread.trim()) {
      const misread = filingPartyMisread.trim();
      const known = [matchedParty.name, matchedParty.legal_name, matchedParty.nickname, ...matchedParty.aliases]
        .map(n => String(n || '').trim().toLowerCase());
      const t = PARTY_MASTER_TABLES.find(x => x.kind === matchedParty.kind);
      if (t && !known.includes(misread.toLowerCase())) {
        try {
          await saveRow(t.table, matchedParty.id, { alias_names: [...matchedParty.aliases, misread].join(', '), updated_at: NOW_ISO() }, t.pk);
          learnedNote += ` Masters: “${misread}” added as an alias of ${matchedParty.name}.`;
          setPartyReloadKey(k => k + 1);
        } catch { /* best-effort — the document is filed either way */ }
      }
      setFilingPartyMisread('');
    }

    setFormDirty(false);
    dropFilingDraft(selectedDoc.document_id);   // filed — the draft has done its job
    setSuccessMsg(`"${selectedDoc.filename}" filed successfully!${learnedNote}`);

    if (currentUser && currentUser.user_id !== accountOwnerId) {
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'receipt_filed',
        title: 'Receipt Filed',
        message: `${currentUser.first_name} filed "${selectedDoc.filename}"`,
        actor_user_id: currentUser.user_id,
        actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
      });
    }

    // Auto-advance to next doc
    const nextIdx = currentIndex + 1;
    if (nextIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[nextIdx].document_id);
    }
  }, [bkCoaId, filingCoaOptions, showBookkeeping, filingPartyMisread, handwrittenDateNeedsConfirm, filingHandwrittenDate, filingPrintedDate, filingPrintedFrom, selectedDoc, filingOwnerKeys, filingDocuTypeId, filingNatureId, natureById, linkNatureToParty, filingExpenseTypeId, filingReceiptKind, filingPartyName, filingInvoiceRef,
      filingDate, filingPaymentMethod, filingPaymentMethodId, filingPaymentCoaId, filingTaxTypeId, filingTaxRateId,
      filingTaxAmount, filingDateFormatId, filingRawDate, filingGlobalBusiId,
      filingAmount, filingCurrency, filingPurpose, filingTags,
      filingFolderIds, docOwners, docFolders, currentUser, accountOwnerId, currentIndex, filteredDocs, dropFilingDraft,
      matchedParty, extractedText, docuTypes, dateFormats]);

  // =============================================
  // Reject
  // =============================================
  const handleReject = useCallback(async () => {
    if (!selectedDoc) return;
    const now = NOW_ISO();
    const updates = { status: 'Rejected', updated_at: now };

    try {
      await saveRow('dms_documents', selectedDoc.document_id, updates, 'document_id');
    } catch {
      // saveRow handles localStorage fallback internally
    }

    setDocuments(prev => prev.map(d =>
      d.document_id === selectedDoc.document_id ? { ...d, ...updates } : d
    ));
    justProcessedRef.current = true;
    setSuccessMsg(`"${selectedDoc.filename}" rejected.`);

    const nextIdx = currentIndex + 1;
    if (nextIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[nextIdx].document_id);
    }
  }, [selectedDoc, currentIndex, filteredDocs]);

  // =============================================
  // Bookkeeping Publish
  // =============================================
  const handleBkPublish = useCallback(async () => {
    if (!selectedDoc) return;
    if (!bkOwnerKey) return;

    const [ownerType, ownerIdStr] = bkOwnerKey.split(':');
    const ownerId = Number(ownerIdStr);
    const now = NOW_ISO();

    const updates = {
      status: 'BK Done',
      owner_type: ownerType,
      owner_id: ownerId,
      docu_type_id: bkDocuTypeId ? Number(bkDocuTypeId) : selectedDoc.docu_type_id,
      nature_id: bkNatureId ? natureById.get(Number(bkNatureId))?.nature_id ?? null : null,
      coa_id: bkCoaId ? Number(bkCoaId) : null,
      bk_memo: bkMemo.trim() || null,
      party_name: bkPartyName || selectedDoc.party_name,
      invoice_ref: bkInvoiceRef || selectedDoc.invoice_ref,
      date_of_document: bkDate || selectedDoc.date_of_document,
      due_date: bkDueDate || null,
      total_amount: bkAmount ? Number(bkAmount) : selectedDoc.total_amount,
      currency: bkCurrency || selectedDoc.currency,
      tax_rate: bkTaxRate ? Number(bkTaxRate) : null,
      updated_at: now,
    };

    try {
      await saveRow('dms_documents', selectedDoc.document_id, updates, 'document_id');
    } catch {
      // saveRow handles localStorage fallback internally
    }

    setDocuments(prev => prev.map(d =>
      d.document_id === selectedDoc.document_id ? { ...d, ...updates } : d
    ));
    justProcessedRef.current = true;

    // The account head chosen for this nature is remembered: by these books (every
    // vendor), and on the vendor's line for the nature when the vendor is of these books.
    let learnedNote = '';
    if (bkNature && updates.coa_id != null) {
      const coaId = updates.coa_id;
      try {
        await saveNatureAccount({ owner_type: ownerType, owner_id: ownerId }, bkNature, coaId);
      } catch { /* best-effort — the bookkeeping is published either way */ }
      // eslint-disable-next-line eqeqeq
      if (bkMatchedParty && bkMatchedParty.owner_type === ownerType && bkMatchedParty.owner_id == ownerId) {
        try {
          // eslint-disable-next-line eqeqeq
          const kindName = String(docuTypes.find(dt => dt.docu_type_id == (updates.docu_type_id as any))?.docu_type_name || '');
          learnedNote = await linkNatureToParty(bkMatchedParty, bkNature, coaId, selectedDoc.document_id, kindName);
          if (learnedNote) setPartyReloadKey(k => k + 1);
        } catch { /* best-effort */ }
      }
    }
    setSuccessMsg(`"${selectedDoc.filename}" bookkeeping published!${learnedNote}`);

    if (currentUser && currentUser.user_id !== accountOwnerId) {
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'bk_published',
        title: 'Bookkeeping Published',
        message: `${currentUser.first_name} published bookkeeping for "${selectedDoc.filename}"`,
        actor_user_id: currentUser.user_id,
        actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
      });
    }

    const nextIdx = currentIndex + 1;
    if (nextIdx < filteredDocs.length) {
      setSelectedDocId(filteredDocs[nextIdx].document_id);
    }
  }, [selectedDoc, bkOwnerKey, bkDocuTypeId, bkNatureId, bkCoaId, bkMemo, bkNature, bkMatchedParty, natureById,
      saveNatureAccount, docuTypes, linkNatureToParty, bkPartyName, bkInvoiceRef, bkDate,
      bkDueDate, bkAmount, bkCurrency, bkTaxRate, currentUser, accountOwnerId,
      currentIndex, filteredDocs]);

  // =============================================
  // Bank transactions (line-items from a Convert-to-CSV statement)
  // =============================================
  // The imported acctg_bank_txn rows linked to the selected statement document.
  const docBankTxns = useMemo(
    () => selectedDoc
      ? bankTxns.filter(t => t.document_id === selectedDoc.document_id && t.is_deleted !== 1)
      : [],
    [bankTxns, selectedDoc]
  );

  // doc_id → number of linked bank txns (for the doc-list badge).
  const txnCountByDoc = useMemo(() => {
    const m = new Map<number, number>();
    bankTxns.forEach(t => {
      if (t.is_deleted === 1 || t.document_id == null) return;
      m.set(t.document_id, (m.get(t.document_id) || 0) + 1);
    });
    return m;
  }, [bankTxns]);

  // GL accounts to map a txn to — scoped to the document's owner (falls back to
  // all of the account owner's detail accounts if the owner has none yet).
  const coaOptions = useMemo<DropdownOption[]>(() => {
    if (!selectedDoc) return [];
    const accts = coaAccounts.filter(a =>
      a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
      // eslint-disable-next-line eqeqeq
      a.account_owner_id == accountOwnerId
    );
    const owned = selectedDoc.owner_type && selectedDoc.owner_id != null
      // eslint-disable-next-line eqeqeq
      ? accts.filter(a => a.owner_type === selectedDoc.owner_type && a.owner_id == selectedDoc.owner_id)
      : [];
    const list = owned.length ? owned : accts;
    return list
      .slice()
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))
      .map(a => ({
        value: String(a.coa_id),
        label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`,
        // found by an alias or a tag too
        keywords: [(a as any).alias_names, (a as any).tags].filter(Boolean).join(', '),
      }));
  }, [coaAccounts, selectedDoc, accountOwnerId]);

  // Profit centers (locations/units flagged "tracks own profitability") for the
  // selected doc's business entity. Empty otherwise → the per-txn picker is hidden.
  const txnProfitCenters = useMemo(
    () => getProfitCenters(selectedDoc && selectedDoc.owner_type === 'business' ? selectedDoc.owner_id : null, locationsData, unitsData),
    [selectedDoc, locationsData, unitsData]);

  // Reset the editable drafts whenever the selected doc's txns change.
  useEffect(() => {
    setTxnDrafts(docBankTxns.map(t => ({
      txn_id: t.txn_id,
      coa_id: t.coa_id == null ? null : Number(t.coa_id),
      category: t.category || '',
      status: t.status || 'imported',
      pc: txnProfitCenters.encode(t.location_id, t.unit_id),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docBankTxns, txnProfitCenters]);

  const updateTxnDraft = useCallback((txnId: number, patch: Partial<TxnDraft>) => {
    setTxnDrafts(prev => prev.map(d => d.txn_id === txnId ? { ...d, ...patch } : d));
    setFormDirty(true);
  }, []);

  const markAllTxnsReviewed = useCallback(() => {
    setTxnDrafts(prev => prev.map(d => ({ ...d, status: 'reviewed' })));
    setFormDirty(true);
  }, []);

  const handleSaveBankTxns = useCallback(async (): Promise<boolean> => {
    const now = NOW_ISO();
    setTxnBusy(true);
    let saved = 0;
    for (const draft of txnDrafts) {
      const orig = bankTxns.find(t => t.txn_id === draft.txn_id);
      if (!orig) continue;
      const pc = txnProfitCenters.decode(draft.pc);
      const changed =
        (orig.coa_id ?? null) !== (draft.coa_id ?? null) ||
        (orig.category ?? '') !== (draft.category ?? '') ||
        (orig.status ?? '') !== (draft.status ?? '') ||
        (orig.location_id ?? null) !== (pc.location_id ?? null) ||
        (orig.unit_id ?? null) !== (pc.unit_id ?? null);
      if (!changed) continue;
      const updates = {
        coa_id: draft.coa_id,
        category: draft.category || null,
        status: draft.status,
        location_id: pc.location_id,
        unit_id: pc.unit_id,
        updated_at: now,
      };
      try {
        await saveRow('acctg_bank_txn', draft.txn_id, updates, 'txn_id');
        saved++;
      } catch { /* saveRow handles localStorage fallback internally */ }
    }
    // Reflect drafts into local state
    setBankTxns(prev => prev.map(t => {
      const d = txnDrafts.find(x => x.txn_id === t.txn_id);
      if (!d) return t;
      const pc = txnProfitCenters.decode(d.pc);
      return { ...t, coa_id: d.coa_id, category: d.category, status: d.status, location_id: pc.location_id, unit_id: pc.unit_id };
    }));
    setFormDirty(false);
    setTxnBusy(false);
    if (saved > 0) {
      setSuccessMsg(`Saved ${saved} transaction assignment${saved === 1 ? '' : 's'}.`);
      if (currentUser && currentUser.user_id !== accountOwnerId) {
        addNotification({
          account_owner_id: accountOwnerId,
          type: 'bank_txn_updated',
          title: 'Bank Transactions Updated',
          message: `${currentUser.first_name} updated ${saved} bank transaction${saved === 1 ? '' : 's'}`,
          actor_user_id: currentUser.user_id,
          actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
        });
      }
    } else {
      setSuccessMsg('No changes to save.');
    }
    return true;
  }, [txnDrafts, bankTxns, currentUser, accountOwnerId, txnProfitCenters]);

  // txn_id of every bank txn already posted to the GL (idempotency guard for "Post to GL").
  const postedTxnIds = useMemo(() => {
    const s = new Set<number>();
    glJournals.forEach(j => {
      if (j.is_deleted !== 1 && j.status === 'posted' && j.source === 'bank_txn' && j.source_txn_id != null) {
        s.add(Number(j.source_txn_id));
      }
    });
    return s;
  }, [glJournals]);

  // Post mapped bank transactions to the GL: each becomes a balanced journal entry
  // (mapped account vs the chosen bank/cash account, per direction). Idempotent —
  // already-posted txns are skipped; unmapped/rejected are skipped + reported.
  const handlePostToGl = useCallback(async () => {
    if (!bankAccountCoaId) { setSuccessMsg('Pick the bank/cash account for this statement first.'); return; }
    const bankCoa = Number(bankAccountCoaId);
    const eligible = docBankTxns.filter(t => t.is_deleted !== 1 && t.status !== 'rejected' && !postedTxnIds.has(t.txn_id));
    const candidates = eligible.filter(t => t.coa_id != null);
    const skippedNoAcct = eligible.length - candidates.length;
    if (candidates.length === 0) {
      setSuccessMsg(skippedNoAcct > 0
        ? `Nothing to post — ${skippedNoAcct} transaction${skippedNoAcct === 1 ? '' : 's'} still need a GL account.`
        : 'Nothing new to post.');
      return;
    }
    setTxnBusy(true);
    let posted = 0;
    const newJournals: Record<string, any>[] = [];
    const balanceById = new Map<number, number>();   // accumulate balances across this run
    try {
      for (const t of candidates) {
        // Reflect already-applied balance changes (e.g. the bank account hit repeatedly).
        const coaSnapshot = coaAccounts.map(a => balanceById.has(Number(a.coa_id))
          ? { ...a, current_balance: balanceById.get(Number(a.coa_id)) } : a);
        const lines = bankTxnToLines(
          { direction: t.direction, amount: t.amount ?? 0, coa_id: Number(t.coa_id), description: t.description, location_id: t.location_id ?? null, unit_id: t.unit_id ?? null },
          bankCoa,
        );
        const result = await postJournalEntry({
          accountOwnerId,
          ownerType: t.owner_type || 'account_owner',
          ownerId: Number(t.owner_id),
          date: (t.txn_date || '').slice(0, 10) || NOW_ISO().slice(0, 10),
          memo: t.description || '',
          source: 'bank_txn',
          sourceTxnId: t.txn_id,
          documentId: t.document_id,
          currency: t.currency || filingBaseCurrency || 'CAD',
          createdBy: currentUser ? currentUser.user_id : null,
          lines,
          coaAccounts: coaSnapshot as any,
        });
        result.balanceUpdates.forEach(u => balanceById.set(u.coa_id, u.current_balance));
        newJournals.push(result.journal);
        await saveRow('acctg_bank_txn', t.txn_id, { status: 'posted', updated_at: NOW_ISO() }, 'txn_id');
        posted++;
      }
      setGlJournals(prev => [...newJournals, ...prev]);
      setCoaAccounts(prev => prev.map(a => balanceById.has(Number(a.coa_id))
        ? { ...a, current_balance: balanceById.get(Number(a.coa_id))! } : a));
      const postedSet = new Set(candidates.map(c => c.txn_id));
      setBankTxns(prev => prev.map(t => postedSet.has(t.txn_id) ? { ...t, status: 'posted' } : t));
      const parts = [`Posted ${posted} ${posted === 1 ? 'entry' : 'entries'} to the GL`];
      if (skippedNoAcct > 0) parts.push(`${skippedNoAcct} skipped (no account)`);
      setSuccessMsg(parts.join(' · ') + '.');
      if (currentUser && currentUser.user_id !== accountOwnerId) {
        addNotification({
          account_owner_id: accountOwnerId, type: 'gl_posted', title: 'Bank Transactions Posted',
          message: `${currentUser.first_name} posted ${posted} transaction${posted === 1 ? '' : 's'} to the GL`,
          actor_user_id: currentUser.user_id, actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
        });
      }
    } catch (e: any) {
      setSuccessMsg(e?.message ? `Posting stopped: ${e.message}` : 'Could not post to the GL. Please try again.');
    } finally {
      setTxnBusy(false);
    }
  }, [bankAccountCoaId, docBankTxns, postedTxnIds, coaAccounts, accountOwnerId, currentUser]);

  // "Save & continue" for the active right-panel form (Filing / Bookkeeping / Bank Txns) — modal.
  // Skip while the Manage Folders popup is open (it registers its own save handler);
  // re-registers when the popup closes (showManageFolders in deps).
  useEffect(() => {
    if (showManageFolders || addPartyOpen || pickerFormOpen) return;
    // The Make Journal Entries tab is a self-contained child that registers its OWN
    // save handler — don't fight it (mirror the Manage Folders skip above).
    if (rightTab === 'makejournal') return;
    const saver = rightTab === 'banktxn'
      ? handleSaveBankTxns
      : submitSaver(rightTab === 'bookkeeping' ? handleBkPublish : handleFilingSubmit);
    registerSaveHandler(saver);
    return () => registerSaveHandler(null);
  }, [rightTab, handleFilingSubmit, handleBkPublish, handleSaveBankTxns, showManageFolders, addPartyOpen, pickerFormOpen]);

  // "Discard changes" in the unsaved-changes modal also drops the Filing draft,
  // so discarded edits don't come back the next time the document is opened.
  useEffect(() => {
    if (showManageFolders || addPartyOpen || pickerFormOpen || selectedDocId == null) return;
    if (showBookkeeping && rightTab !== 'filing') return;
    registerDiscardHandler(() => discardFilingDraft(selectedDocId));
    return () => registerDiscardHandler(null);
  }, [rightTab, showBookkeeping, showManageFolders, addPartyOpen, pickerFormOpen, selectedDocId, discardFilingDraft]);

  // Filing edits are autosaved as a draft (kept until the document is filed), so leaving
  // Filing never asks "Unsaved changes" — the guard sends the draft and goes. Bookkeeping /
  // bank-transaction edits have no draft and still ask.
  useEffect(() => {
    if (showManageFolders || addPartyOpen || pickerFormOpen || selectedDocId == null) return;
    if (showBookkeeping && rightTab !== 'filing') return;
    registerAutosaveHandler(() => flushFilingDraft());
    return () => registerAutosaveHandler(null);
  }, [rightTab, showBookkeeping, showManageFolders, addPartyOpen, pickerFormOpen, selectedDocId, flushFilingDraft]);

  // =============================================
  // Bookkeeping computed values
  // =============================================
  const bkComputed = useMemo(() => {
    const amount = Number(bkAmount) || 0;
    const rate = Number(bkTaxRate) || 0;
    const tax = rate > 0 ? (amount * rate) / (100 + rate) : 0;
    const subtotal = amount - tax;
    return { subtotal, tax, total: amount };
  }, [bkAmount, bkTaxRate]);

  // =============================================
  // Record Room — folder tree
  // =============================================
  const visibleFolderIds = useMemo(() => {
    const term = folderSearchTerm.trim().toLowerCase();
    const hasOwner = scopedOwnerKeys.length > 0;
    // No search and no owner picked → show the whole tree.
    if (!term && !hasOwner) return null;

    // Folder ids owned by ANY selected owner entity (incl. joint ownership).
    let ownedIds: Set<number> | null = null;
    if (hasOwner) {
      ownedIds = new Set<number>();
      scopedOwnerKeys.forEach(key => {
        const [oType, oIdStr] = key.split(':');
        const oId = Number(oIdStr);
        folderOwners
          // eslint-disable-next-line eqeqeq
          .filter(o => o.is_deleted !== 1 && o.owner_type === oType && o.owner_id == oId)
          .forEach(o => ownedIds!.add(Number(o.folder_id)));
      });
    }

    // A folder is a "match" when it passes BOTH active filters (owner + search).
    const matched = ownerFolders.filter(f => {
      // A shared folder belongs to everyone, so it stays visible under any owner filter.
      if (ownedIds && !ownedIds.has(f.folder_id) && Number((f as any).is_shared) !== 1) return false;
      if (term && !f.folder_name?.toLowerCase().includes(term)) return false;
      return true;
    });

    // Include each match plus its ancestors so the tree stays navigable.
    const ids = new Set<number>();
    matched.forEach(f => {
      ids.add(f.folder_id);
      let current = f;
      // Stop at an ancestor already seen on THIS walk: two folders that are (wrongly)
      // each other's parent would otherwise loop here forever and freeze the page.
      const walked = new Set<number>([f.folder_id]);
      while (current.parent_folder_id != null && !walked.has(current.parent_folder_id)) {
        walked.add(current.parent_folder_id);
        ids.add(current.parent_folder_id);
        const parent = ownerFolders.find(p => p.folder_id === current.parent_folder_id);
        if (!parent) break;
        current = parent;
      }
    });
    return ids;
  }, [ownerFolders, folderOwners, folderSearchTerm, scopedOwnerKeys]);

  // Who a folder belongs to, in words: "Umbrella", "Shared", the owner's name (+N more), or ''.
  const folderOwnerLabel = useCallback((folder: FolderRow): string => {
    if (Number(folder.umbrella_folder) === 1) return 'Umbrella';
    if (Number((folder as any).is_shared) === 1) return 'Shared';
    const fOwners = folderOwners.filter(o => o.folder_id === folder.folder_id && o.is_deleted !== 1);
    const primary = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
    if (!primary) return '';
    const name = getOwnerName(primary.owner_type, primary.owner_id, usersData, businessesData) || '';
    return fOwners.length > 1 ? `${name} +${fOwners.length - 1}` : name;
  }, [folderOwners, usersData, businessesData]);

  const compareFolders = useCallback((a: FolderRow, b: FolderRow): number => {
    const byName = (a.folder_name || '').localeCompare(b.folder_name || '', undefined, { sensitivity: 'base', numeric: true });
    const flag = (f: FolderRow, key: 'umbrella_folder' | 'is_shared') => Number((f as any)[key]) === 1 ? 0 : 1;
    switch (folderSort) {
      case 'owner': {
        const la = folderOwnerLabel(a), lb = folderOwnerLabel(b);
        if (!la !== !lb) return la ? -1 : 1;                 // folders with no owner last
        return la.localeCompare(lb, undefined, { sensitivity: 'base' }) || byName;
      }
      case 'umbrella': return flag(a, 'umbrella_folder') - flag(b, 'umbrella_folder') || byName;
      case 'shared': return flag(a, 'is_shared') - flag(b, 'is_shared') || byName;
      default: return byName;
    }
  }, [folderSort, folderOwnerLabel]);

  const rootFolders = useMemo(() => {
    return ownerFolders
      .filter(f => f.parent_folder_id == null && (visibleFolderIds === null || visibleFolderIds.has(f.folder_id)))
      .sort(compareFolders);
  }, [ownerFolders, visibleFolderIds, compareFolders]);

  const getChildFolders = useCallback((parentId: number) => {
    return ownerFolders
      .filter(f => f.parent_folder_id === parentId && (visibleFolderIds === null || visibleFolderIds.has(f.folder_id)))
      .sort(compareFolders);
  }, [ownerFolders, visibleFolderIds, compareFolders]);

  // Folder List: Expand all = every folder that has sub-folders open; Collapse all = main level only.
  const expandAllFolders = useCallback(() => {
    const withChildren = new Set<number>();
    ownerFolders.forEach(f => { if (f.parent_folder_id != null) withChildren.add(Number(f.parent_folder_id)); });
    setExpandedFolders(withChildren);
  }, [ownerFolders]);
  const collapseAllFolders = useCallback(() => setExpandedFolders(new Set()), []);

  const getDocsInFolder = useCallback((folderId: number) => {
    return documents.filter(d =>
      // eslint-disable-next-line eqeqeq
      d.is_deleted != 1 && d.is_active == 1 &&
      d.dms_folders_id === folderId &&
      // eslint-disable-next-line eqeqeq
      d.account_owner_id == accountOwnerId
    );
  }, [documents, accountOwnerId]);

  // Folder List: how many documents each folder holds, its sub-folders included. A
  // document is counted once per folder even when it is filed in several folders of the
  // same branch (primary folder + multi-folder rows + owners' folders, as docInFolderSet).
  const { folderDocCounts, folderDirectDocs } = useMemo(() => {
    const counts = new Map<number, number>();
    const directDocs = new Map<number, DocRow[]>();   // documents filed IN the folder itself
    if (!foldersOnly) return { folderDocCounts: counts, folderDirectDocs: directDocs };
    const parentOf = new Map<number, number | null>();
    folders.forEach(f => parentOf.set(Number(f.folder_id), f.parent_folder_id == null ? null : Number(f.parent_folder_id)));
    for (const d of documents) {
      // eslint-disable-next-line eqeqeq
      if (d.is_deleted == 1 || d.is_active == 0 || d.account_owner_id != accountOwnerId) continue;
      const direct = new Set<number>();
      if (d.dms_folders_id != null) direct.add(Number(d.dms_folders_id));
      (docFoldersByDoc.get(d.document_id) || []).forEach(r => direct.add(Number(r.folder_id)));
      (docOwnersByDoc.get(d.document_id) || []).forEach(r => { if (r.folder_id != null) direct.add(Number(r.folder_id)); });
      direct.forEach(id => {
        if (!directDocs.has(id)) directDocs.set(id, []);
        directDocs.get(id)!.push(d);
      });
      const withAncestors = new Set<number>();
      direct.forEach(id => {
        let cur: number | null | undefined = id;
        let guard = 0;
        while (cur != null && !withAncestors.has(cur) && guard++ < 25) {
          withAncestors.add(cur);
          cur = parentOf.get(cur);
        }
      });
      withAncestors.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
    }
    // newest first
    directDocs.forEach(list => list.sort((a, b) =>
      String(b.date_of_document || '').localeCompare(String(a.date_of_document || '')) || Number(b.document_id) - Number(a.document_id)));
    return { folderDocCounts: counts, folderDirectDocs: directDocs };
  }, [foldersOnly, folders, documents, accountOwnerId, docFoldersByDoc, docOwnersByDoc]);

  // Folder List → a document: open it in Process Docs / Receipts (same hand-off as Reports).
  const openDocFromFolderList = useCallback(async (docId: number) => {
    if (!(await confirmDiscardChanges())) return;
    try { sessionStorage.setItem('akt_navigate_to_doc', String(docId)); } catch { /* ignore */ }
    window.location.hash = 'process-receipts';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  const toggleFolder = useCallback((folderId: number) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  }, []);

  // Drilled in from the Reports dashboard → highlight the target folder and expand
  // its ancestors so it's visible. Runs once the folder tree has loaded.
  useEffect(() => {
    if (initialSelectedFolderId == null || folders.length === 0) return;
    setShowRecordRoom(true);
    setSelectedRecordFolderId(initialSelectedFolderId);
    setExpandedFolders(prev => {
      const next = new Set(prev);
      let cur = folders.find(f => f.folder_id === initialSelectedFolderId);
      let guard = 0;
      while (cur && cur.parent_folder_id != null && guard < 20) {
        next.add(Number(cur.parent_folder_id));
        cur = folders.find(f => f.folder_id === cur!.parent_folder_id);
        guard++;
      }
      return next;
    });
  }, [initialSelectedFolderId, folders]);

  // =============================================
  // Render helpers
  // =============================================
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Review': return <span className="pr-doc-status-icon review">?</span>;
      case 'Filed': return <span className="pr-doc-status-icon filed"><FiCheck /></span>;
      case 'Rejected': return <span className="pr-doc-status-icon rejected"><FiX /></span>;
      case 'BK Done': return <span className="pr-doc-status-icon bk-done"><FiCheck /></span>;
      default: return <span className="pr-doc-status-icon review">?</span>;
    }
  };

  const getDocOwnerLabel = (doc: DocRow): string => {
    if (doc.owner_type && doc.owner_id) {
      const name = getOwnerName(doc.owner_type, doc.owner_id, usersData, businessesData);
      if (name) {
        const ent = ownerEntities.find(e => e.owner_type === doc.owner_type && e.owner_id === doc.owner_id);
        return `${name}${ent ? ` (${ent.badge})` : ''}`;
      }
    }
    // Fallback: look up user_id
    // eslint-disable-next-line eqeqeq
    const u = usersData.find((u: any) => u.user_id == doc.user_id);
    if (u) {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      return name || u.email || '';
    }
    return '';
  };

  // =============================================
  // Render: Folder tree node (recursive)
  // =============================================
  const handleFolderFilter = useCallback((folderId: number) => {
    setSelectedRecordFolderId(prev => prev === folderId ? null : folderId);
    setSelectedDocId(null);
  }, []);

  const renderFolderNode = (folder: FolderRow): React.ReactNode => {
    const children = getChildFolders(folder.folder_id);
    // The Folder List shows a folder's documents only at the LAST level (a folder with no
    // sub-folders), and keeps that list closed until the folder is opened — a search or
    // "Expand all" opens folders, never document lists.
    const isLeaf = children.length === 0;
    const docsInFolder = foldersOnly
      ? (isLeaf ? folderDirectDocs.get(Number(folder.folder_id)) || [] : [])
      : getDocsInFolder(folder.folder_id);
    const isExpanded = expandedFolders.has(folder.folder_id) || (visibleFolderIds !== null && !(foldersOnly && isLeaf));
    const hasChildren = children.length > 0 || docsInFolder.length > 0;
    const isActiveFilter = selectedRecordFolderId === folder.folder_id;
    const emoji = FOLDER_EMOJI[folder.folder_icon || 'other'] || '📁';

    // Left marker: umbrella folders → umbrella icon; owned folders → owner's
    // nickname badge (site-orange); otherwise the folder type emoji.
    const isUmbrella = Number(folder.umbrella_folder) === 1;
    const fOwners = folderOwners.filter(o => o.folder_id === folder.folder_id && o.is_deleted !== 1);
    const primaryOwner = fOwners.find(o => Number(o.is_primary) === 1) || fOwners[0];
    const ownerNick = primaryOwner ? getOwnerNickname(primaryOwner.owner_type, primaryOwner.owner_id, usersData, businessesData) : '';
    const ownerName = primaryOwner ? getOwnerName(primaryOwner.owner_type, primaryOwner.owner_id, usersData, businessesData) : '';
    const extraOwners = fOwners.length > 1 ? fOwners.length - 1 : 0;

    return (
      <div key={folder.folder_id} className="pr-folder-node">
        <div
          className={`pr-folder-row ${isActiveFilter ? 'pr-folder-active' : ''} ${dropTargetId === folder.folder_id ? 'pr-folder-droptarget' : ''} ${draggedFolderId === folder.folder_id ? 'pr-folder-dragging' : ''}`}
          draggable={renamingFolderId !== folder.folder_id && !folderMoving}
          onDragStart={(e) => handleFolderDragStart(e, folder.folder_id)}
          onDragOver={(e) => handleFolderDragOver(e, folder.folder_id)}
          onDragLeave={() => setDropTargetId(prev => (prev === folder.folder_id ? null : prev))}
          onDrop={(e) => handleFolderDrop(e, folder.folder_id)}
          onDragEnd={handleFolderDragEnd}
          title={draggedFolderId != null ? 'Drop here to move into this folder' : undefined}
        >
          {foldersOnly && (() => {
            const n = folderDocCounts.get(Number(folder.folder_id)) || 0;
            return (
              <span className={`pr-folder-doccount ${n ? '' : 'zero'}`}
                title={`${n} document${n === 1 ? '' : 's'} in this folder and its sub-folders`}>
                {n}
              </span>
            );
          })()}
          <span
            className={`pr-folder-expand ${isExpanded ? 'expanded' : ''}`}
            onClick={() => hasChildren && toggleFolder(folder.folder_id)}
          >
            {hasChildren ? <FiChevronRight /> : ''}
          </span>
          {isUmbrella ? (
            <span className="pr-folder-icon pr-folder-umbrella" title="Umbrella folder (groups sub-folders)">&#9748;</span>
          ) : Number((folder as any).is_shared) === 1 ? (
            <span className="pr-folder-nick pr-folder-nick-shared" title="Shared folder — everyone in this account">Shared</span>
          ) : ownerNick ? (
            <span
              className="pr-folder-nick"
              title={ownerName + (extraOwners ? ` +${extraOwners} more owner${extraOwners === 1 ? '' : 's'}` : '')}
            >
              {ownerNick}
            </span>
          ) : (
            <span className="pr-folder-icon">{emoji}</span>
          )}
          {renamingFolderId === folder.folder_id ? (
            <span className="pr-folder-rename">
              <input
                className="pr-folder-rename-input"
                value={renameValue}
                autoFocus
                disabled={folderBusy}
                onChange={e => setRenameValue(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  if (e.key === 'Enter') submitRename();
                  else if (e.key === 'Escape') setRenamingFolderId(null);
                }}
              />
              <button className="pr-folder-inline-btn save" onClick={submitRename} disabled={folderBusy} title="Save"><FiCheck size={13} /></button>
              <button className="pr-folder-inline-btn cancel" onClick={() => setRenamingFolderId(null)} title="Cancel"><FiX size={13} /></button>
            </span>
          ) : (
            <span
              className={`pr-folder-name pr-folder-name-clickable ${nameHasLongToken(folder.folder_name) ? 'pr-folder-name-fill' : ''}`}
              // Folder List (Masters → Folders): a click opens the folder for editing. In
              // Process Docs the same click filters the document list by the folder.
              onClick={() => (foldersOnly ? openManageFoldersFor('edit', folder.folder_id) : handleFolderFilter(folder.folder_id))}
              onDoubleClick={() => { if (!foldersOnly && hasChildren) toggleFolder(folder.folder_id); }}
              title={foldersOnly
                ? 'Click to edit this folder'
                : hasChildren ? 'Double-click to open / collapse this folder' : isActiveFilter ? 'Click to clear folder filter' : 'Click to filter documents by this folder'}
            >
              {folder.folder_name}
            </span>
          )}
          {foldersOnly && (
            <span className={`pr-folder-owner-col ${isUmbrella ? 'umbrella' : Number((folder as any).is_shared) === 1 ? 'shared' : ''}`}
              title={folderOwnerLabel(folder)}>
              {folderOwnerLabel(folder) || '—'}
            </span>
          )}
          <span className="pr-folder-menu-wrapper" ref={folderMenuOpenId === folder.folder_id ? folderMenuRef : undefined}>
            <button
              className="pr-folder-menu-btn"
              aria-expanded={folderMenuOpenId === folder.folder_id}
              onClick={(e) => { e.stopPropagation(); setFolderMenuOpenId(prev => prev === folder.folder_id ? null : folder.folder_id); }}
              title="Folder options"
            >
              <FiChevronDown size={16} />
            </button>
            {folderMenuOpenId === folder.folder_id && (
              <div className="pr-folder-context-menu">
                <button className="pr-folder-context-item" onClick={() => openManageFoldersFor('addSub', folder.folder_id)}>Add Sub Folder…</button>
                <button className="pr-folder-context-item" onClick={() => openManageFoldersFor('edit', folder.folder_id)}>Edit Folder</button>
                <button className="pr-folder-context-item" onClick={() => startRename(folder)}>Rename Folder</button>
                <button className="pr-folder-context-item danger" onClick={() => startDeleteFolder(folder.folder_id)}>Delete Folder</button>
                <div className="pr-folder-context-divider" />
                <button
                  className="pr-folder-context-item"
                  onClick={() => handleDownloadAllFiles(folder.folder_id, folder.folder_name)}
                  disabled={downloadingFolderId === folder.folder_id}
                >
                  {downloadingFolderId === folder.folder_id
                    ? <><span className="pr-ctx-spinner" /> Zipping...</>
                    : 'Download All Files'}
                </button>
                <button className="pr-folder-context-item" onClick={() => handleExportCsv(folder)}>Export To CSV</button>
                <button className="pr-folder-context-item" onClick={handlePushToDrive}>Push to Google Drive</button>
              </div>
            )}
          </span>
        </div>
        {confirmDeleteFolderId === folder.folder_id && (
          <div className="pr-folder-inline-confirm">
            <span className="pr-folder-inline-confirm-text">Delete "{folder.folder_name}" and its sub-folders?</span>
            <div className="pr-folder-inline-confirm-actions">
              <button className="pr-confirm-del-btn" onClick={confirmDeleteFolder} disabled={folderBusy}>{folderBusy ? 'Deleting…' : 'Delete'}</button>
              <button className="pr-confirm-cancel-btn" onClick={() => setConfirmDeleteFolderId(null)} disabled={folderBusy}>Cancel</button>
            </div>
          </div>
        )}
        {isExpanded && (
          <div className="pr-folder-children">
            {children.map(child => renderFolderNode(child))}
            {docsInFolder.map(doc => (
              <div
                key={doc.document_id}
                className="pr-folder-doc-item"
                role="button"
                tabIndex={foldersOnly ? 0 : undefined}
                // Folder List: open the document in Process Docs / Receipts
                onClick={() => (foldersOnly ? openDocFromFolderList(doc.document_id) : toggleSelectDoc(doc.document_id))}
                onKeyDown={e => { if (foldersOnly && e.key === 'Enter') openDocFromFolderList(doc.document_id); }}
                title={foldersOnly ? 'Open in Process Docs / Receipts' : undefined}
              >
                <FiFileText className="pr-folder-doc-icon" />
                <span className="pr-folder-name">{doc.filename}</span>
                {foldersOnly && doc.date_of_document && (
                  <span className="pr-folder-doc-date">{String(doc.date_of_document).slice(0, 10)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // =============================================
  // RENDER
  // =============================================
  if (!accountOwnerId) {
    return (
      <div className="pr-no-doc-msg">
        <FiAlertCircle style={{ fontSize: 32, marginBottom: 8 }} />
        <p>Please log in to process receipts.</p>
      </div>
    );
  }

  return (
    <div className={`pr-container ${foldersOnly ? 'pr-folders-only' : ''}`}>
      {/* ======== Record Room Sidebar ======== */}
      {(showRecordRoom || foldersOnly) && (
        <div className="pr-record-room">
          {/* The "Record Room (Folders)" caption is already on the toggle button
              beside this panel, so it's omitted here to free vertical space. */}
          {/* Owner scope — compact, multi-select; scopes BOTH the folder tree
              and the document list. One-line trigger opens a searchable list so
              the folder tree keeps its vertical space. Account owner picks any
              owner; a regular user is locked to their own folders. */}
          <div className="pr-folder-owner-select">
            {canPickOwners ? (
              <div className="pr-owner-picker" ref={ownerPickerRef}>
                <button
                  type="button"
                  className={`pr-owner-trigger ${ownerPickerOpen ? 'open' : ''}`}
                  aria-expanded={ownerPickerOpen}
                  onClick={() => setOwnerPickerOpen(o => !o)}
                  title="Choose which owners' folders to show"
                >
                  <span className="pr-owner-trigger-label">Owners</span>
                  <span className={`pr-owner-trigger-val ${selectedDocOwnerIds.length === 0 ? 'all' : ''}`}>
                    {selectedDocOwnerIds.length === 0
                      ? 'All'
                      : selectedDocOwnerIds.length === 1
                        ? (ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === selectedDocOwnerIds[0])?.display_name || '1 owner')
                        : `${selectedDocOwnerIds.length} owners`}
                  </span>
                  <FiChevronDown size={14} className={`pr-owner-trigger-caret ${ownerPickerOpen ? 'open' : ''}`} />
                </button>
                {ownerPickerOpen && (
                  <div className="pr-owner-pop">
                    <div className="pr-owner-pop-search">
                      <FiSearch size={12} />
                      <input
                        autoFocus
                        type="text"
                        value={ownerFilterText}
                        onChange={e => setOwnerFilterText(e.target.value)}
                        placeholder="Search owners…"
                      />
                      {selectedDocOwnerIds.length > 0 && (
                        <button
                          className="pr-owner-pop-clear"
                          onClick={() => { setSelectedDocOwnerIds([]); setSelectedDocId(null); }}
                          title="Show all owners"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="pr-owner-pop-list">
                      {(() => {
                        const q = ownerFilterText.trim().toLowerCase();
                        const list = q
                          ? pickableOwnerEntities.filter(e => e.display_name.toLowerCase().includes(q))
                          : pickableOwnerEntities;
                        if (list.length === 0) return <div className="pr-owner-pop-empty">No owners match.</div>;
                        return list.map(ent => {
                          const key = `${ent.owner_type}:${ent.owner_id}`;
                          const checked = selectedDocOwnerIds.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`pr-owner-chip ${checked ? 'checked' : ''}`}
                              aria-pressed={checked}
                              onClick={() => toggleOwner(key)}
                              title={checked ? 'Click to remove from view' : 'Click to add to view'}
                            >
                              <span className="pr-owner-chip-check">{checked && <FiCheck size={12} />}</span>
                              <span className="pr-owner-chip-name">{ent.display_name}</span>
                              <span className="pr-owner-chip-badge">{ent.badge}</span>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="pr-owner-self">Showing your folders</div>
            )}
            {/* New folder lives here as a compact "+" beside the Owners control. */}
            <button className="pr-owner-add-folder" onClick={openManageFolders} title="New folder">
              <FiPlus size={16} />
            </button>
            {/* Collapse the folder pane from its own top row too (the » on the document list
                brings it back). Not in the Folder List, where the folders ARE the screen. */}
            {!foldersOnly && (
              <button
                className="pr-owner-add-folder pr-rr-collapse-inline"
                onClick={() => setShowRecordRoom(false)}
                title="Hide Record Room (folders)"
                aria-label="Hide Record Room folders"
              >
                <FiChevronsLeft size={16} />
              </button>
            )}
          </div>
          <div className="pr-folder-search">
            <FiSearch size={13} />
            <input
              type="text"
              placeholder="Search folders..."
              value={folderSearchTerm}
              onChange={e => setFolderSearchTerm(e.target.value)}
              className="pr-folder-search-input"
            />
            {folderSearchTerm && (
              <button className="pr-folder-search-clear" onClick={() => setFolderSearchTerm('')}><FiX size={12} /></button>
            )}
          </div>
          <div className={`pr-folder-list-tools ${foldersOnly ? '' : 'pr-folder-list-tools-compact'}`}>
            <label className="pr-folder-sort">
              Sort by
              <select value={folderSort} onChange={e => setFolderSort(e.target.value as FolderSortKey)} aria-label="Sort folders by">
                <option value="name">Name</option>
                <option value="owner">Owner</option>
                <option value="umbrella">Umbrella first</option>
                <option value="shared">Shared first</option>
              </select>
            </label>
            {foldersOnly && (<>
              <span className="pr-folder-list-spacer" />
              <button type="button" className="pr-folder-tool-btn" onClick={expandAllFolders} title="Show every sub-folder, all levels">
                Expand all
              </button>
              <button type="button" className="pr-folder-tool-btn" onClick={collapseAllFolders} title="Show main-level folders only">
                Collapse all
              </button>
            </>)}
          </div>
          {foldersOnly && rootFolders.length > 0 && (
            <div className="pr-folder-list-head" role="row">
              <span className="pr-folder-list-head-docs" role="columnheader" title="Documents in the folder and its sub-folders">Docs</span>
              <span className="pr-folder-list-head-name" role="columnheader">Folder name</span>
              <span className="pr-folder-list-head-owner" role="columnheader">Folder owner</span>
            </div>
          )}
          <div className="pr-folder-tree">
            {rootFolders.length === 0 ? (
              <div className="pr-doc-list-empty">
                {scopedOwnerKeys.length > 0 ? 'No folders for the selected owner(s).' : 'No folders yet'}
              </div>
            ) : (
              rootFolders.map(f => renderFolderNode(f))
            )}
          </div>
        </div>
      )}

      {/* In folders-only mode (DMS → Folders → Folder List) the doc list / preview /
          forms are hidden — only the Record Room above is shown, full width. */}
      {!foldersOnly && (<>
      {/* ======== Left Panel — Doc List ======== */}
      <div
        className={`pr-left-panel ${showDocList ? '' : 'pr-left-collapsed'}`}
        style={showDocList ? { width: docListWidth, minWidth: docListWidth, maxWidth: docListWidth } : undefined}
      >
        {!showDocList ? (
          <button
            className="pr-doclist-expand"
            onClick={() => setShowDocList(true)}
            title="Show document list"
            aria-label="Show document list"
          >
            <FiChevronsRight size={16} />
            <span className="pr-doclist-expand-label">Documents</span>
          </button>
        ) : (<>
        {/* Owner scope lives in the Record Room multi-select. When the Record
            Room is collapsed, show a compact summary chip here so the active
            owner filter stays visible and clearable. */}

        {/* Active filter chips */}
        {((!showRecordRoom && canPickOwners && selectedDocOwnerIds.length > 0) || selectedRecordFolderId !== null) && (
          <div className="pr-active-filters">
            {!showRecordRoom && canPickOwners && selectedDocOwnerIds.length > 0 && (
              <span className="pr-filter-chip">
                <FiFolder size={12} />
                {selectedDocOwnerIds.length === 1
                  ? (ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === selectedDocOwnerIds[0])?.display_name || 'Owner')
                  : `${selectedDocOwnerIds.length} owners`}
                <button className="pr-filter-chip-clear" onClick={() => { setSelectedDocOwnerIds([]); setSelectedDocId(null); }} title="Clear owner filter"><FiX size={12} /></button>
              </span>
            )}
            {selectedRecordFolderId !== null && (
              <span className="pr-filter-chip">
                <FiFolder size={12} />
                {folders.find(f => f.folder_id === selectedRecordFolderId)?.folder_name || 'Folder'}
                <button className="pr-filter-chip-clear" onClick={() => { setSelectedRecordFolderId(null); setSelectedDocId(null); }} title="Clear folder filter"><FiX size={12} /></button>
              </span>
            )}
          </div>
        )}

        {/* Filter tabs — with a compact Record Room (folders) collapse toggle on the far left */}
        <div className="pr-filter-tabs">
          <button
            className={`pr-rr-collapse${!showRecordRoom ? ' engaged' : ''}`}
            onClick={() => setShowRecordRoom(s => !s)}
            title={showRecordRoom ? 'Hide Record Room (folders)' : 'Show Record Room (folders)'}
            aria-label="Toggle Record Room folders"
          >
            {showRecordRoom ? <FiChevronsLeft size={15} /> : <FiChevronsRight size={15} />}
          </button>
          {/* BK Done is a bookkeeping state — hidden in the DMS pillar (no bookkeeping),
              but kept under My Accounts / Client's Accounts where filing → bookkeeping happens. */}
          {FILTER_TABS.filter(tab => showBookkeeping || tab.key !== 'bk-done').map(tab => (
            <button
              key={tab.key}
              className={`pr-filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
              onClick={() => { setActiveFilter(tab.key); selectFirstOnTabRef.current = tab.key; setTabClickNonce(n => n + 1); }}
              title={`${tab.label} (${filterCounts[tab.key]})`}
            >
              {/* Two lines — the name, then the count — so six tabs fit the narrow list pane */}
              <span className="pr-filter-label">{tab.label}</span>
              <span className="pr-filter-count">({filterCounts[tab.key]})</span>
            </button>
          ))}
          {/* Collapse the whole document list pane → more room for the preview */}
          <button
            className="pr-doclist-collapse"
            onClick={() => setShowDocList(false)}
            title="Collapse the document list (more room for the preview)"
            aria-label="Collapse document list"
          >
            <FiChevronsLeft size={15} />
          </button>
        </div>

        {/* Search (≈75%) + Sort chevron (≈15%) on one line */}
        <div className="pr-list-controls">
          <div className="pr-search-wrapper">
            <FiSearch className="pr-search-icon" />
            <input
              type="text"
              className="pr-search-input"
              placeholder="Search name, vendor, amount, ref, tag, text…"
              title="Finds a document by its name, tags, vendor (or alias), amount, reference, date (2025-10), type, folder, owner, notes, or words printed on it. Every word must match; quotes keep a phrase together."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button type="button" className="pr-search-clear" onClick={() => setSearchTerm('')} title="Clear search" aria-label="Clear search">
                <FiX size={14} />
              </button>
            )}
          </div>
          <div className="pr-sort-picker" ref={sortPickerRef}>
            <button
              type="button"
              className={`pr-sort-trigger ${sortOpen ? 'open' : ''}`}
              aria-expanded={sortOpen}
              aria-haspopup="listbox"
              onClick={() => setSortOpen(o => !o)}
              title={`Sort by: ${SORT_OPTIONS.find(o => o.value === sortKey)?.label || ''}`}
            >
              <FiChevronDown size={16} className={`pr-sort-caret ${sortOpen ? 'open' : ''}`} />
            </button>
            {sortOpen && (
              <div className="pr-sort-pop" role="listbox" aria-label="Sort documents by">
                <div className="pr-sort-pop-head">Sort by</div>
                {SORT_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={sortKey === o.value}
                    className={`pr-sort-opt ${sortKey === o.value ? 'active' : ''}`}
                    onClick={() => { setSortKey(o.value); setSortOpen(false); }}
                  >
                    <FiCheck size={13} className="pr-sort-opt-check" />
                    <span>{o.label}{o.value === 'pending-approval' && pendingApprovalCount > 0 ? ` (${pendingApprovalCount})` : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Document list */}
        <div
          className="pr-doc-list"
          ref={docListRef}
          tabIndex={0}
          onKeyDown={handleListKeyDown}
          role="listbox"
          aria-label="Documents — use up and down arrows to navigate, Tab to view"
        >
          {filteredDocs.length === 0 ? (
            <div className="pr-doc-list-empty">
              {documents.filter(d => d.is_deleted !== 1).length === 0
                ? 'No documents uploaded yet. Upload documents first.'
                : 'No documents match this filter.'
              }
            </div>
          ) : (
            filteredDocs.map(doc => {
              // Owner badge reflects the CHOSEN owner only (owner_type/owner_id). If no
              // owner was picked at upload, leave it blank — do NOT fall back to the
              // uploader's identity (user_id), since uploader ≠ owner.
              const docNick = doc.owner_type && doc.owner_id
                ? getOwnerNickname(doc.owner_type, doc.owner_id, usersData, businessesData)
                : '';
              const isPerm = Number(doc.is_permanent) === 1;
              // Filed = the doc has a real folder anywhere — its primary folder OR any
              // shared owner's folder (dms_document_owners). Treat null / '' / 0 as none.
              const sharedFolderRows = docOwnersByDoc.get(doc.document_id) || [];
              const allFolderIds = [doc.dms_folders_id, ...sharedFolderRows.map(r => r.folder_id)]
                .filter(x => x != null && Number(x) !== 0)
                .map(Number);
              const isUnfiled = allFolderIds.length === 0;
              const folderNameOf = (id: number) =>
                folders.find(f => Number(f.folder_id) === id)?.folder_name || 'Unknown folder';
              // Prefer the folder that put this doc in the currently-filtered subtree,
              // else its primary folder, else the first shared folder.
              let shownFolderId: number | null = doc.dms_folders_id != null && Number(doc.dms_folders_id) !== 0
                ? Number(doc.dms_folders_id)
                : (allFolderIds[0] ?? null);
              if (selectedRecordFolderId !== null) {
                const sub = getFolderAndDescendantIds(selectedRecordFolderId);
                const hit = allFolderIds.find(id => sub.has(id));
                if (hit != null) shownFolderId = hit;
              }
              const docFolderName = shownFolderId != null ? folderNameOf(shownFolderId) : '';
              const extraFolderCount = allFolderIds.filter(id => id !== shownFolderId).length;
              const dup = dupInfo.get(doc.document_id);
              const isDupCopy = !!dup && !dup.isOriginal;
              // In the Duplicates sort, nest copies under their original (indent +
              // accent) so a group reads as one block even with different filenames.
              const dupRowClass = (sortKey === 'duplicates' || activeFilter === 'duplicates') && dup
                ? (dup.isOriginal ? 'pr-dup-orig-row' : 'pr-dup-copy-row')
                : '';
              return (
                <div
                  key={doc.document_id}
                  data-doc-id={doc.document_id}
                  role="option"
                  aria-selected={selectedDocId === doc.document_id}
                  className={`pr-doc-item ${selectedDocId === doc.document_id ? 'selected' : ''} ${dupRowClass}`}
                  onClick={() => toggleSelectDoc(doc.document_id)}
                >
                  {getStatusIcon(doc.status)}
                  <div className="pr-doc-info">
                    <div className="pr-doc-filename">
                      {/* Before the name, so a long filename's ellipsis can't hide it */}
                      {draftDocIds.has(doc.document_id) && (
                        <span className="pr-dup-badge pr-draft-badge" title="You have unsaved Filing changes on this document">
                          Draft
                        </span>
                      )}
                      {dup && (dup.isOriginal ? (
                        <span className="pr-dup-badge pr-dup-lead pr-dup-original" title="Original — other copies of this document exist">
                          Original
                        </span>
                      ) : (
                        <span className="pr-dup-badge pr-dup-lead pr-dup-copy" title={`Possible duplicate — ${dup.matchPct}% match with the original`}>
                          Dup {dup.matchPct}%
                        </span>
                      ))}
                      <span className={isDupCopy ? 'pr-dup-copy-name' : ''}>{doc.filename}</span>
                    </div>
                    {searchTerm.trim() && textHits.has(doc.document_id) && (
                      <div className="pr-doc-texthit" title="Found in the words printed on this document">
                        <FiSearch size={11} /> …{textHits.get(doc.document_id)}…
                      </div>
                    )}
                    {/* The document date — the handwritten one where it was read */}
                    {doc.date_of_document && (
                      <div
                        className="pr-doc-date"
                        title={String(doc.date_source || '').startsWith('handwritten')
                          ? 'Document date — as written on the document by hand'
                          : 'Document date'}
                      >
                        {String(doc.date_source || '').startsWith('handwritten') ? '✍ ' : ''}
                        {String(doc.date_of_document).slice(0, 10)}
                      </div>
                    )}
                    {/* Owner nickname + the folder it's filed in (blank when unfiled) */}
                    {(docNick || !isUnfiled) && (
                      <div className="pr-doc-owner-label">
                        {docNick && <span className="pr-doc-nick">{docNick}</span>}
                        {docNick && !isUnfiled && <span className="pr-doc-folder-sep">|</span>}
                        {!isUnfiled && (
                          <span className="pr-doc-folder-name">
                            {docFolderName}{extraFolderCount > 0 ? ` +${extraFolderCount}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {doc.tags && (
                      <div className="pr-doc-tags">
                        <span className="ti-row-tags">
                          {parseTags(doc.tags).map(t => <span key={t} className="ti-row-tag">{t}</span>)}
                        </span>
                      </div>
                    )}
                    {showBookkeeping && txnCountByDoc.get(doc.document_id) ? (
                      <div className="pr-doc-txn-badge">💳 {txnCountByDoc.get(doc.document_id)} txns</div>
                    ) : null}
                  </div>
                  {/* Per-row quick actions (hover) */}
                  <div className="pr-doc-row-actions">
                    <button
                      className={`pr-doc-row-btn ${isPerm ? 'pr-star-on' : ''}`}
                      title={isPerm ? 'Permanent — click to unmark' : 'Mark permanent (never auto-delete)'}
                      onClick={(e) => { e.stopPropagation(); handleTogglePermanent(doc); }}
                    >
                      <FiStar size={13} style={isPerm ? { fill: 'currentColor' } : undefined} />
                    </button>
                    <button
                      className="pr-doc-row-btn"
                      title="Move to another folder"
                      onClick={(e) => { e.stopPropagation(); setSelectedDocId(doc.document_id); setShowDocMove(true); }}
                    >
                      <FiFolderPlus size={13} />
                    </button>
                    <button
                      className="pr-doc-row-btn pr-doc-row-danger"
                      title="Delete document"
                      onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc); }}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        </>)}
        {/* Drag the right edge to widen/narrow the doc list; double-click to reset. */}
        {showDocList && (
          <div
            className="pr-doclist-resizer"
            onMouseDown={onDocListResizeStart}
            onDoubleClick={resetDocListWidth}
            title="Drag to resize the document list (double-click to reset)"
            role="separator"
            aria-orientation="vertical"
          />
        )}
      </div>

      {/* ======== Center Panel — Preview ======== */}
      <div
        className="pr-center-panel"
        ref={centerPaneRef}
        tabIndex={0}
        onKeyDown={handleViewerKeyDown}
        aria-label="Document viewer — left/right arrows change document, up/down and Page keys scroll"
      >
        {successMsg && <div className="pr-success-msg">{successMsg}</div>}
        {errorMsg && (
          <div className="pr-error-msg">
            {errorMsg}
            <button className="pr-error-msg-close" onClick={() => setErrorMsg('')} title="Dismiss">✕</button>
          </div>
        )}
        {extracting && (
          <div className="pr-extracting-banner">
            <span className="pr-extracting-spinner" /> Reading document &amp; auto-filling fields...
          </div>
        )}

        {/* Duplicate flag for the open document */}
        {selectedDoc && (() => {
          const dup = dupInfo.get(selectedDoc.document_id);
          if (!dup) return null;
          const groupCount = Array.from(dupInfo.values()).filter(g => g.groupId === dup.groupId).length;
          const notDupControl = notDupConfirmFor === selectedDoc.document_id ? (
            <span className="pr-dup-confirm" role="group" aria-label="Confirm not a duplicate">
              <span>Are these different documents?</span>
              <button type="button" className="pr-dup-view-original pr-dup-confirm-yes" disabled={notDupSaving}
                onClick={() => confirmNotDuplicate(selectedDoc.document_id, dup.groupId)}>
                {notDupSaving ? 'Saving…' : 'Yes, not a duplicate'}
              </button>
              <button type="button" className="pr-dup-view-original" disabled={notDupSaving}
                onClick={() => setNotDupConfirmFor(null)}>Cancel</button>
            </span>
          ) : (
            <button type="button" className="pr-dup-view-original"
              title="These documents only look alike — confirm they are different and stop flagging them"
              onClick={() => setNotDupConfirmFor(selectedDoc.document_id)}>
              Not a duplicate
            </button>
          );
          if (dup.isOriginal) {
            return (
              <div className="pr-dup-banner pr-dup-banner-original">
                <span className="pr-dup-banner-icon">⧉</span>
                <span>
                  This is the <strong>original</strong> — {groupCount - 1} other{groupCount - 1 !== 1 ? ' copies' : ' copy'} of this
                  document {groupCount - 1 !== 1 ? 'were' : 'was'} uploaded. Use the <em>Duplicates (grouped)</em> sort to see them all.
                </span>
                {notDupControl}
              </div>
            );
          }
          const original = documents.find(d => d.document_id === dup.originalId);
          return (
            <div className="pr-dup-banner pr-dup-banner-copy">
              <span className="pr-dup-banner-icon">⚠️</span>
              <span>
                Possibly this is a <strong>duplicate</strong>. Sort by <em>Duplicate (Grouped)</em> to see both together.
              </span>
              {original && (
                <button
                  type="button"
                  className="pr-dup-view-original"
                  onClick={() => setSelectedDocId(dup.originalId as number)}
                >
                  View original
                </button>
              )}
              {notDupControl}
            </div>
          );
        })()}

        <div className="pr-preview-nav">
          {/* Left zone: PDF view controls (merged here so there's one toolbar row). */}
          <div className="pr-nav-viewer">
            {pdfBytes && selectedDoc && (
              <>
                <button
                  className="pr-zoom-icon-btn"
                  title="Zoom out"
                  onClick={() => setPdfZoom(z => Math.max(0.25, +(z - 0.15).toFixed(2)))}
                >
                  <FiZoomOut size={15} />
                </button>
                <div className="pr-vmode-dd" ref={pdfModeRef}>
                  <button
                    className="pr-vmode-trigger"
                    onClick={() => setPdfModeMenuOpen(o => !o)}
                    title="Page view"
                    aria-haspopup="listbox"
                    aria-expanded={pdfModeMenuOpen}
                  >
                    <span className="pr-vmode-trigger-label">
                      {(VIEW_MODES.find(m => m.key === pdfViewMode) || VIEW_MODES[0]).label}
                    </span>
                    <FiChevronDown size={14} className={`pr-vmode-caret ${pdfModeMenuOpen ? 'open' : ''}`} />
                  </button>
                  {pdfModeMenuOpen && (
                    <div className="pr-vmode-menu" role="listbox" aria-label="Page view">
                      {VIEW_MODES.map(m => (
                        <button
                          key={m.key}
                          role="option"
                          aria-selected={pdfViewMode === m.key}
                          className={`pr-vmode-item ${pdfViewMode === m.key ? 'active' : ''}`}
                          title={m.title}
                          onClick={() => pickPdfMode(m.key)}
                        >
                          <FiCheck size={12} className="pr-vmode-item-check" />
                          {m.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="pr-zoom-icon-btn"
                  title="Zoom in"
                  onClick={() => setPdfZoom(z => Math.min(4, +(z + 0.15).toFixed(2)))}
                >
                  <FiZoomIn size={15} />
                </button>
                {pdfNumPages > 1 && (
                  <span className="pr-pdf-pageinfo">Pg {pdfCurrentPage}/{pdfNumPages}</span>
                )}
                <div className="pr-vmode-dd" ref={pdfRotateRef}>
                  <button
                    className="pr-zoom-icon-btn"
                    title={pdfRotating ? 'Rotating…' : 'Rotate pages'}
                    aria-haspopup="menu"
                    aria-expanded={pdfRotateMenuOpen}
                    disabled={pdfRotating}
                    onClick={() => setPdfRotateMenuOpen(o => !o)}
                  >
                    <FiRotateCw size={15} className={pdfRotating ? 'pr-rotate-busy' : undefined} />
                  </button>
                  {pdfRotateMenuOpen && (
                    <div className="pr-vmode-menu pr-rotate-menu" role="menu" aria-label="Rotate pages">
                      {pdfNumPages > 1 && <div className="pr-rotate-head">Page {pdfCurrentPage}</div>}
                      <button role="menuitem" className="pr-vmode-item" onClick={() => rotateDocPages('page', 90)}>
                        <FiRotateCw size={12} /> Rotate right 90°
                      </button>
                      <button role="menuitem" className="pr-vmode-item" onClick={() => rotateDocPages('page', -90)}>
                        <FiRotateCcw size={12} /> Rotate left 90°
                      </button>
                      <button role="menuitem" className="pr-vmode-item" onClick={() => rotateDocPages('page', 180)}>
                        <FiRotateCw size={12} /> Rotate 180°
                      </button>
                      {pdfNumPages > 1 && (
                        <>
                          <div className="pr-rotate-head">All {pdfNumPages} pages</div>
                          <button role="menuitem" className="pr-vmode-item" onClick={() => rotateDocPages('all', 90)}>
                            <FiRotateCw size={12} /> Rotate right 90°
                          </button>
                          <button role="menuitem" className="pr-vmode-item" onClick={() => rotateDocPages('all', -90)}>
                            <FiRotateCcw size={12} /> Rotate left 90°
                          </button>
                          <button role="menuitem" className="pr-vmode-item" onClick={() => rotateDocPages('all', 180)}>
                            <FiRotateCw size={12} /> Rotate 180°
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button
                  className="pr-zoom-icon-btn"
                  disabled={!pageEditAllowed || pdfNumPages < 2 || pageEditBusy}
                  title={!pageEditAllowed ? 'Only documents not filed yet can be separated'
                    : pdfNumPages < 2 ? 'This document has one page' : 'Separate pages into documents'}
                  onClick={() => setSplitOpen(true)}
                >
                  <FiScissors size={15} />
                </button>
                <button
                  className="pr-zoom-icon-btn"
                  disabled={!pageEditAllowed || pageEditBusy}
                  title={pageEditAllowed ? 'Combine with other documents' : 'Only documents not filed yet can be combined'}
                  onClick={() => setCombineOpen(true)}
                >
                  <FiLayers size={15} />
                </button>
              </>
            )}
          </div>
          <div className="pr-nav-pager">
            {(() => {
              if (activeFilter !== 'to-file') return null;
              const backIds = viewHistory.filter(id => id !== selectedDocId);
              const prevDoc = backIds.length ? documents.find(d => d.document_id === backIds[backIds.length - 1]) : undefined;
              // eslint-disable-next-line eqeqeq
              if (!prevDoc || prevDoc.is_deleted == 1) return null;
              return (
                <button
                  className="pr-nav-back-link"
                  onClick={() => {
                    goingBackRef.current = true;
                    setViewHistory(h => h.slice(0, h.lastIndexOf(prevDoc.document_id)));
                    setSelectedDocId(prevDoc.document_id);
                  }}
                  title={`Open the document you looked at before: "${prevDoc.filename}"${isDateVerified(prevDoc) ? ' (now in Date Verified — untick Date verified to bring it back here)' : ''}`}
                >
                  <FiCornerUpLeft size={13} /> Prev
                </button>
              );
            })()}
            <button
              className="pr-nav-btn"
              disabled={currentIndex <= 0}
              onClick={() => navigateDoc(-1)}
              title="Previous document (Alt+P)"
            >
              &lt;&lt;
            </button>
            <button
              className="pr-nav-btn"
              disabled={currentIndex >= filteredDocs.length - 1}
              onClick={() => navigateDoc(1)}
              title="Next document (Alt+N)"
            >
              &gt;&gt;
            </button>
          </div>
          <div className="pr-nav-doc-actions">
            <button
              className="pr-nav-icon-btn"
              disabled={!pdfBytes || !selectedDoc}
              onClick={handleDownloadFile}
              title="Download this file"
            >
              <FiDownload size={15} />
            </button>
            <button
              className="pr-nav-icon-btn"
              disabled={!pdfBytes}
              onClick={handleOpenInNewTab}
              title="Open in new tab"
            >
              <FiExternalLink size={15} />
            </button>

            {/* ★ Permanent — never auto-delete */}
            <button
              className={`pr-nav-icon-btn ${Number(selectedDoc?.is_permanent) === 1 ? 'pr-star-on' : ''}`}
              disabled={!selectedDoc}
              onClick={() => handleTogglePermanent()}
              title={Number(selectedDoc?.is_permanent) === 1 ? 'Permanent (never auto-deleted) — click to unmark' : 'Mark permanent — never auto-delete'}
            >
              <FiStar size={15} style={Number(selectedDoc?.is_permanent) === 1 ? { fill: 'currentColor' } : undefined} />
            </button>

            {/* Move-to-folder popover anchor (the toolbar button was removed; the
                row-hover Move button in the doc list opens this same popover). */}
            <div className="pr-doc-move-wrap" ref={docMoveRef}>
              {showDocMove && selectedDoc && (
                <div className="pr-doc-move-pop">
                  <div className="pr-doc-move-search">
                    <FiSearch size={12} />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Move to folder…"
                      value={docMoveSearch}
                      onChange={e => setDocMoveSearch(e.target.value)}
                    />
                  </div>
                  <div className="pr-doc-move-list">
                    {(() => {
                      const q = docMoveSearch.trim().toLowerCase();
                      const targets = ownerFolders
                        .filter(f => Number(f.umbrella_folder) !== 1 && f.folder_id !== selectedDoc.dms_folders_id)
                        .filter(f => !q || f.folder_name.toLowerCase().includes(q))
                        .slice(0, 50);
                      if (targets.length === 0) return <div className="pr-doc-move-empty">No matching folders.</div>;
                      return targets.map(f => (
                        <button key={f.folder_id} className="pr-doc-move-item" onClick={() => handleMoveDoc(f.folder_id)}>
                          {f.folder_name}
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Delete (soft-delete) */}
            <button
              className="pr-nav-icon-btn pr-nav-icon-danger"
              disabled={!selectedDoc}
              onClick={() => handleDeleteDoc()}
              title="Delete this document"
            >
              <FiTrash2 size={15} />
            </button>
          </div>
        </div>

        <div className="pr-preview-area">
          {!selectedDoc ? (
            <div className="pr-preview-placeholder">
              <FiFile style={{ fontSize: 48, color: 'var(--gray-300)' }} />
              <p>Select a document from the list to preview</p>
            </div>
          ) : pdfLoading ? (
            <div className="pr-preview-placeholder">
              <p>Loading preview...</p>
            </div>
          ) : pdfBytes ? (
            <PdfCanvasViewer
              pdfBytes={pdfBytes}
              title={selectedDoc.filename}
              viewMode={pdfViewMode}
              zoom={pdfZoom}
              onNumPages={setPdfNumPages}
              onCurrentPageChange={setPdfCurrentPage}
              openAtPage={pdfOpenAtPage}
            />
          ) : (
            <div className="pr-preview-fallback">
              <div className="pr-preview-fallback-icon">
                <FiFileText />
              </div>
              <p><strong>{selectedDoc.filename}</strong></p>
              <p>{selectedDoc.page_count ? `${selectedDoc.page_count} page(s)` : 'PDF document'}</p>
            </div>
          )}
        </div>
      </div>

      {/* ======== Right Panel — Filing / Bookkeeping ======== */}
      <div className={`pr-right-panel ${showRightPanel ? '' : 'pr-right-collapsed'}`}>
        {/* Collapsed → a slim strip to re-open (mirrors the doc-list collapse). */}
        <button
          className="pr-right-expand"
          onClick={() => setShowRightPanel(true)}
          title="Show Filing panel"
          aria-label="Show Filing panel"
        >
          <FiChevronsLeft size={16} />
          <span className="pr-right-expand-label">Filing</span>
        </button>
        <div className="pr-right-tabs">
          <button
            className={`pr-right-collapse${!showRightPanel ? ' engaged' : ''}`}
            onClick={() => setShowRightPanel(false)}
            title="Collapse Filing panel"
            aria-label="Collapse Filing panel"
          >
            <FiChevronsRight size={15} />
          </button>
          <button
            className={`pr-right-tab ${rightTab === 'filing' ? 'active' : ''}`}
            onClick={() => switchRightTab('filing')}
          >
            Filing
          </button>
          {showBookkeeping && (
            <button
              className={`pr-right-tab ${rightTab === 'bookkeeping' ? 'active' : ''}`}
              onClick={() => switchRightTab('bookkeeping')}
            >
              Bookkeeping
            </button>
          )}
          {showBookkeeping && (
            <button
              className={`pr-right-tab ${rightTab === 'makejournal' ? 'active' : ''}`}
              onClick={() => switchRightTab('makejournal')}
            >
              Make Journal Entries
            </button>
          )}
          {showBookkeeping && docBankTxns.length > 0 && (
            <button
              className={`pr-right-tab ${rightTab === 'banktxn' ? 'active' : ''}`}
              onClick={() => switchRightTab('banktxn')}
            >
              Bank Txns <span className="pr-tab-badge">{docBankTxns.length}</span>
            </button>
          )}
        </div>

        {(showBookkeeping && rightTab === 'makejournal') ? (
          /* ---- Make Journal Entries (manual double-entry) ---- */
          <JournalEntriesTab
            viewMode={viewMode}
            selectedClientId={selectedClientId}
            documentId={selectedDoc ? selectedDoc.document_id : null}
            defaultOwnerKey={selectedDoc && selectedDoc.owner_type && selectedDoc.owner_id != null
              ? `${selectedDoc.owner_type}:${selectedDoc.owner_id}` : null}
          />
        ) : (showBookkeeping && rightTab === 'banktxn' && docBankTxns.length > 0) ? (
          /* ---- Bank Transactions (line-items from a statement) ---- */
          <>
            <div className="pr-banktxn-head">
              <div className="pr-banktxn-title">Bank Transactions</div>
              <div className="pr-banktxn-sub">
                {docBankTxns.length} line-item{docBankTxns.length === 1 ? '' : 's'} from this statement — assign a GL account to each, then post to the ledger.
              </div>
              <button type="button" className="pr-banktxn-bulk" onClick={markAllTxnsReviewed}>
                Mark all reviewed
              </button>
            </div>
            {/* Bank/cash account for this statement — the offsetting side of every posted txn */}
            <div className="pr-banktxn-post-bar">
              <label className="pr-banktxn-post-label">Bank / Cash account</label>
              <SearchableDropdown
                className="pr-banktxn-bankacct"
                options={coaOptions}
                value={bankAccountCoaId}
                onChange={(v: string) => setBankAccountCoaId(v)}
                placeholder={coaOptions.length ? 'Choose the statement’s bank account…' : 'No GL accounts yet'}
              />
              <button
                className="pr-submit-btn publish pr-banktxn-post-btn"
                disabled={txnBusy || !bankAccountCoaId}
                onClick={handlePostToGl}
                title={!bankAccountCoaId ? 'Pick the bank/cash account first' : 'Create GL journal entries for mapped, unposted transactions'}
              >
                {txnBusy ? 'Posting…' : 'Post to GL'}
              </button>
            </div>
            <div className="pr-banktxn-list">
              {docBankTxns.map(t => {
                const draft = txnDrafts.find(d => d.txn_id === t.txn_id);
                const amt = t.amount == null ? 0 : Number(t.amount);
                const isPosted = postedTxnIds.has(t.txn_id) || t.status === 'posted';
                return (
                  <div key={t.txn_id} className={`pr-banktxn-card ${t.direction} ${isPosted ? 'posted' : ''}`}>
                    <div className="pr-banktxn-row1">
                      <span className="pr-banktxn-date">{(t.txn_date || '').slice(0, 10) || '—'}</span>
                      {isPosted && <span className="pr-banktxn-posted-badge">Posted ✓</span>}
                      <span className={`pr-banktxn-amt ${t.direction}`}>
                        {t.direction === 'debit' ? '−' : '+'}{amt.toFixed(2)} {t.currency || ''}
                      </span>
                    </div>
                    <div className="pr-banktxn-desc" title={t.description || ''}>
                      {t.description || '(no description)'}
                    </div>
                    <div className="pr-banktxn-fields">
                      <SearchableDropdown
                        className="pr-banktxn-coa"
                        options={coaOptions}
                        value={draft?.coa_id != null ? String(draft.coa_id) : ''}
                        onChange={(v: string) => updateTxnDraft(t.txn_id, { coa_id: v ? Number(v) : null })}
                        placeholder={coaOptions.length ? 'Map to GL account…' : 'No GL accounts yet — add some in Chart of Accounts'}
                        disabled={isPosted}
                      />
                      {txnProfitCenters.hasAny && (
                        <SearchableDropdown
                          className="pr-banktxn-coa"
                          options={[{ value: '', label: '— No profit center —' }, ...txnProfitCenters.options]}
                          value={draft?.pc || ''}
                          onChange={(v: string) => updateTxnDraft(t.txn_id, { pc: v })}
                          placeholder="Profit center (optional)"
                          disabled={isPosted}
                        />
                      )}
                      <div className="pr-banktxn-field-row">
                        <input
                          type="text"
                          className="pr-form-input"
                          placeholder="Category"
                          value={draft?.category || ''}
                          onChange={e => updateTxnDraft(t.txn_id, { category: e.target.value })}
                          disabled={isPosted}
                        />
                        {isPosted ? (
                          <span className="pr-form-input pr-banktxn-status pr-banktxn-status-locked">posted</span>
                        ) : (
                          <select
                            className="pr-form-input pr-banktxn-status"
                            value={draft?.status || 'imported'}
                            onChange={e => updateTxnDraft(t.txn_id, { status: e.target.value })}
                          >
                            {TXN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pr-form-actions">
              <button
                className="pr-submit-btn publish"
                disabled={txnBusy}
                onClick={handleSaveBankTxns}
              >
                {txnBusy ? 'Saving…' : 'Save assignments'}
              </button>
            </div>
          </>
        ) : (!showBookkeeping || rightTab === 'filing') ? (
          /* ---- Filing Form ---- */
          <>
            <div className="pr-right-form pr-form-outlined">
              {selectedDoc && draftDocIds.has(selectedDoc.document_id) && (
                <div className="pr-draft-note" role="status">
                  <span>Unsaved changes are kept as a draft until you file this document.</span>
                </div>
              )}
              {/* Owner(s) Of Document — MULTI-select. Click one or more owners; the doc
                  is co-owned by all of them (selected pinned to the top, ✓). */}
              <div className="pr-form-group">
                <label className="pr-form-label">
                  Owner(s) Of Document <span className="required">*</span>
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  multiSelect
                  options={ownerSelectOptions}
                  selectedValues={filingOwnerKeys}
                  onToggleValue={toggleFilingOwner}
                  closeOnSelect
                  placeholder="Select owner(s) — click one or more"
                />
                {filingFolderIds.length > 0 && filingOwnerKeys.length === 0 && (
                  <p className="pr-owner-gate-hint">
                    Choose at least one document owner to enable filing.
                  </p>
                )}
              </div>

              {/* Select folder(s) — MULTI-select. Click one or more folders; the doc
                  is filed in every selected folder (selected pinned to the top, ✓). */}
              <div className="pr-form-group">
                <label className="pr-form-label">
                  Select folder(s) <span className="required">*</span>
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  multiSelect
                  options={folderSelectOptions}
                  selectedValues={filingFolderIds.map(String)}
                  onToggleValue={(v: string) => toggleFilingFolder(Number(v))}
                  closeOnSelect
                  placeholder="Select folder(s) — click one or more"
                  actionRow={{
                    label: <><FiFolderPlus size={15} /> Add new folder…</>,
                    onClick: openManageFoldersFromFiling,
                  }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">
                  Date of document
                  {filingDateAmbiguous && (
                    <span className="pr-ai-suggested" title={`Ambiguous printed date "${filingRawDate}" — pick the correct reading`}>✨ confirm date</span>
                  )}
                  {filingDateOrigin === 'handwritten' && (
                    <span className="pr-ai-suggested pr-date-origin-check" title="Read from the date written on the document by hand. Handwriting is easy to misread — please check it.">✍ handwritten — check</span>
                  )}
                  {filingDateOrigin === 'printed' && (
                    <span className="pr-date-origin" title="Read from the date printed on the document">printed</span>
                  )}
                  {filingDateOrigin === 'filename' && (
                    <span className="pr-date-origin" title="No date was found on the document — this one comes from the file name">from file name</span>
                  )}
                </label>
                {/* Date, then the two ticks — Tab: date -> Date verified -> Undated -> next field;
                    Space ticks. Both save at once (no Submit needed). */}
                <div className="pr-date-row">
                  <input
                    ref={filingDateInputRef}
                    type="date"
                    max="9999-12-31"   /* 4-digit year: the browser then jumps to the month by itself after the 4th digit */
                    className="pr-form-input date-highlight pr-date-input"
                    value={filingDate}
                    disabled={filingUndated}
                    title={filingUndated ? 'Marked Undated — untick Undated to enter a date' : undefined}
                    onChange={e => { filingDateTypedRef.current = true; setFilingDate(e.target.value); setFilingDateAmbiguous(false); setFilingDateChoice('user'); setFormDirty(true); }}
                    onKeyDown={e => {
                      // Chrome's Tab walks the year / month / day parts of a date box; here Tab
                      // leaves the field for the Date verified tick (typing still moves between parts).
                      if (e.key === 'Tab' && !e.shiftKey && dateVerifiedTickRef.current) {
                        e.preventDefault();
                        dateVerifiedTickRef.current.focus();
                      }
                    }}
                  />
                  {selectedDoc && (
                    <>
                      <label className="pr-date-tick" title={isDateVerified(selectedDoc)
                        ? `Date checked ${String(selectedDoc.date_verified_at).slice(0, 10)}${selectedDoc.status === 'Review' ? ' — untick to move it back to Verify date & File' : ''}`
                        : selectedDoc.status === 'Review'
                          ? 'The date is right — save it, move this document to Date Verified and open the next one (Alt+V)'
                          : 'The date is right — mark it as checked'}>
                        <input ref={dateVerifiedTickRef} type="checkbox" checked={isDateVerified(selectedDoc)} disabled={dateVerifySaving}
                          onChange={() => toggleDateVerified(true)} />
                        Date verified
                      </label>
                      <label className="pr-date-tick" title="This document has no date">
                        <input type="checkbox" checked={filingUndated} disabled={dateVerifySaving}
                          onChange={toggleUndated} />
                        Undated
                      </label>
                    </>
                  )}
                </div>
                {filingDateLayoutFrom && (
                  <span className="pr-form-hint" title="The date was read with the format set in Masters for this party's document type">
                    Date format from Masters: {filingDateLayoutFrom}
                  </span>
                )}
                {handwrittenDateNeedsConfirm && (
                  <div className="pr-date-future" role="alert">
                    <span className="pr-date-future-text">
                      The handwritten date was read as {filingHandwrittenDate} and the printed date as {filingPrintedDate}.
                      {' '}{filingDate === filingHandwrittenDate ? 'The handwritten date has been filled in. ' : ''}
                      Either reading can be wrong — which date is right?
                    </span>
                    <button
                      type="button"
                      className="pr-date-candidate-btn"
                      onClick={() => { filingDateTypedRef.current = true; setFilingDate(filingHandwrittenDate); setFilingDateChoice('handwritten'); setFormDirty(true); }}
                    >
                      Handwritten: {filingHandwrittenDate}
                    </button>
                    <button
                      type="button"
                      className="pr-date-candidate-btn"
                      onClick={() => { filingDateTypedRef.current = true; setFilingDate(filingPrintedDate); setFilingDateChoice('printed'); setFormDirty(true); }}
                    >
                      Printed: {filingPrintedDate}
                    </button>
                    {/* Both readings can be wrong (faint print, untidy handwriting): the user
                        keeps the date already in the field, or types the right one. */}
                    {dateInFieldIsOwn ? (
                      <button
                        type="button"
                        className="pr-date-candidate-btn"
                        title="Neither reading is right — keep the date that is in the field"
                        onClick={() => { setFilingDateChoice('user'); setFilingDateAmbiguous(false); setFormDirty(true); }}
                      >
                        Neither — keep {filingDate}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="pr-date-candidate-btn"
                        title="Neither reading is right — clear the field and type the correct date"
                        onClick={() => {
                          setFilingDate(''); setFilingDateChoice('user'); setFilingDateAmbiguous(false); setFormDirty(true);
                          setTimeout(() => filingDateInputRef.current?.focus(), 0);
                        }}
                      >
                        Neither — I’ll type it
                      </button>
                    )}
                  </div>
                )}
                {filingDateAmbiguous && filingDateCandidates.length > 0 && (
                  <div className="pr-date-candidates">
                    <span className="pr-date-candidates-label">
                      {filingDateIsFuture ? 'This date is later than today. Did it mean:' : `"${filingRawDate}" could be:`}
                    </span>
                    {filingDateCandidates.map(c => (
                      <button
                        key={c.order}
                        type="button"
                        className="pr-date-candidate-btn"
                        onClick={() => {
                          filingDateTypedRef.current = true;
                          setFilingDate(c.iso);
                          setFilingDateFormatId(c.formatId);
                          setFilingDateAmbiguous(false);
                          setFormDirty(true);
                        }}
                      >
                        {c.iso} <span className="pr-date-candidate-order">({c.order})</span>
                      </button>
                    ))}
                  </div>
                )}
                {dateCheckPrompt && dateCheckPrompt.date === filingDate && (
                  <div className="pr-date-future" role="alert">
                    <span className="pr-date-future-text">
                      Not saved yet: {filingDate} {dateCheckPrompt.reason}. Correct the date, or save it as it is.
                    </span>
                    <button
                      type="button"
                      className="pr-date-candidate-btn"
                      onClick={() => { dateCheckOkRef.current = filingDate; toggleDateVerified(dateCheckPrompt.stay); }}
                    >
                      Yes, {filingDate} is right
                    </button>
                    <button
                      type="button"
                      className="pr-date-candidate-btn"
                      onClick={() => { setDateCheckPrompt(null); filingDateInputRef.current?.focus(); }}
                    >
                      Fix the date
                    </button>
                  </div>
                )}
                {futureDateNeedsConfirm && (
                  <div className="pr-date-future" role="alert">
                    <span className="pr-date-future-text">
                      {filingDate} is later than today. Correct the date, or confirm it is right.
                    </span>
                    <button
                      type="button"
                      className="pr-date-candidate-btn"
                      onClick={() => { setFutureDateConfirmed(filingDate); setFormDirty(true); }}
                    >
                      Yes, the date is correct
                    </button>
                  </div>
                )}
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Kind of Document</label>
                {/* What the document IS (receipt, deposit, cheque copy, e-transfer…) — read off
                    the document; editable, and own kinds can be added. */}
                <SearchableDropdown
                  className="pr-form-select"
                  options={kindOptions}
                  value={filingDocuTypeId}
                  onChange={pickFilingKind}
                  placeholder="Expense Receipt, Bank Deposit, Cheque copy…"
                  actionRow={{ label: <><FiPlus size={14} /> Add new kind of document</>, onClick: () => openKindForm('filing', null) }}
                  createRow={{ label: t => <><FiPlus size={14} /> Add “{t}” as a kind…</>, onCreate: t => openKindForm('filing', null, t) }}
                  onEditOption={v => openKindForm('filing', Number(v))}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">
                  Nature of Document
                  {filingNatureAuto && filingNatureId && (
                    <span className="pr-ai-suggested" title="Suggested from the vendor or the document — change if needed">✨ suggested</span>
                  )}
                </label>
                {/* What it was FOR (Meal, Fuel…), from the natures of the kind above — or type a
                    new one: it is kept for next time and sets up the account head + memo. */}
                <SearchableDropdown
                  className="pr-form-select"
                  options={natureOptionsFor(filingDocuTypeId)}
                  value={natureValue(filingNatureId)}
                  onChange={pickFilingNature}
                  placeholder="Meal, Fuel, Vehicle repair… or type your own"
                  actionRow={{ label: <><FiPlus size={14} /> Add new nature</>, onClick: () => openNatureForm('filing', null) }}
                  createRow={{ label: t => <><FiPlus size={14} /> Add “{t}” as a nature…</>, onCreate: t => openNatureForm('filing', null, t) }}
                  onEditOption={v => editNatureOption('filing', v)}
                />
                {natureHint(filingNatureId, showBookkeeping, filingExpenseTypeId) && (
                  <span className="pr-form-hint">{natureHint(filingNatureId, showBookkeeping, filingExpenseTypeId)}</span>
                )}
              </div>

              {/* Expense Type is a bookkeeping field — Your Accounts / Client's Accounts only, not DMS. */}
              {showBookkeeping && (
              <div className="pr-form-group">
                <label className="pr-form-label">
                  Expense Type
                  {filingExpenseAuto && filingExpenseTypeId && (
                    <span className="pr-ai-suggested" title="Auto-suggested from the receipt — change if needed">✨ suggested</span>
                  )}
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={expenseTypes
                    .filter((e: any) => Number(e.is_deleted) !== 1 && Number(e.is_active) !== 0)
                    .map((e: any) => ({
                      value: String(e.expense_type_id),
                      label: e.type_name,   // universal name; tax-return line is a per-country concern, not shown here
                    }))}
                  value={filingExpenseTypeId}
                  onChange={(v: string) => { setFilingExpenseTypeId(v); setFilingExpenseAuto(false); setFormDirty(true); }}
                  placeholder="What kind of expense is this?"
                />
              </div>
              )}

              {/* The GL account this document is booked to — found by name, number,
                  nickname, alias names or tags ("6633"). Same value as Bookkeeping's. */}
              {showBookkeeping && (
              <div className="pr-form-group">
                <label className="pr-form-label">
                  Account Head
                  {bkCoaAuto && bkCoaId && bkAccountHead && (
                    <span className="pr-ai-suggested" title="Filled in from the Nature of Document — change if needed">
                      ✨ {ACCOUNT_HEAD_SOURCE_LABEL[bkAccountHead.source]}
                    </span>
                  )}
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={filingCoaOptions}
                  value={filingCoaOptions.some(o => o.value === bkCoaId) ? bkCoaId : ''}
                  onChange={(v: string) => { bkTouchedRef.current.coa = true; setBkCoaId(v); setBkCoaAuto(false); setFormDirty(true); }}
                  placeholder={!filingBooksKey ? 'Choose the owner first' : filingCoaOptions.length ? 'Name, number, alias or tag…' : 'No chart of accounts for these books yet'}
                  disabled={filingCoaOptions.length === 0}
                />
              </div>
              )}

              <div className="pr-form-group">
                <label className="pr-form-label">Party Name</label>
                {/* From Masters, by document type (as in Bookkeeping): vendors (+ other parties)
                    for money going out, customers for money coming in, everyone otherwise. */}
                <SearchableDropdown
                  className="pr-form-select"
                  options={filingPartyOptions}
                  value={filingPartyName}
                  onChange={(v: string) => {
                    const before = filingPartyName.trim();
                    const beforeListed = filingPartyInMasters.has(before.toLowerCase());
                    const nowListed = filingPartyInMasters.has(v.trim().toLowerCase());
                    // a name that was not in Masters replaced by one that is = a misread to learn
                    if (before && !beforeListed && nowListed) setFilingPartyMisread(before);
                    else if (!nowListed || v.trim().toLowerCase() === filingPartyMisread.toLowerCase()) setFilingPartyMisread('');
                    setFilingPartyName(v);
                    setFormDirty(true);
                  }}
                  placeholder={filingPartySide === 'vendor' ? 'Choose a vendor / party…' : filingPartySide === 'customer' ? 'Choose a customer…' : 'Choose a party…'}
                  actionRow={{ label: <><FiPlus size={14} /> {PARTY_SIDES[filingPartySide].addLabel}</>, onClick: () => openAddParty('filing') }}
                />
                {matchedParty && (
                  <span className="pr-form-hint">
                    {matchedParty.kind}{matchedParty.nickname ? ` · ${matchedParty.nickname}` : ''}{matchedParty.owner_type === 'shared' ? ' · Shared' : ''} — from Masters
                  </span>
                )}
                {matchedParty && filingPartyMisread && (
                  <span className="pr-form-hint pr-party-misread">
                    “{filingPartyMisread}” will be saved as another name (alias) of {matchedParty.name}, so it is recognised next time.
                    <button type="button" className="pr-party-misread-x" title="Don't save it as an alias" aria-label="Don't save it as an alias"
                      onClick={() => setFilingPartyMisread('')}><FiX size={12} /></button>
                  </span>
                )}
                {filingPartyNeedsMasters && (
                  <span className="pr-form-hint pr-party-unlisted">
                    Not in your {filingPartySide === 'customer' ? 'Customers' : 'Vendors'} list. Optional: if the name was
                    read wrong, pick the right one (we'll remember the misread), or add it as new.
                  </span>
                )}
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Invoice / Ref. #</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={filingInvoiceRef}
                  onChange={e => { setFilingInvoiceRef(e.target.value); setFormDirty(true); }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Method of payment if any</label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={paymentSelectOptions}
                  value={paymentSelectValue}
                  onChange={(v: string) => {
                    setFilingPaymentCoaWhy(null);
                    if (v.startsWith('c:')) {
                      choosePaymentAccount(v.slice(2));
                    } else {
                      const id = v.startsWith('m:') ? v.slice(2) : '';
                      setFilingPaymentCoaId('');
                      setFilingPaymentMethodId(id);
                      const pm = paymentMethods.find(p => String(p.payment_method_id) === id);
                      setFilingPaymentMethod(pm ? pm.method_name : '');  // keep legacy text in sync
                    }
                    setFormDirty(true);
                  }}
                  placeholder={filingPaymentAccounts.length ? 'Select the account or method it was paid by' : 'Select payment method'}
                />
                {filingPaymentAccount && filingPaymentCoaWhy && (
                  <span className="pr-form-hint">Read from the receipt — {PAYMENT_MATCH_WHY_LABEL[filingPaymentCoaWhy]}</span>
                )}
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Tax{taxOptionInfo.ownerState ? ` (${taxOptionInfo.ownerState})` : ''}</label>
                  <SearchableDropdown
                    className="pr-form-select"
                    options={taxOptionInfo.options}
                    value={filingTaxRateId}
                    onChange={(v: string) => {
                      setFilingTaxRateId(v);
                      const rate = taxRates.find(r => String(r.tax_rate_id) === v);
                      setFilingTaxTypeId(rate ? String(rate.tax_type_id) : '');
                      if (!v) setFilingTaxAmount('');  // no tax code → no tax amount
                      setFormDirty(true);
                    }}
                    placeholder="Select tax (optional)"
                  />
                  {taxOptionInfo.needsAddress && (
                    <p className="pr-tax-addr-hint">
                      Showing federal tax only. Add this owner's province in <strong>Family &amp; Businesses</strong> to see province-specific taxes.
                    </p>
                  )}
                  {taxOwnerAddress.asOfMoved && (
                    <p className="pr-tax-addr-hint" style={{ color: 'var(--gray-500)' }}>
                      Using <strong>{taxOptionInfo.ownerState}</strong> — where this business was located on the document's date.
                    </p>
                  )}
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Tax amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pr-form-input"
                    value={filingTaxRateId ? filingTaxAmount : ''}
                    disabled={!filingTaxRateId}
                    placeholder={filingTaxRateId ? '' : 'Select tax first'}
                    title={filingTaxRateId ? undefined : 'Pick a tax first — a tax amount needs a tax code'}
                    onChange={e => { setFilingTaxAmount(e.target.value); setFormDirty(true); }}
                  />
                </div>
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Total amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pr-form-input"
                    value={filingAmount}
                    onChange={e => { setFilingAmount(e.target.value); setFormDirty(true); }}
                  />
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Currency</label>
                  <select
                    className="pr-form-input"
                    value={filingCurrency}
                    title={filingBaseCurrency ? `Default: ${filingBaseCurrency} (the owner's base currency)` : undefined}
                    onChange={e => { setFilingCurrency(e.target.value); setFormDirty(true); }}
                  >
                    {!filingCurrency && <option value="">—</option>}
                    {currencyOptions(filingBaseCurrency, filingCurrency).map(c => (
                      <option key={c} value={c}>{c}{c === filingBaseCurrency ? ' (default)' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Memo / Notes</label>
                <textarea
                  className="pr-form-textarea"
                  value={filingPurpose}
                  onChange={e => { setFilingPurpose(e.target.value); setFormDirty(true); }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label" htmlFor="pr-filing-tags">Tags</label>
                <TagInput
                  id="pr-filing-tags"
                  value={filingTags}
                  onChange={v => { setFilingTags(v); setFormDirty(true); }}
                  placeholder="Type a tag — e.g. rental, 118 1SNW, farm"
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Rename Document</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={filingFilename}
                  placeholder="Document name"
                  onChange={e => {
                    const v = e.target.value;
                    setFilingFilename(v);
                    // Real-time save: debounce while typing so PostgreSQL is updated
                    // as the user edits (without a write per keystroke).
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) {
                      const id = selectedDoc.document_id;
                      renameTimerRef.current = setTimeout(() => persistRename(id, v), 600);
                    }
                  }}
                  onBlur={() => {
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) persistRename(selectedDoc.document_id, filingFilename);
                  }}
                />
                <span className="pr-form-hint">Saved automatically as you type.</span>
                {suggestedFilename && suggestedFilename !== filingFilename.trim() && (
                  <button
                    type="button"
                    className="pr-date-candidate-btn pr-suggest-name"
                    title="Date _ party nickname _ reference _ amount"
                    onClick={() => {
                      setFilingFilename(suggestedFilename);
                      if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                      if (selectedDoc) persistRename(selectedDoc.document_id, suggestedFilename);
                    }}
                  >
                    Use: {suggestedFilename}
                  </button>
                )}
              </div>
            </div>

            <div className="pr-form-actions">
              <button
                className="pr-reject-btn"
                onClick={handleReject}
                title="Reject this document"
              >
                Reject
              </button>
              <button
                className="pr-submit-btn"
                disabled={filingOwnerKeys.length === 0 || filingFolderIds.length === 0 || futureDateNeedsConfirm || handwrittenDateNeedsConfirm}
                title={futureDateNeedsConfirm ? 'The document date is later than today — confirm or correct it first'
                  : handwrittenDateNeedsConfirm ? 'The handwritten and printed dates differ — choose which one is right (or neither) first'
                    : filingOwnerKeys.length === 0 || filingFolderIds.length === 0 ? undefined : 'Submit (Ctrl+S or Ctrl+Enter)'}
                ref={filingSubmitBtnRef}
                onClick={handleFilingSubmit}
              >
                Submit
              </button>
            </div>
          </>
        ) : (
          /* ---- Bookkeeping Form ---- */
          <>
            <div className="pr-right-form pr-form-outlined">
              <div className="pr-form-group">
                <label className="pr-form-label">Owner Of Document <span className="required">*</span></label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={ownerEntities.map(ent => ({
                    value: `${ent.owner_type}:${ent.owner_id}`,
                    label: ent.display_name,
                  }))}
                  value={bkOwnerKey}
                  onChange={setBkOwnerKey}
                  placeholder="Choose a Document Owner"
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Kind of Document</label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={kindOptions}
                  value={bkDocuTypeId}
                  onChange={pickBkKind}
                  placeholder="Expense Receipt, Bank Deposit, Cheque copy…"
                  actionRow={{ label: <><FiPlus size={14} /> Add new kind of document</>, onClick: () => openKindForm('bk', null) }}
                  createRow={{ label: t => <><FiPlus size={14} /> Add “{t}” as a kind…</>, onCreate: t => openKindForm('bk', null, t) }}
                  onEditOption={v => openKindForm('bk', Number(v))}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Nature of Document</label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={natureOptionsFor(bkDocuTypeId)}
                  value={natureValue(bkNatureId)}
                  onChange={pickBkNature}
                  placeholder="Meal, Fuel, Vehicle repair… or type your own"
                  actionRow={{ label: <><FiPlus size={14} /> Add new nature</>, onClick: () => openNatureForm('bk', null) }}
                  createRow={{ label: t => <><FiPlus size={14} /> Add “{t}” as a nature…</>, onCreate: t => openNatureForm('bk', null, t) }}
                  onEditOption={v => editNatureOption('bk', v)}
                />
                {natureHint(bkNatureId, true, filingExpenseTypeId) && (
                  <span className="pr-form-hint">{natureHint(bkNatureId, true, filingExpenseTypeId)}</span>
                )}
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Party Name</label>
                {/* From Masters, by document type: vendors (+ other parties) for money going
                    out, customers for money coming in, everyone for a report / other document. */}
                <SearchableDropdown
                  className="pr-form-select"
                  options={bkPartyOptions}
                  value={bkPartyName}
                  onChange={setBkPartyName}
                  placeholder={bkPartySide === 'vendor' ? 'Choose a vendor / party…' : bkPartySide === 'customer' ? 'Choose a customer…' : 'Choose a party…'}
                  actionRow={{ label: <><FiPlus size={14} /> {PARTY_SIDES[bkPartySide].addLabel}</>, onClick: () => openAddParty('bk') }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">
                  Account Head
                  {bkCoaAuto && bkCoaId && bkAccountHead && (
                    <span className="pr-ai-suggested" title="Filled in from the Nature of Document — change if needed">
                      ✨ {ACCOUNT_HEAD_SOURCE_LABEL[bkAccountHead.source]}
                    </span>
                  )}
                </label>
                <SearchableDropdown
                  className="pr-form-select"
                  options={bkCoaOptions}
                  value={bkCoaId}
                  onChange={(v: string) => { bkTouchedRef.current.coa = true; setBkCoaId(v); setBkCoaAuto(false); setFormDirty(true); }}
                  placeholder={bkCoaOptions.length ? 'Choose an account…' : 'No chart of accounts for these books yet'}
                  disabled={bkCoaOptions.length === 0}
                />
                {bkNature && bkCoaId && (
                  <span className="pr-form-hint">Publishing remembers this account for “{bkNature.nature_name}” in these books{bkMatchedParty && bkMatchedParty.owner_type !== 'shared' ? ` and for ${bkMatchedParty.name}` : ''}.</span>
                )}
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label" htmlFor="pr-bk-memo">Memo</label>
                <input
                  id="pr-bk-memo"
                  type="text"
                  className="pr-form-input"
                  value={bkMemo}
                  placeholder="e.g. Meal at India Palace, paid from Visa"
                  onChange={e => { bkTouchedRef.current.memo = true; setBkMemo(e.target.value); setBkMemoAuto(false); setFormDirty(true); }}
                />
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Invoice / Ref. #</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={bkInvoiceRef}
                  onChange={e => setBkInvoiceRef(e.target.value)}
                />
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Date</label>
                  <input
                    type="date"
                    max="9999-12-31"
                    className="pr-form-input"
                    value={bkDate}
                    onChange={e => setBkDate(e.target.value)}
                  />
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Due Date</label>
                  <input
                    type="date"
                    max="9999-12-31"
                    className="pr-form-input"
                    value={bkDueDate}
                    onChange={e => setBkDueDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label className="pr-form-label">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pr-form-input"
                    value={bkAmount}
                    onChange={e => setBkAmount(e.target.value)}
                  />
                </div>
                <div className="pr-form-group">
                  <label className="pr-form-label">Currency</label>
                  <select
                    className="pr-form-input"
                    value={bkCurrency}
                    onChange={e => setBkCurrency(e.target.value)}
                  >
                    {!bkCurrency && <option value="">—</option>}
                    {currencyOptions(filingBaseCurrency, bkCurrency).map(c => (
                      <option key={c} value={c}>{c}{c === filingBaseCurrency ? ' (default)' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pr-form-group">
                <label className="pr-form-label">Tax Rate</label>
                <input
                  type="number"
                  step="0.01"
                  className="pr-form-input"
                  placeholder="e.g. 10.00"
                  value={bkTaxRate}
                  onChange={e => setBkTaxRate(e.target.value)}
                />
              </div>

              {/* Tax summary */}
              {(Number(bkAmount) > 0) && (
                <div className="pr-tax-summary">
                  <div className="pr-tax-line">
                    <span>Subtotal:</span>
                    <span>{bkComputed.subtotal.toFixed(2)} {bkCurrency}</span>
                  </div>
                  <div className="pr-tax-line">
                    <span>Tax:</span>
                    <span>{bkComputed.tax.toFixed(2)} {bkCurrency}</span>
                  </div>
                  <div className="pr-tax-line total">
                    <span>Total:</span>
                    <span>{bkComputed.total.toFixed(2)} {bkCurrency}</span>
                  </div>
                </div>
              )}

              <div className="pr-form-group">
                <label className="pr-form-label">Rename Document</label>
                <input
                  type="text"
                  className="pr-form-input"
                  value={filingFilename}
                  placeholder="Document name"
                  onChange={e => {
                    const v = e.target.value;
                    setFilingFilename(v);
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) {
                      const id = selectedDoc.document_id;
                      renameTimerRef.current = setTimeout(() => persistRename(id, v), 600);
                    }
                  }}
                  onBlur={() => {
                    if (renameTimerRef.current) clearTimeout(renameTimerRef.current);
                    if (selectedDoc) persistRename(selectedDoc.document_id, filingFilename);
                  }}
                />
                <span className="pr-form-hint">Saved automatically as you type.</span>
              </div>
            </div>

            <div className="pr-form-actions">
              <button
                ref={bkPublishBtnRef}
                className="pr-submit-btn publish"
                disabled={!bkOwnerKey}
                title="Publish (Ctrl+S or Ctrl+Enter)"
                onClick={handleBkPublish}
              >
                Publish
              </button>
            </div>
          </>
        )}
      </div>
      </>)}

      {/* ======== Manage Folders popup (full functionality: owner selection + validation) ======== */}
      {showManageFolders && (
        <div
          className="pr-mf-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeManageFolders(); }}
        >
          <div className="pr-mf-modal" style={manageFoldersDrag.style}>
            <div className="pr-mf-modal-head pr-modal-drag" onMouseDown={manageFoldersDrag.onHeaderMouseDown} title="Drag to move">
              <span className="pr-mf-modal-title">Manage Folders</span>
              <button className="pr-mf-modal-close" onClick={closeManageFolders} title="Close">
                <FiX size={18} />
              </button>
            </div>
            <div className="pr-mf-modal-body">
              <ManageFolders viewMode={viewMode} selectedClientId={selectedClientId} initialAction={manageFoldersAction} />
            </div>
          </div>
        </div>
      )}

      {/* ======== Kind of Document — add / edit ======== */}
      {kindForm && (() => {
        const k = kindFormRow;
        const rk = k ? (k.row_kind as 'standard' | 'edited-standard' | 'own') : null;
        const standardName = k && k.standard_id != null
          ? String(docuTypeRows.find(r => Number(r.docu_type_id) === k.standard_id)?.docu_type_name || k.docu_type_name) : '';
        const taken = new Set(docuTypes.filter(dt => dt.is_deleted !== 1 && (!k || dt.docu_type_id !== k.docu_type_id))
          .map(dt => String(dt.docu_type_name).toLowerCase()));
        return (
          <KindFormModal
            key={k ? `k${k.docu_type_id}` : `new:${kindForm.name}`}
            editing={k && rk ? {
              kind: rk,
              values: { name: k.docu_type_name, worksLikeId: k.standard_id != null ? String(k.standard_id) : '' },
              standardName,
            } : null}
            initial={{ name: kindForm.name }}
            standardOptions={docuTypes.filter(dt => dt.row_kind !== 'own').map(dt => ({ value: String(dt.docu_type_id), label: dt.docu_type_name }))}
            takenNames={taken}
            onSave={saveKindForm}
            onRemove={k ? removeKindForm : undefined}
            onBackToStandard={rk === 'edited-standard' ? restoreStandardKind : undefined}
            onClose={() => closeKindForm()}
          />
        );
      })()}

      {/* ======== Nature of Document — add / edit ======== */}
      {natureForm && (() => {
        const n = natureForm.nature;
        const books = natureFormBooks;
        const coaNow = n && books ? findNatureAccount(books, n) : null;
        const taken = new Set(natures.filter(x => !n || x.nature_id !== n.nature_id).map(x => x.nature_name.toLowerCase()));
        return (
          <NatureFormModal
            key={n ? `n${n.nature_id}` : `new:${natureForm.name}`}
            editing={n && natureFormKind ? {
              kind: natureFormKind,
              values: {
                name: n.nature_name, matchWords: n.match_words,
                docuTypeId: n.docu_type_id ? String(n.docu_type_id) : '',
                expenseTypeId: n.expense_type_id ? String(n.expense_type_id) : '',
                coaId: coaNow ? String(coaNow.coa_id) : '',
              },
            } : null}
            initial={{
              name: natureForm.name,
              // the document's kind, else Expense Receipt (most natures are bought things)
              docuTypeId: (natureForm.from === 'bk' ? bkDocuTypeId : filingDocuTypeId)
                || String(docuTypes.find(dt => dt.docu_type_code === 'EXPS')?.docu_type_id ?? ''),
              expenseTypeId: showBookkeeping ? filingExpenseTypeId : '',
              coaId: natureForm.from === 'bk' ? bkCoaId : '',
            }}
            docuTypeOptions={docuTypes.filter(dt => dt.is_deleted !== 1).map(dt => ({ value: String(dt.docu_type_id), label: dt.docu_type_name }))}
            expenseTypeOptions={showBookkeeping ? expenseTypes
              .filter((e: any) => Number(e.is_deleted) !== 1 && Number(e.is_active) !== 0)
              .map((e: any) => ({ value: String(e.expense_type_id), label: e.type_name })) : []}
            books={books ? { label: books.label, coaOptions: books.coaOptions } : null}
            noBooksNote={showBookkeeping ? 'Choose the document’s owner first to set the account head for its books.' : undefined}
            takenNames={taken}
            onSave={saveNatureForm}
            onRemove={n ? removeNatureForm : undefined}
            onBackToStandard={natureFormKind === 'edited-standard' ? restoreStandardNature : undefined}
            onClose={() => closeNatureForm()}
          />
        );
      })()}

      {/* ======== Add new Vendor / Customer / Party (the Masters form, new entry) ======== */}
      {addPartyOpen && (
        <div className="pr-mf-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) dismissAddParty(); }}>
          <div className="pr-mf-modal pr-party-modal" role="dialog" aria-modal="true" aria-label={PARTY_SIDES[addPartyCtx.side].addLabel} style={addPartyDrag.style}>
            <div className="pr-mf-modal-head pr-modal-drag" onMouseDown={addPartyDrag.onHeaderMouseDown} title="Drag to move">
              <span className="pr-mf-modal-title">{PARTY_SIDES[addPartyCtx.side].addLabel}</span>
              <button className="pr-mf-modal-close" onClick={dismissAddParty} title="Close">
                <FiX size={18} />
              </button>
            </div>
            <div className="pr-mf-modal-body">
              <PartiesMasterTab
                viewMode={viewMode}
                selectedClientId={selectedClientId}
                quickAdd={{
                  kinds: PARTY_SIDES[addPartyCtx.side].addKinds,
                  ownerKey: addPartyCtx.ownerKey || undefined,
                  name: addPartyCtx.name,
                  onSaved: (_kind, name) => closeAddParty(name),
                  onCancel: () => closeAddParty(),
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ---- Duplicates found on arrival ---- */}
      {dupPrompt && (() => {
        const copyCount = dupPrompt.reduce((n, g) => n + g.copies.length, 0);
        return (
          <div
            className="pr-mf-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) closeDupPrompt(); }}
            onKeyDown={(e) => { if (e.key === 'Escape') closeDupPrompt(); }}
          >
            <div className="pr-del-modal pr-dup-modal" role="dialog" aria-modal="true" aria-label="Duplicate documents found" style={dupModalDrag.style}>
              <div className="pr-del-head pr-modal-drag" onMouseDown={dupModalDrag.onHeaderMouseDown} title="Drag to move">
                <div className="pr-del-title"><FiAlertCircle size={18} /> Duplicate documents found</div>
                <button className="pr-mf-modal-close" onClick={closeDupPrompt} title="Close">
                  <FiX size={18} />
                </button>
              </div>
              <div className="pr-del-body">
                <p className="pr-dup-modal-remind">You will be reminded every time you are here.</p>
                <p className="pr-dup-modal-lead">
                  {copyCount === 1 ? 'One document looks' : `${copyCount} documents look`} like a copy of another one.
                </p>
                <div className="pr-dup-modal-list">
                  {dupPrompt.map(g => (
                    <div key={g.original.document_id} className="pr-dup-modal-group">
                      <div className="pr-dup-modal-original" title={g.original.filename}>
                        <span className="pr-dup-modal-keep">Original</span> {g.original.filename}
                      </div>
                      {g.copies.map(c => (
                        <div key={c.doc.document_id} className="pr-dup-modal-copy" title={c.doc.filename}>
                          <span className="pr-dup-modal-name">{c.doc.filename}</span>
                          <span className="pr-dup-modal-meta">
                            {c.matchPct >= 100 ? 'exact copy' : `${Math.round(c.matchPct)}% match`}
                            {c.doc.status !== 'Review' ? ` · ${c.doc.status === 'Filed' ? 'already filed' : c.doc.status}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="pr-del-actions">
                <button className="pr-del-btn-secondary" onClick={closeDupPrompt}>Not now</button>
                <button className="pr-del-btn-secondary pr-dup-modal-review" autoFocus onClick={reviewDuplicates}>Review in list</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ---- Delete document confirmation ---- */}
      {/* Separate pages of the open document (replaces it) */}
      {splitOpen && selectedDoc && pdfBytes && (
        <SplitReviewModal
          edit={{ name: selectedDoc.filename, bytes: pdfBytes, pageCount: pdfNumPages, busy: pageEditBusy }}
          onClose={() => { if (!pageEditBusy) setSplitOpen(false); }}
          onConfirm={separatePages}
        />
      )}

      {/* Combine the open document with others (replaces them all) */}
      {combineOpen && selectedDoc && (
        <CombineDocsModal
          current={{ document_id: selectedDoc.document_id, filename: selectedDoc.filename, date_of_document: selectedDoc.date_of_document, page_count: pdfNumPages || selectedDoc.page_count }}
          candidates={combineCandidates}
          busy={pageEditBusy}
          onClose={() => { if (!pageEditBusy) setCombineOpen(false); }}
          onConfirm={combineDocs}
        />
      )}

      {deleteDocTarget && (
        <div
          className="pr-mf-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !deletingDoc) setDeleteDocTarget(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape' && !deletingDoc) setDeleteDocTarget(null); }}
        >
          <div className="pr-del-modal" role="dialog" aria-modal="true" aria-label="Delete document" style={deleteModalDrag.style}>
            <div className="pr-del-head pr-modal-drag" onMouseDown={deleteModalDrag.onHeaderMouseDown} title="Drag to move">
              <div className="pr-del-title"><FiAlertCircle size={18} /> Delete Document</div>
              <button
                className="pr-mf-modal-close"
                onClick={() => setDeleteDocTarget(null)}
                disabled={deletingDoc}
                title="Close"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="pr-del-body">
              <div className="pr-del-warning">
                <div className="pr-del-warning-title">
                  <FiAlertCircle size={16} /> Are you sure you want to delete &ldquo;{deleteDocTarget.filename}&rdquo;?
                </div>
                <p>This removes the document from your list. It is a soft-delete (reversible) — you can restore it from <strong>Reports → Deleted Items</strong>.</p>
              </div>
            </div>
            <div className="pr-del-actions">
              <button className="pr-del-btn-secondary" onClick={() => setDeleteDocTarget(null)} disabled={deletingDoc}>
                Cancel
              </button>
              {/* Focus lands here when the box opens: Enter deletes, Esc cancels, Shift+Tab reaches Cancel. */}
              <button className="pr-del-btn-danger" autoFocus onClick={confirmDeleteDoc} disabled={deletingDoc}>
                <FiTrash2 size={16} /> {deletingDoc ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessReceiptsTab;
